import { recordEmailMilestone } from "../lifecycle";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { FreeCopresent, Presentation } from "../models/presentation";
import { JoinPresentation } from "../models/join-presentation";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { MoveSlide } from "../models/move-slide";
import { ActionType } from "../models/actionType";
import { Message } from "../models/message";
import { PresentationStatus } from "../models/presentationStatus";
import styled from "styled-components";
import { useSwipeable } from "react-swipeable";
import { UpdateSlide } from "../models/update-slide";
import { UpdateSlideImage } from "../models/update-slide-image";
import { PreloadSlideImage } from "../models/preload-slide-image";
import { SlideStepImages, UpdateSlideStep } from "../models/slide-steps";
import { Presenter } from "../models/presenter";
import { CoPresentersChanged } from "../models/co-presenters-changed";
import { SetControlLock } from "../models/set-control-lock";
import { JoinBlocked } from "../models/join-blocked";
import {
  REMOVED_CLOSE_CODE,
  RemovePresenter,
  SetPresenterPaused,
} from "../models/moderate-presenter";
import { FREE_COPRESENTER_SEATS } from "../config/plans";
import ConnectionStatus from "../components/connection-status";
import PresentationTimer, { formatRemaining } from "../components/timer";
import NavButton from "../components/nav-button";
import { MadeBy } from "../components/made-by";
import { QaPanel } from "../components/qa-panel";
// Same builder the panel and the extension use, so the previewed QR encodes the
// URL the audience actually lands on rather than a second guess at it.
import {
  audienceDisplayBase as qaAudienceBase,
  audienceUrlFor as qaAudienceUrl,
} from "../qa/host-client";
import { usePostHog } from "@posthog/react";
import { addOrUpdateSession } from "../hooks/use-session-history";
import { openPreviewGrant } from "../hostAuth";
import {
  beginGoogleSignIn,
  getStoredHostSession,
  getStoredHostSub,
  clearHostSession,
  isWebAuthConfigured,
  resolveHostSession,
} from "../webAuth";
import {
  captureDevicePairing,
  clearDevicePairing,
  getDevicePairing,
  hasExpiredDevicePairing,
  hostBearerFor,
  isPairedDevice,
  pairedHostUrl,
  requestDevicePairing,
  storeDevicePairing,
} from "../devicePairing";
import { QRCodeSVG } from "qrcode.react";
import { useFeaturebaseMessenger } from "../featurebase";
import { THEME_KEY, hostThemeTokens, readStoredTheme, themeTokens } from "../theme";
import { UPGRADED_PARAM } from "../upgradeReturn";
import { ProPlans } from "../components/pro-plans";
import {
  DESKTOP_QUERY,
  MOBILE_QUERY,
  PHONE_LANDSCAPE_QUERY,
  PRESENTER_VIEW_QUERY,
  desktopQueryAnd,
} from "../layout";
import { useMediaQuery } from "../hooks/use-media-query";
import { browserTimeZone, formatResetDate, METER_FROM } from "../usage";
import type { PresentationAllowance } from "../usage";
import hostControllerShot from "../assets/host-controller-blurred.webp";
import copresentLogo from "../assets/copresent-logo.svg";
import { initialSlideSync, receive as receiveSlide, SlideSyncState } from "../slideSync";
import { afterNext, afterPrev, LiveStep, PreviewSources, shownImage } from "../slideSteps";
import { PRO_PAYWALL_SHOWN, UPGRADE_CLICKED } from "../analytics/events";
import { PRIVACY_POLICY_URL, TERMS_URL } from "../config/legal";
import { cleanNoteText } from "../notesText";

// Controller modes. "copresenter" is the existing audience-facing remote;
// "host" is the dedicated host controller (roster, link sharing, control lock),
// reached via the /host/:magicId route. Default is "copresenter" so the
// copresenter route's behaviour is completely unchanged.
type ControllerMode = "copresenter" | "host";

const FONT_SCALE_KEY = "notesFontScale";
const NOTES_VISIBLE_KEY = "notesVisible";
// Touch layout: "slide" or "notes", whichever the Slide | Notes switch last
// chose. Only read when the deck has notes. Without notes there's only the
// slide to show.
const MOBILE_VIEW_KEY = "mobileView";
const PREVIEW_VISIBLE_KEY = "previewVisible";
// The touch layout's own Preview switch (the menu's "Live previews" tile). Its
// own key, not desktop's: the old phone pill's stored "off" must not come back
// as a surprise, and a desktop choice shouldn't decide what a phone shows.
const MOBILE_PREVIEW_VISIBLE_KEY = "mobilePreviewVisible";
const ROSTER_COLLAPSED_KEY = "hostRosterCollapsed";
const NOTES_COLLAPSED_KEY = "hostNotesCollapsed";

// Host rail geometry, in one place because THREE things have to agree about it:
// the rail's own width, the icon strip left behind when it collapses, and the
// floating pill that straddles the rail's right edge.
//
// They were three separate literals, and the pill's was written as 304 - 13 with
// the arithmetic already done — so widening the rail to 352 left the pill 61px
// inside the panel, sitting on top of the Q&A footer buttons. Nothing failed;
// it just looked broken, and only on the one screen wide enough to see it.
const HOST_RAIL_WIDTH = 352;
const HOST_RAIL_STRIP = 64;
/** The collapse pill's width; it sits centred on the edge, so half hangs over. */
const RAIL_TAB_WIDTH = 26;

// Same story on the right: the notes rail's width and its own pill. This one was
// still correct (347 = 360 - 13) only because nothing had moved the notes rail
// yet — the identical bug, waiting.
const NOTES_RAIL_WIDTH = 360;
/** Gutter from the window edge when the notes rail is fully collapsed. Unlike
    the roster, nothing is left behind on this side to straddle. */
const NOTES_TAB_EDGE_GAP = 8;
// The notes sizes A− / A+ step through. The old ceiling was 1.6×, and it was
// the most-hit limit in the controller: speakers reading notes off a phone or
// tablet held at arm's length wanted more. Steps widen as the text grows, so
// getting from normal to very large is a few taps, not a dozen, while the
// small end keeps the fine control it had. 2.6× is about 44px on a phone and
// 68px on a tablet, a few words to a line, which is past where notes stop
// being readable as notes.
const FONT_SCALES = [0.8, 0.9, 1, 1.15, 1.3, 1.45, 1.6, 1.8, 2, 2.3, 2.6] as const;
const MIN_FONT_SCALE = FONT_SCALES[0];
const MAX_FONT_SCALE = FONT_SCALES[FONT_SCALES.length - 1];

// The next size up or down from any scale, including ones stored under the old
// even 0.15 steps (1.15, 1.45 and so on), which simply land on the nearest
// size in that direction.
const nextFontScale = (current: number, direction: 1 | -1): number => {
  if (direction > 0) {
    return FONT_SCALES.find((v) => v > current + 1e-6) ?? MAX_FONT_SCALE;
  }
  return [...FONT_SCALES].reverse().find((v) => v < current - 1e-6) ?? MIN_FONT_SCALE;
};


// What /presentation/:magicId may carry — two shapes, both real:
//
//   UUID       a magicId, minted by the extension (uuidv4). Every link shared
//              before invite tokens existed, and the fallback when the backend
//              couldn't issue a token.
//   32 hex     an invite token, minted by the backend (16 CSPRNG bytes, hex).
//              What the host controller's invite link carries now.
//
// The join path resolves either server-side; this guard only filters garbage —
// e.g. Bing/Copilot indexing "/presentation/${magicId}" → "/presentation/$".
// It must accept BOTH: rejecting the token shape here bounced every new invite
// link to the dashboard before the backend ever saw it.
const MAGIC_ID_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;

// Google Slides deck (file) ids — the /host/deck/:presentationId param. Letters,
// digits, hyphen, underscore; always well over 10 chars. The host controller is
// keyed to the DECK, not the rotating magic link, so its URL carries this stable
// id and resolves the live session's magicId at load.
const DECK_ID_PATTERN = /^[A-Za-z0-9_-]{10,}$/;

// Liveness watchdog cadence. The clicker can't trust its cached status: the
// host may close their tab (we should leave ONLINE) or the backend may swap the
// presentation out from under us. So on a steady tick we reconcile by rejoining
// whenever we're not confidently live, probe a suspiciously quiet ONLINE
// session, and force a reconnect if even the probe goes unanswered.
const WATCHDOG_INTERVAL_MS = 2500;
// While ONLINE, how long with zero inbound frames before we send a liveness
// probe. A present-but-idle host sends nothing, so this is deliberately well
// above a normal pause between slides.
const ONLINE_PROBE_AFTER_MS = 20000;
// No inbound frames at all for this long (probes included) means the socket is
// dead/half-open — drop it so react-use-websocket reconnects from scratch.
const CONNECTION_DEAD_AFTER_MS = 40000;

// Inline SVG icons (Heroicons-style outlines). Inlined rather than depending
// on a library to keep bundle size unchanged. All use `currentColor` so the
// IconButton's text-color drives the stroke — same hover/pressed states work
// for free.
const ICON_SVG_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const NotesIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);

const PreviewIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m21 16-5-5L5 21" />
  </svg>
);

// Support (live chat): a question in a circle, so it does not share the Q&A speech bubble.
const HelpIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M9.4 9.2a2.7 2.7 0 0 1 5.2 1c0 1.8-2.6 2.2-2.6 3.9" />
    <path d="M12 17.2h.01" />
  </svg>
);

const ExpandIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

const TimerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2.5" />
    <path d="M9 2h6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const SunIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

// Page emits the theme tokens — every other styled component in this file (and
// NavButton) references them via `var(--token, fallback)`. The token block
// itself lives in ../theme so /upgrade/success renders in the same theme; only
// the host overrides below are controller-specific.
const Page = styled.div<{ $dark: boolean; $host?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  /* Anchor for the absolutely-positioned theme toggle in the top-right
     corner (see ThemeToggleSlot). */
  position: relative;

  ${(p) => themeTokens(p.$dark)}

  background-color: var(--bg-page);
  color: var(--text-primary);

  /* Host controller, dark mode: its own off-black surface (see ../theme).
     Lands after the base tokens above, so it wins. */
  ${(p) => (p.$host ? hostThemeTokens(p.$dark) : "")}
`;

const Chrome = styled.header`
  flex: 0 0 auto;
  padding: 20px 16px 12px;
  text-align: center;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(18px, 1.6vw + 12px, 28px);
  line-height: 1.2;
`;

const MetaRow = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
  color: var(--text-muted);
`;

const SlideCount = styled.span`
  font-weight: 500;
`;

// Compact icon-button used by every header tool — visibility toggles, theme,
// and font scale. Stays visually quiet by default (transparent, muted color),
// brightens on hover, and uses `aria-pressed` to indicate ON/OFF state for
// the toggleable buttons (notes, preview, theme). Buttons without
// `aria-pressed` (font A−/A+) sit at the default muted weight.
const IconButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  transition: background-color 0.12s, color 0.12s;

  &[aria-pressed="false"] {
    color: var(--text-fade);
  }
  &[aria-pressed="true"] {
    color: var(--text-primary);
  }

  &:hover:enabled {
    background: var(--bg-chip-hover);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// Its own row under the status line, not a cluster inside it. As bare icons in
// the meta row these two read as decoration next to "Connected · 12 slides" and
// went unfound — the preview panel in particular, which is the one control that
// changes what the whole screen is for.
const Toolbar = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// The mobile timer's own row, under the toolbar. Full width with the value
// centred and the controls pushed to the edges, so a thumb reaches − and +
// without crossing the number.
//
// A row rather than a sheet: five 44px targets plus the numeral fit a 375px
// viewport with room to spare, and the whole value of "+1 minute" is that it's
// one tap while you're speaking. Behind a sheet it's three taps.
const TimerRow = styled.div<{ $hidden: boolean }>`
  margin-top: 8px;
  display: ${(p) => (p.$hidden ? "none" : "flex")};
  justify-content: center;
`;

// The pill's live value when the row is collapsed. Tabular so the pill doesn't
// resize once a second and shove Notes/Preview sideways mid-talk.
const PillTime = styled.span<{ $tone: "default" | "warn" | "over" }>`
  font-variant-numeric: tabular-nums;
  min-width: 46px;
  text-align: left;
  color: ${(p) =>
    p.$tone === "over"
      ? "var(--danger, #FE4657)"
      : p.$tone === "warn"
      ? "var(--warning, #F29339)"
      : "inherit"};
`;

// Named toggle: icon + word, in a pill that fills in when the panel is showing.
// aria-pressed carries the state for assistive tech and drives the styling, so
// the label stays a plain noun ("Notes") — matching the visible text, which an
// aria-label of "Hide notes" would not.
const ViewToggle = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  /* border-chip at rest, not border-soft: soft is ~7% and the pill simply
     stopped looking like a button. The FILL is what carries the on/off state,
     so both states can afford a legible outline. */
  border: 1px solid var(--border-chip);

  /* OFF is the loud state, and it's deliberately the inverse of the usual
     filled-means-active convention.

     When a panel is on, its content is on the screen — the pill is telling you
     something you can already see, so it recedes. When it's off, the pill is
     the ONLY sign that the panel exists at all, and that's the moment it has to
     be findable. The prominence follows what there is to gain by pressing.

     A chip with no aria-pressed (the ones that open the paywall rather than
     toggling) lands here too, which is right: it's a call to action. */
  background: var(--bg-chip);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: background-color 0.12s, color 0.12s, border-color 0.12s;

  svg {
    width: 16px;
    height: 16px;
  }

  &[aria-pressed="true"] {
    background: transparent;
    color: var(--text-fade);
  }

  &:hover {
    background: var(--bg-chip-hover);
    color: var(--text-primary);
  }
`;

// Floating slot in the top-right corner of the page for the theme toggle.
// Lives outside the Chrome/MetaRow flow so it doesn't compete with the
// title and meta info — it's a global "page-level" control rather than a
// presentation-specific one.
const ThemeToggleSlot = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 2;
  /* Mobile: the hamburger owns the top-right corner, so the theme toggle
     moves to the LEFT. Desktop keeps it top-right (no hamburger there). */
  @media ${MOBILE_QUERY} {
    right: auto;
    left: 16px;
  }
`;

// Wraps everything below the Chrome header — preview, notes, controls and
// the keyboard hint — in one positioning container. The wide-desktop side-
// by-side variant uses the NotesArea media query below to absolutely
// position the notes panel; anchoring on BodyArea (rather than just a
// preview-and-notes wrapper) means the notes panel can span the FULL
// vertical extent of the preview + buttons + hint stack, not just the
// preview's height.
const BodyArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

// NotesArea takes the space left between the pinned Chrome and Controls.
// `flex: 1 1 0` with `min-height: 0` lets the scroll container inside actually
// constrain — without the 0 basis, the content's natural height would win and
// push the Controls off-screen when the font is cranked up.
//
// Width caps at 520px so when notes is the only thing in the centre (no
// preview) it lines up with the Controls/preview column. On wide desktops
// (≥1200px) when shown alongside the preview, `$alongPreview` repositions
// notes as an absolute panel anchored to the right edge, leaving the
// centred preview untouched as the visual centrepiece.
const NotesArea = styled.main<{ $alongPreview?: boolean; $capHalf?: boolean }>`
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  position: relative;

  /* When notes is the centrepiece (no preview, desktop) it grows to fill the
     height left between the Chrome and Controls — same as the mobile fill
     layout. We deliberately do NOT cap it to the preview's 16:9 footprint:
     historical notes run long (median ~470 chars, p90 ~1100), so reading
     real-estate beats keeping the nav buttons the same height as the preview
     layout. The bottom fade (ScrollFade) signals when there's more below. */

  /* Mobile: a CEILING of half the column, so the nav buttons below can carry
     their slide thumbnails. A ceiling rather than a fixed half — a short note
     still hands the space back, and notes are usually at the ceiling anyway.
     ScrollFade already tells the reader there's more below.

     Percentages of BodyArea, never vh: iOS Safari's vh includes the collapsing
     URL bar, so 50vh is wrong by ~60px in one of its two states. BodyArea is a
     bounded flex column, so % here resolves against something honest.

     ONLY when the thumbnails are actually there to want the space ($capHalf).
     Applying it unconditionally would cost every free room — which is sent no
     images at all — half its reading area in exchange for a strip of blank
     column above unchanged nav buttons. */
  ${(p) =>
    p.$capHalf &&
    `
    @media ${MOBILE_QUERY} {
      flex: 1 1 50%;
      max-height: 50%;
    }
  `}

  ${(p) =>
    p.$alongPreview &&
    `
    @media (min-width: 1200px) {
      position: absolute;
      /* top/bottom match the inner padding of PreviewArea (16px in
         alongNotes mode) and Controls (8px bottom in compact mode) so the
         notes card's visual edges line up with the slide card's top edge
         and the nav buttons' bottom edge — without these offsets the
         panel anchors to BodyArea's raw edges and visibly overshoots. */
      top: 16px;
      right: 16px;
      bottom: 8px;
      width: clamp(300px, 28vw, 420px);
      max-width: 420px;
      margin: 0;
      padding: 0;
    }
  `}
`;

const NotesCard = styled.div<{ $scale: number }>`
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  background-color: var(--bg-card);
  border: 2px solid var(--border-soft);
  border-radius: 12px;
  /* Named so FontControls can sit a fixed inset from the corner. */
  --notes-pad-top: clamp(20px, 2.4vw, 36px);
  --notes-pad-right: clamp(16px, 2vw, 32px);
  padding: var(--notes-pad-top) var(--notes-pad-right);
  color: var(--text-primary);
  line-height: 1.55;
  white-space: pre-wrap;
  font-size: calc(clamp(17px, 1.4vw + 12px, 26px) * ${(p) => p.$scale});
  -webkit-overflow-scrolling: touch;
`;

// Wraps the scrolling NotesCard so the scroll-affordance fade can anchor to the
// card's exact footprint in every layout (centred, mobile fill, side-by-side).
const NotesViewport = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  display: flex;
`;

// Bottom fade + "More ↓" hint shown only when the note overflows the visible
// card, so the presenter knows to scroll. Inset by the card's 2px border and
// rounded to match its corners; pointer-events: none so scroll/swipe pass
// straight through to the card underneath.
const ScrollFade = styled.div`
  position: absolute;
  left: 2px;
  right: 2px;
  bottom: 2px;
  height: 64px;
  border-radius: 0 0 10px 10px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: linear-gradient(to bottom, transparent, var(--bg-card) 82%);
  z-index: 1;
`;

const MoreHint = styled.span`
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  background: var(--bg-chip);
  border: 1px solid var(--border-chip);
  border-radius: 999px;
  padding: 2px 10px;
`;

// Shown in place of NotesCard when the panel is open but the current slide
// has no speaker notes. Keeps the layout anchored (no jumping as the host
// advances through deck sections that don't have notes) and tells the user
// explicitly that this is intentional, not a bug.
const EmptyNotesCard = styled.div`
  flex: 1 1 0;
  min-height: 0;
  background-color: var(--bg-card);
  border: 2px solid var(--border-soft);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-fade);
  font-size: 14px;
  font-style: italic;
`;

// Font-size control inside the notes card, floated to the top-right corner so
// the first lines of the note wrap around it instead of running underneath
// (COP-126: on a phone the buttons used to sit on top of the opening words).
// Sticky, so it stays reachable while the note scrolls; once it sticks, the
// soft background chip keeps it legible over the text passing underneath.
// Where sticky floats aren't supported it simply scrolls away with the text.
const FONT_CONTROLS_INSET = "8px";
const FontControls = styled.div`
  float: right;
  position: sticky;
  /* Sticky offsets count from inside the card's padding, so take the padding
     off to stick at the same corner inset the chip rests at. */
  top: calc(${FONT_CONTROLS_INSET} - var(--notes-pad-top));
  /* Pull the chip out of the card's padding into its top-right corner, the
     same inset from the top as from the right (Peter on COP-126). The first
     line of text wraps beside it. */
  margin: calc(${FONT_CONTROLS_INSET} - var(--notes-pad-top))
    calc(${FONT_CONTROLS_INSET} - var(--notes-pad-right)) 4px 12px;
  display: inline-flex;
  gap: 4px;
  background: var(--font-controls-bg);
  backdrop-filter: blur(6px);
  border-radius: 8px;
  padding: 3px;
  z-index: 1;
  /* The card is pre-wrap for the note; the chip has no text to keep. */
  white-space: normal;
  line-height: 1;
`;

const FontButton = styled.button`
  appearance: none;
  border: 1px solid var(--border-chip);
  background: var(--bg-chip);
  border-radius: 6px;
  width: 32px;
  height: 26px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    background: var(--bg-chip-hover);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// SlidePreview shows the current slide, rendered server-side via the Google
// Slides API (the backend pushes a fresh thumbnail URL over the WebSocket on
// each slide change, and prefetches one slide ahead so forward navigation
// usually hits a warm cache). Behaves like NotesArea when no notes exist:
// grows to fill the space (`$primary`) instead of hugging a compact card
// above the Controls.
//
// Hidden on phones (< 768px), where vertical room is already tight and notes
// + nav are the priority — same breakpoint the Footer uses to hide on mobile.
// Width is locked to 520px to match the Controls row directly below — the
// preview and the nav buttons share the same column so the eye reads them as
// one stack. (Controls is always non-primary / 520px whenever a preview is
// visible, because `controlsPrimary` requires no preview.) `$primary`
// controls vertical behaviour: takes the leftover space and a bit more
// padding when there are no notes; hugs the controls as a strip otherwise.
//
// `$alongNotes` opts into the wide-desktop variant — when notes pops out to
// the right as an absolute side panel (≥1200px), the preview should reclaim
// the vertical space the notes used to take in stacked mode, so we promote
// it to the primary-style flex behaviour at that breakpoint only.
const PreviewArea = styled.section<{ $primary: boolean; $alongNotes?: boolean }>`
  /* Hug the slide card at its natural 16:9 size and sit at the TOP of the
     column; the Controls below grow to take the remaining height. Hugging
     (rather than growing) also means the card is never clamped/letterboxed by
     a constrained flex height — it always renders at full width-driven 16:9. */
  flex: 0 0 auto;
  min-height: 0;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  /* Bottom padding (6px) + the Controls' 6px top padding = a 12px gap between
     the slide card and the PREV/NEXT buttons. */
  padding: ${(p) => (p.$primary ? "16px 16px 6px" : "8px 16px 6px")};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${MOBILE_QUERY} {
    display: none;
  }

  ${(p) =>
    p.$alongNotes &&
    `
    @media (min-width: 1200px) {
      padding: 16px 16px 6px;
    }
  `}
`;

// 16:9 card. White backing (matches most slide bodies, so the thin letterbox
// strip that appears when the Slides thumbnail isn't a perfect 16:9 fit
// disappears against the slide instead of showing as a dark bar). Soft drop
// shadow replaces the visible border for a sleeker floating-card look — the
// card edges read as depth rather than as a hard outline.
// Backing stays white in both themes — the slide images themselves are
// authored on whatever background the deck uses (usually white), and
// dark-backing a white slide would create a visible white frame inside a
// dark card. Shadow tightens in dark mode (the soft halo it casts on a
// light page reads as glow on a dark page if not deepened).
const SlideCard = styled.div<{ $empty?: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 100%;
  /* White behind real slide images so they don't letterbox against a dark card.
     But when showing the placeholder (no image), go dark to match — otherwise
     the white backing bleeds through the rounded-corner antialiasing as pale
     corner slivers. */
  background: ${(p) => (p.$empty ? "var(--bg-fallback)" : "#fff")};
  border-radius: 14px;
  overflow: hidden;
  /* Positioning context for the absolutely-filled PreviewFallback below. */
  position: relative;
  box-shadow:
    0 1px 2px var(--shadow-near),
    0 10px 30px -12px var(--shadow-far);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

// Shown inside the SlideCard when the preview can't be displayed — either the
// WebSocket has dropped (we have no fresh URL to trust) or the <img> itself
// failed to load (404, expired Slides API contentUrl, transient network).
// Sized to fill the card so the layout doesn't jump between image and fallback.
// `max-width` on the description forces the long sentence to wrap into a
// compact block — without it the line stretches edge-to-edge and `text-align:
// center` has nothing to centre, so the message visually reads as left-aligned
// inside the card.
const PreviewFallback = styled.div`
  /* Absolutely cover the card's visible box (inset: 0) rather than relying on
     height: 100%, which doesn't resolve reliably against the card's
     aspect-ratio height and was centering the text against the wrong height
     (text landed below the visual centre). */
  position: absolute;
  inset: 0;
  /* Match the card's rounded corners so the white slide-card background (kept
     white so real slide images don't letterbox) doesn't peek out as pale corner
     slivers behind this dark placeholder fill. */
  border-radius: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  text-align: center;
  background: var(--bg-fallback);
  color: var(--text-muted);

  strong {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-headline);
  }

  span {
    display: block;
    max-width: 340px;
    font-size: 13px;
    line-height: 1.5;
  }
`;

// The free host's offer, sat where the slide would be. Bounded width so the
// buttons don't stretch to the panel's full 16:9 span and read as a banner —
// they should look like a choice, the same size as the one in the rail.
const PreviewOffer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 240px;
  margin-top: 6px;
`;

// Controls must ALWAYS stay visible — `flex-shrink: 0` pins them regardless
// of how tall the notes content would like to be.
// - $primary (nav is the only thing on screen): the centerpiece — grows to
//   fill the column with a generous min-height.
// - $fill (a preview is shown above): PreviewArea hugs the slide card at its
//   natural 16:9 size, and the buttons GROW to take the remaining height below
//   it — so the nav fills the column instead of sitting as a thin strip.
// - otherwise (e.g. notes-only): natural height so the notes card dominates.
// - $wide (under a slide view): spans the column like the slide above it,
//   rather than sitting as a 520px strip under a slide twice that width.
const Controls = styled.div<{ $primary: boolean; $fill?: boolean; $wide?: boolean }>`
  flex: ${(p) => (p.$primary ? "1 1 auto" : p.$fill ? "1 1 0" : "0 0 auto")};
  flex-shrink: 0;
  width: 100%;
  max-width: ${(p) => (p.$wide ? "none" : p.$primary ? "640px" : "520px")};
  margin: 0 auto;
  /* Tighter top padding in compact mode so the buttons sit close to the
     preview/notes block above — the visual gap was too generous before. */
  padding: ${(p) =>
    p.$primary
      ? "32px 16px calc(32px + env(safe-area-inset-bottom, 0px))"
      : "6px 16px calc(8px + env(safe-area-inset-bottom, 0px))"};
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  gap: 12px;
  min-height: ${(p) => (p.$primary ? "240px" : "120px")};

  @media ${DESKTOP_QUERY} {
    min-height: ${(p) => (p.$primary ? "320px" : "140px")};
  }
`;

// The slide either side of you, inside the button that takes you there.
//
// On a phone this stands in for the preview panel there's no room for. On
// desktop it sits BESIDE that panel and completes it: prev · current · next,
// the presenter view every other tool has, without a fourth region to lay out.
//
// The box is ALWAYS reserved once the deck has previews, image or not. Sizing
// the button to its content would make it grow as thumbnails stream in and
// shrink at the deck's ends — moving the target under someone's cursor or thumb
// mid-presentation, which is the one thing a nav control must never do.
const NavThumb = styled.span`
  display: block;
  width: 100%;
  /* 16:9, matching the Slides thumbnails the backend renders. */
  aspect-ratio: 16 / 9;
  /* Yields height before the label does. In a short window the whole column
     (chrome + preview panel + controls + hint) wants more room than there is,
     and the nav buttons get squeezed; without these the thumbnail held its full
     size, overflowed its button and was clipped by the overflow rule below —
     sliced down both sides, with the label crushed against the bottom edge.
     min-height 0 is what actually permits a flex item to shrink past its
     content size; max-height stops it ever exceeding the button. */
  flex: 0 1 auto;
  min-height: 0;
  max-height: 100%;
  border-radius: 6px;
  overflow: hidden;
  background: var(--bg-fallback);
  /* Hairline so an empty box still reads as "a slide goes here" rather than as
     a rendering glitch. */
  box-shadow: inset 0 0 0 1px var(--border-soft);

  /* A tablet in the touch layout has buttons several hundred pixels wide, and
     a thumbnail that wide is taller than a phone's whole nav row. Phones never
     reach this, their buttons are narrower. */
  max-width: 220px;

  /* Desktop caps the WIDTH rather than filling the button. The nav buttons are
     wide and short here (140px min in compact mode, sharing the column with the
     full preview panel), so a full-width 16:9 thumb would be ~124px tall and
     crowd the label out. 168px wide is ~95px tall, which clears it — and the
     current slide should stay the biggest thing on screen regardless. */
  @media ${DESKTOP_QUERY} {
    max-width: 168px;
  }

  /* Past a point shrinking stops helping and a 20px sliver is just noise. Below
     this the button goes back to being a plain PREV/NEXT, which is what it was
     before this feature and is perfectly usable.
     DESKTOP ONLY: a phone has no preview panel competing for the column, and a
     short phone (an SE is ~550px of usable height) is exactly where the
     thumbnails matter most. */
  @media ${desktopQueryAnd("(max-height: 420px)")} {
    display: none;
  }
`;

const NavThumbImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  /* contain, not cover: identical at the natural size (box and image are both
     16:9), but when the box has been squeezed the slide letterboxes instead of
     having its edges cropped off. A cropped thumbnail misrepresents the slide
     you're about to move to, which is the one job it has. */
  object-fit: contain;
`;

// ─────────────────────── Touch layout: the slide views ───────────────────────
// A phone or tablet used as a clicker. The deck is up on the big screen and the
// device is in the presenter's hand, and plenty of them have no notes and want
// to SEE the slide they're on. "The preview image is too small" was the request
// that started this: the desktop preview is capped at 520px, and a phone had no
// current-slide preview at all, only the neighbours inside PREV/NEXT.
//
// Three views, all in the touch layout (MOBILE_QUERY):
//   • slide view: the current slide fills the free space above PREV/NEXT. It's
//     the default when the deck has no notes. When it has notes, a Slide | Notes
//     switch in the header chooses between them.
//   • presenter view: the slide view on a LANDSCAPE TABLET, with the next slide
//     and the notes in a column beside it.
//   • stage view: the slide edge to edge, nothing else on screen. It opens on
//     its own when a phone is turned sideways, and from the expand button on the
//     slide anywhere else. Tapping the left or right half moves back or forward.

// Same visual language as ViewToggle, split in two. Lives in the header Toolbar,
// in place of the Notes pill it replaces: with a slide to show, hiding the notes
// no longer leaves an empty column, it swaps what fills it.
const ViewSwitch = styled.div`
  display: inline-flex;
  height: 32px;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 1px solid var(--border-chip);
  background: var(--bg-chip);
`;

const ViewSwitchOption = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-fade);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;

  svg {
    width: 15px;
    height: 15px;
  }

  &[aria-pressed="true"] {
    background: var(--bg-card);
    color: var(--text-primary);
    box-shadow: 0 1px 2px var(--shadow-near);
  }
`;

// The column a slide view lays out in: the slide, then PREV/NEXT taking what's
// left. A size container so the slide can be sized against the column itself
// (BigSlideArea), whichever shape it is.
const SlideViewColumn = styled.div`
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  container-type: size;
`;

// The slide's box. As wide as the column, and so as tall as a 16:9 slide that
// wide, unless that would leave PREV/NEXT less than their minimum (Controls,
// 120px, plus this box's margins), in which case it gives way. Everything the
// slide doesn't need goes to the buttons, so it sits directly above them at the
// biggest size the column allows rather than floating in an empty band.
//
// Itself a size container, so the card inside fits whichever dimension ran out.
const BigSlideArea = styled.section`
  flex: 0 0 auto;
  height: min(calc((100cqw - 32px) * 9 / 16), calc(100cqh - 140px));
  margin: 8px 16px 6px;
  container-type: size;
  display: flex;
  justify-content: center;
`;

const BigSlideCard = styled(SlideCard)`
  width: min(100cqw, 100cqh * 16 / 9);
  max-height: 100cqh;
`;

// Opens the stage view. Sits on the slide it enlarges, the place you'd look for
// it, and keeps clear of the slide's own content by sitting in the corner.
const ExpandButton = styled.button`
  appearance: none;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Presenter view: current slide and PREV/NEXT on the left, next slide and notes
// on the right.
const PresenterGrid = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  width: 100%;
`;

const PresenterMain = styled(SlideViewColumn)`
  flex: 2 1 0;
`;

const PresenterSide = styled.aside`
  flex: 1 1 0;
  min-width: 240px;
  max-width: 400px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 calc(8px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
`;

const PresenterNext = styled.div`
  flex: 0 0 auto;
  padding: 0 16px;
`;

const PresenterNextLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-fade);
  margin-bottom: 6px;
`;

const PresenterNextCard = styled(SlideCard)`
  border-radius: 10px;
`;

// Stage view. Black, like the screen the deck is projected on, in both themes.
// Above everything in the page except the dialogs (ModalOverlay is 50), so a
// name prompt or an upgrade confirmation still lands on top of it. 49, not
// lower: the host's bottom tab bar (48) and its sheet (47) must sit under it.
const Stage = styled.div`
  position: fixed;
  inset: 0;
  z-index: 49;
  background: #000;
  display: flex;
  flex-direction: column;
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px)
    env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);
  box-sizing: border-box;
  /* A double tap on a tap zone is two slide moves, not a zoom. */
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
`;

// The page's own header, kept: which deck, where in it, and whether the
// remote is connected are what a presenter glances down for. Its own row above
// the slide rather than floating over it, so it never covers slide content and
// a tap on it never moves the deck. Always light-on-black, whatever the theme.
const StageHeader = styled.header`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 8px 16px;
  color: #fff;

  ${Title} {
    color: #fff;
    font-size: clamp(16px, 1.2vw + 12px, 24px);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${MetaRow} {
    justify-content: flex-start;
    margin-top: 2px;
    color: rgba(255, 255, 255, 0.65);
  }
`;

const StageHeading = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
`;

const StageTimer = styled.span`
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

// The slide's box: everything under the header. Size container so the slide
// fits whichever dimension runs out first; the positioning context for the tap
// zones, which cover this area only.
const StageBody = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  container-type: size;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StageSlide = styled.img`
  display: block;
  width: min(100cqw, 100cqh * 16 / 9);
  height: auto;
  max-height: 100cqh;
  object-fit: contain;
`;

// The two halves of the slide area. Real buttons, so they have a name for a
// screen reader and a disabled state at the ends of the deck. Invisible: the
// point of the view is that the slide fills it.
const StageTapZone = styled.button<{ $side: "prev" | "next" }>`
  appearance: none;
  position: absolute;
  top: 0;
  bottom: 0;
  ${(p) => (p.$side === "prev" ? "left: 0;" : "right: 0;")}
  width: 50%;
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    cursor: default;
  }

  /* A brief wash on the half that was pressed, so a tap visibly registered
     even when the slide it moved to looks much like the last one. */
  &:active:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const StageExit = styled.button`
  appearance: none;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
`;

// Keyboard-shortcut nudge sitting just under the PREV/NEXT buttons. The
// shortcuts (←/→, Space, PageUp/PageDown) have always worked — this just
// makes them discoverable. Hidden below 768px because phones don't have a
// keyboard and the message would only steal vertical space from the controls.
const KeyboardHint = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: 520px;
  /* Gap above matches the 12px gap BETWEEN the PREV/NEXT buttons: the
     Controls' 8px bottom padding plus this 4px top margin = 12px. Below,
     a clear gap before the Footer. */
  margin: 4px auto 16px;
  padding: 0 16px;
  box-sizing: border-box;
  text-align: center;
  font-size: 12px;
  color: var(--text-fade);

  kbd {
    display: inline-block;
    padding: 1px 6px;
    margin: 0 2px;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    background: var(--bg-chip);
    border: 1px solid var(--border-chip);
    border-bottom-width: 2px;
    border-radius: 4px;
    line-height: 1;
  }

  @media ${MOBILE_QUERY} {
    display: none;
  }

  /* Also the first thing to go when the window is SHORT. It's a one-time
     discoverability nudge for shortcuts that work whether or not it's on
     screen — so in a squeezed column it is pure cost, and it was the line
     visibly colliding with the PREV/NEXT labels. */
  @media (max-height: 640px) {
    display: none;
  }
`;

const Footer = styled(MadeBy)`
  flex: 0 0 auto;
  font-size: 12px;
  text-align: center;
  padding: 8px 16px;
  color: var(--text-muted);

  p {
    margin: 2px 0;
    line-height: 1.4;
  }

  a {
    color: var(--link);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: var(--link-hover);
      text-decoration: underline;
    }
  }

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

// ───────────────────────────── Name modal ─────────────────────────────
// Themed replacement for the old blocking window.prompt() that asked a
// joining co-presenter for their name. Lives inside <Page> so it inherits
// the theme CSS custom properties (var(--bg-card) etc.), making it light/
// dark aware for free. Shown only on the first join from a given browser
// (subsequent visits read the cached name from localStorage).
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  /* Dim + blur the page behind the dialog. Same scrim in both themes —
     it reads as "the page is inactive" regardless of light/dark. */
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  animation: copresentModalFade 0.14s ease-out;

  @keyframes copresentModalFade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 380px;
  box-sizing: border-box;
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  padding: 28px 24px 24px;
  box-shadow:
    0 1px 2px var(--shadow-near),
    0 24px 60px -16px var(--shadow-far);
  animation: copresentModalPop 0.16s ease-out;

  @keyframes copresentModalPop {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-headline);
`;

const ModalText = styled.p`
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-muted);
`;

const ModalInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  /* >=16px keeps iOS Safari from zooming the viewport on focus. */
  font-size: 16px;
  color: var(--text-primary);
  background: var(--bg-fallback);
  border: 1.5px solid var(--border-chip);
  border-radius: 10px;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;

  &::placeholder {
    color: var(--text-fade);
  }
  &:focus {
    border-color: var(--link);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
  }
`;

const ModalButton = styled.button`
  margin-top: 16px;
  width: 100%;
  appearance: none;
  border: none;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: var(--link);
  cursor: pointer;
  transition: background-color 0.12s, opacity 0.12s;

  &:hover:enabled {
    background: var(--link-hover);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;



// ───────────────────────────── Upgrade dialog ─────────────────────────────
// Split into two bands — a tinted "offer" header (pitch + price) and a plain
// body (what you get + the CTA). The band is doing the hierarchy work: before
// it, the eyebrow, title, price, and feature list were five left-aligned blocks
// at one visual level, so the price read as a heading for the features rather
// than as the cost of the pitch above it.

// Full-bleed on phones, so the overlay has no visible margin for the card to
// float in. Paired with UpgradeCard's mobile block below.
const UpgradeOverlay = styled(ModalOverlay)`
  @media ${MOBILE_QUERY} {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
`;
















// ── Payment-received dialog ──────────────────────────────────────────
// Deliberately plainer than the offer it replaces. The offer has to sell, so it
// earns its price block, feature list and zone rule. This only has to say "it
// worked" and get out of the way — one card, four elements, no bands. Every
// extra sentence here is spent on someone who has already paid and wants to get
// back to presenting.
const PaidCard = styled(ModalCard)`
  max-width: 380px;
  padding: 30px 26px 24px;
  text-align: center;
`;

const PaidBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(34, 197, 94, 0.16);
  color: #22c55e;
  /* Concentric ring instead of a bigger disc — presence without shouting. */
  box-shadow: 0 0 0 8px rgba(34, 197, 94, 0.06);
`;

const PaidTitle = styled.h2`
  margin: 16px 0 0;
  font-size: 22px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-headline);
`;

const PaidText = styled.p`
  margin: 8px 0 0;
  font-size: 14.5px;
  line-height: 1.5;
  color: var(--text-muted);
`;

const PaidEmail = styled.strong`
  color: var(--text-secondary);
  font-weight: 600;
  /* A long Google address must not widen the dialog on a phone. */
  overflow-wrap: anywhere;
`;

const PaidButton = styled(ModalButton)`
  margin-top: 22px;
  padding: 13px 16px;
  font-size: 15px;
  border-radius: 11px;
`;

const BigCheckIcon = () => (
  <svg width="26" height="26" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 10.5l4 4 8-9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


// --- Host controller UI ------------------------------------------------------

const HostPanel = styled.section<{ $center?: boolean }>`
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Verified host: a top-aligned panel above the controller body. Unverified:
     a single sign-in card centred in the empty viewport, footer pinned below. */
  ${(p) =>
    p.$center
      ? "flex: 1 1 auto; justify-content: center; align-items: center; max-width: 480px; padding: 24px 16px;"
      : "flex: 0 0 auto; max-width: 900px; margin-bottom: 4px;"}
`;

// The header a panel in the rail leads with — "Control from your phone", "Live
// previews". Sized as a heading rather than as a caption: at 13px it read as a label ON the content below it, which left each
// panel with no clear top and made switching sections feel like the same page
// changing rather than a different one arriving.
const HostLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-headline);
  text-transform: uppercase;
  // Less tracking than at 13px: uppercase needs the extra air to stay readable
  // when it is small, and keeping it here would space a 16px heading out into a
  // banner.
  letter-spacing: 0.02em;
`;

const HostActionButton = styled.button`
  appearance: none;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 10px;
  border-radius: 9px;
  border: 1px solid var(--border-chip);
  background: var(--bg-chip);
  color: var(--text-primary);
  padding: 9px 14px;
  font-size: 14px;
  font-weight: 600;
  &:hover {
    background: var(--bg-chip-hover);
  }
`;

const RosterList = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RosterItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-primary);

  @media ${MOBILE_QUERY} {
    min-height: 44px;
    font-size: 16px;
  }
`;

// Name column. Takes the slack so the moderation buttons sit hard right and
// don't shuffle position as names change length.
const RosterName = styled.span<{ $paused: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => (p.$paused ? "var(--text-muted)" : "inherit")};
`;

// "Paused" tag next to a paused copresenter's name. The host needs to see the
// state on the roster itself — the pause button alone would leave them reading
// button labels to work out who can currently move the deck.
const PausedTag = styled.span`
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 1px 6px;
  border-radius: 999px;
  color: #b45309;
  background: rgba(245, 158, 11, 0.16);
`;

// Per-row moderation button. Icon-only and deliberately understated — it sits
// beside every name in the room, and a heavy style there would read as a
// warning about the person rather than an action available on them.
//
// Icon-only means the label lives entirely in aria-label + title, so both must
// be set on every instance: without them this is an unlabelled button to a
// screen reader and a mystery glyph to everyone else.
const RosterAction = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 28px: comfortably past the ~24px minimum for a touch target on the mobile
     roster drawer, without making the row taller than the text it sits on. */
  width: 28px;
  height: 28px;
  appearance: none;
  /* Bordered chip AT REST, same vocabulary as the invite-link icon buttons.
     It used to be transparent until hovered, and a bare 15px "||" glyph on a
     roster row reads as text decoration, not a control — hover-only
     affordance also never fires on touch, which is exactly where the mobile
     roster drawer lives. */
  border: 1px solid var(--border-chip);
  background: var(--bg-chip);
  color: var(--text-secondary);
  border-radius: 7px;
  cursor: pointer;
  transition: color 0.12s, background-color 0.12s;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-chip-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 1px;
    color: var(--text-primary);
  }
`;

const StatusDot = styled.span<{ $online: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => (p.$online ? "#22c55e" : "var(--text-fade)")};
`;

/**
 * The section before anyone has joined.
 *
 * It used to be a grey sentence over a link field and two icon buttons — a form
 * the host had to read to find out what it assembled. An empty room is not a
 * roster with nothing in it; it is one proposition ("share this and people can
 * follow") with one thing to do about it, so it is built the way Q&A's empty
 * state is: picture, sentence, button, centred.
 */
const InviteEmpty = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2px 0 24px;
`;

/**
 * Title for the OFFER state only, exactly as the Q&A panel does it.
 *
 * The section wrapper deliberately has no heading — the tab that opened it is
 * still on screen. This is the other half of that same rule: a roster needs no
 * label, but an empty state is a pitch for a feature the host may never have
 * used, and a pitch has to say what it is for. Hidden on a phone, where the
 * sheet arrives with the tab still visible and the panel is short enough that a
 * repeated word costs real estate the pitch needs.
 */
const InviteEmptyTitle = styled.h3`
  margin: 0;
  /* The same 16px as every other panel header in the rail. */
  font-size: 16px;
  font-weight: 700;
  /* Upper case, tracked out to match: at this weight the caps close up and read
     as a block rather than as a word. */
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-headline);

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const InviteEmptyBody = styled.div`
  flex: 1 1 auto;
  /* Stretch, not the parent's centring: the button is full width and would
     otherwise measure itself against the widest line of prose. */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* One gap for all three — picture, sentence and button are three beats of the
     same pitch, and even spacing is what lets the eye move down them in order. */
  gap: 22px;
`;

const InviteEmptyText = styled.p`
  /* A measure, not a width: centred prose wider than about 30 characters makes
     the eye hunt for the start of each line. */
  max-width: 28ch;
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-muted);
`;

/**
 * Copy, as the ONE thing to do here — so it is the primary button, not an icon
 * chip beside a field.
 *
 * There is no field in this state at all. A URL nobody has used yet is not
 * information the host needs on screen; it is a string they need in a chat
 * window, and every version of this that showed it spent the widest element in
 * the panel proving it existed.
 */
const InvitePrimary = styled.button`
  appearance: none;
  cursor: pointer;
  width: 100%;
  border: none;
  border-radius: 9px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  background: var(--link);
  color: #fff;

  @media ${MOBILE_QUERY} {
    min-height: 48px;
    font-size: 15px;
  }
`;

/**
 * What copresenting is, in one picture: your deck on a screen, and the people
 * following it on their own.
 *
 * Drawn from theme tokens rather than a flat image so it holds up in both
 * themes, and stroked in the same weight as the app's icons — the same
 * construction as the Q&A panel's illustration, because these are two empty
 * states of the same rail and any difference in style would read as a
 * difference in kind.
 */
const CopresentIllustration = () => (
  <svg width="132" height="104" viewBox="0 0 132 104" fill="none" aria-hidden="true">
    {/* The deck */}
    <rect
      x="15"
      y="3"
      width="102"
      height="58"
      rx="8"
      stroke="var(--border-chip)"
      strokeWidth="2"
    />
    {/* Two lines of slide, kept vague — this is any deck, not a specific one */}
    <path
      d="M29 21h40M29 32h26"
      stroke="var(--border-chip)"
      strokeWidth="3.5"
      strokeLinecap="round"
    />

    {/* Down to the people following it */}
    <path
      d="M34 61v10h64V61M66 71v9M34 71v9M98 71v9"
      stroke="var(--border-chip)"
      strokeWidth="2"
      strokeLinecap="round"
    />

    {/* You, holding the clicker — the filled one — and two copresenters on
        their own screens, drawn the same size because they can drive it too. */}
    <circle cx="34" cy="90" r="10" fill="var(--link)" />
    <circle cx="66" cy="90" r="10" stroke="var(--border-chip)" strokeWidth="2" />
    <circle cx="98" cy="90" r="10" stroke="var(--border-chip)" strokeWidth="2" />
  </svg>
);

// Free-plan seat meter + upgrade pitch, sat under the roster so the limit is
// visible while there's still room — not sprung on the host at the wall.
const SeatMeter = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-soft);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// The at-capacity notice that names who just got turned away. This replaced a
// modal: a blocked join is the host's problem to solve between slides, not an
// interruption to stop the talk for. Inline in the rail, it waits to be noticed
// instead of seizing the screen. Amber-tinted so it reads as "attention", not
// "error" — nothing is broken, a seat is just full.
const BlockedNotice = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 9px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.28);
`;

// Dismiss, top-right. The notice is a heads-up, not a blocker — a host who's
// seen it (and isn't upgrading right now) can clear it and get back to the deck.
// It still reappears if someone else is turned away, since that's a fresh
// JOIN_BLOCKED.
const BlockedNoticeDismiss = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  display: inline-flex;
  padding: 2px;
  border-radius: 6px;
  color: var(--text-muted);
  transition: color 0.12s, background-color 0.12s;

  &:hover {
    color: var(--text-primary);
    background: rgba(245, 158, 11, 0.18);
  }
`;

const BlockedNoticeText = styled.p`
  margin: 0;
  /* Room so a long line never slides under the dismiss button. */
  padding-right: 16px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
`;

const BlockedNoticeName = styled.strong`
  color: var(--text-primary);
  font-weight: 600;
  overflow-wrap: anywhere;
`;


const SeatMeterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SeatPips = styled.span`
  display: inline-flex;
  gap: 4px;
  flex: 0 0 auto;
`;

const SeatPip = styled.span<{ $taken: boolean }>`
  width: 14px;
  height: 5px;
  border-radius: 3px;
  background: ${(p) => (p.$taken ? "var(--link)" : "var(--border-chip)")};
`;

const UpgradeButton = styled.button`
  appearance: none;
  cursor: pointer;
  width: 100%;
  border: 0;
  border-radius: 9px;
  background: var(--link);
  color: #fff;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 700;
  &:hover {
    filter: brightness(1.06);
  }
`;


// ── The sign-in wall's backdrop ──────────────────────────────────────────
//
// A skeleton of the controller, blurred, with the dialog over it. It is drawn
// from primitives rather than from the real components on purpose: the point of
// the wall is that NOTHING has been fetched yet, and a backdrop assembled from
// the live roster and the live deck title would quietly undo that by requiring
// exactly the data the wall exists to withhold.
//
// So this is scenery. It says "there is a controller here" — which is the true
// and useful thing to say to someone who just pressed Copresent in Slides — and
// it knows nothing about the deck.
const AuthWallSkeleton = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  /* Under the image, so a failed load is a flat panel rather than a white
     flash on a dark page. */
  background: var(--bg-fallback, #1a1b1e);
  /* pointer-events makes it inert even for a mouse that lands outside the
     dialog; aria-hidden on the element covers the third route in — a screen
     reader narrating a controller that does not exist. */
  pointer-events: none;
  user-select: none;
`;

/**
 * A REAL host controller, shipped as a static image.
 *
 * The skeleton this replaced was a handful of grey rectangles, which blurred
 * into an anonymous smudge that could have been any app. Behind the dialog a
 * visitor should see the thing they are about to get — the slide, the notes
 * panel, the thumbnail rail, recognisable as shapes and unreadable as text.
 *
 * Still nothing of theirs: one bundled file, identical for every visitor,
 * showing a Slidesgo demo deck. No fetch, no session, no visitor data. The
 * property the wall exists to hold is enforced by the deck resolve and the
 * socket both being gated on an account — never by this being vague.
 *
 * THE BLUR IS BAKED IN, not a CSS filter. `filter: blur()` over a full-bleed
 * image re-composites on every frame, which is a real cost on the low-end
 * laptops this product runs on; it renders differently per engine; and it
 * leaves a transparent halo at the edges that then needs a scale() hack to
 * hide. Baking it also let the source be downscaled to 960px first — the
 * detail is being destroyed anyway — which took the asset from 69KB to 3KB,
 * on a screen whose whole job is to load before someone decides.
 *
 * NO SCRIM OVER IT, deliberately. Earlier passes darkened the image and laid a
 * radial vignette on top, and between them they crushed the controller into a
 * black void — the backdrop stopped saying anything at all. Contrast for the
 * dialog comes from the dialog: a solid card with a real shadow, which is how
 * every sign-in wall worth copying does it.
 *
 * Regenerate with the snippet in docs/host-key-and-trial.md if the controller
 * is redesigned.
 */
const AuthWallShot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

// The dialog's own layer, above the skeleton.
//
// Not the shared ModalOverlay: that one closes on a backdrop click, and this
// dialog has nothing to close TO. Dismissing it would leave someone staring at
// a blurred rectangle with no way back.
const AuthWallOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  overflow-y: auto;

  > * {
    /* Sign-in dialogs live around 360-400px, and for a reason: one short
       sentence and one button do not need more, and a wide card makes a
       two-line sentence sprawl into three short ones. 480 read as a panel. */
    max-width: 400px;
  }
`;

// Sign-in card shown to an unverified host — a value pitch with clear
// hierarchy (eyebrow → title → feature list → branded CTA).
const SignInCard = styled.div`
  width: 100%;
  /* NOT var(--bg-card). That token is the surface every panel in the
     controller already uses, so behind the wall it put the dialog at the same
     value as the thing it sits on, and the card read as part of the backdrop.
     A modal is the one surface that must be unambiguously nearer.
     
     DARKER than the backdrop, not lighter — which is the counterintuitive
     half. The blurred controller's mid-tones are grey panels, so a lighter
     card lands in the same range and keeps merging; a near-black one separates
     cleanly, and the white Google button gets its maximum contrast against it.
     Tried both side by side; lighter was still muddy. */
  background: #17181c;
  /* A hairline you can actually see. --border-soft is 8% white, which vanishes
     over a busy image. */
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  /* Two shadows and a highlight, doing three different jobs: a wide soft cast
     for depth, a tight one to seat the card, and an inset top edge that reads
     as light catching the near face. Nothing dims the backdrop any more, so
     separation is entirely this card's problem to solve. */
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.66),
    0 4px 12px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
  /* Even on all four sides. The old 22/22/20 read as a wonky bottom margin
     rather than as deliberate optical balance. Roomier than it was: presence
     comes from size as much as contrast, and a cramped card reads as an
     interruption rather than as the point of the screen. */
  padding: 32px;
  display: flex;
  flex-direction: column;
  /* Text block spacing. The button sets its own, larger, gap below — the ask
     and the action should not look like two paragraphs. */
  gap: 12px;
`;

/**
 * The wall's own card: logo-led and centre-aligned.
 *
 * A separate component rather than props on SignInCard, because that one is
 * shared with the room-full dialog — prose that reads correctly ranged left.
 * Centring is right here and wrong there, and a `$centered` flag would just
 * defer the same decision to every call site.
 */
const AuthCard = styled(SignInCard)`
  align-items: center;
  text-align: center;
  gap: 14px;
`;

/**
 * The mark, doing the job the "HOST CONTROLS" eyebrow used to.
 *
 * A logo says which product is asking faster than a label can, and this is the
 * first Copresent surface a signed-out visitor sees — the deck id in the URL
 * tells them nothing. Decorative, so it is hidden from screen readers: the
 * dialog is already named by its title.
 */
const SignInLogo = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  margin-bottom: 2px;
`;

/**
 * The consent line.
 *
 * Signing in CREATES an account, so this is a sign-up in
 * everything but name, and the terms belong in front of someone before they
 * press the button rather than in an email afterwards. Both links open in a
 * new tab on purpose — a host who lands here is usually mid-set-up with a deck
 * already open, and navigating the controller away to read a policy would lose
 * that.
 */
const SignInLegal = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-muted, #9c9da4);

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

const SignInHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Eyebrow = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--link);
`;

const SignInTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: var(--text-headline);
`;

const SignInBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  /* No max-width: the card is already 420px, and a second constraint just
     made the last line break early against an edge nothing else respected. */
`;

const ViewOnlyBanner = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: 900px;
  margin: 0 auto 4px;
  padding: 8px 16px;
  box-sizing: border-box;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  color: var(--text-secondary);
`;

// Branded Google sign-in button — white per Google's guidelines, so it reads
// the same and stands out in both light and dark themes.
const GoogleButton = styled.button`
  appearance: none;
  cursor: pointer;
  /* Full width, not shrink-to-fit. A hug-content button sat flush left and
     lined up with nothing — its right edge landed mid-sentence, which is what
     made the card look unconsidered. Edge to edge, it agrees with the card. */
  width: 100%;
  /* Bigger than the 12px text rhythm, so the action separates from the ask.
     12 + 12 = 24, which sits just under the card's 28px padding rather than
     fighting it. */
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 44px;
  background: #fff;
  color: #1f2937;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 1px 2px var(--shadow-near);
  transition: box-shadow 0.15s ease, transform 0.04s ease;
  &:hover {
    box-shadow: 0 3px 10px var(--shadow-far);
  }
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 2px;
  }
`;

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Text that appears only on the phone, inside a control that is icon-only on
// desktop.
const MobileOnlyLabel = styled.span`
  display: none;

  @media ${MOBILE_QUERY} {
    display: inline;
  }
`;

const LinkRow = styled.div`
  display: flex;
  gap: 6px;

  /* On a phone nobody reads a URL out of a truncated field, and nobody types it
     — they tap Copy and paste it into whatever chat is open. So the field goes
     and Copy becomes the button, at a size that can be hit without looking. */
  @media ${MOBILE_QUERY} {
    gap: 8px;
  }
`;

const LinkInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;

  @media ${MOBILE_QUERY} {
    display: none;
  }
  border-radius: 8px;
  border: 1px solid var(--border-chip);
  background: var(--bg-fallback);
  color: var(--text-secondary);
  padding: 7px 9px;
  font-size: 12px;
`;

// Square icon chips beside the link input — copy, and rotate ("New link").
// Icon-only, so the accessible name and the hover tooltip both come from
// aria-label/title at the call site, not from visible text.
const LinkIconButton = styled.button<{ $primaryOnMobile?: boolean }>`
  appearance: none;
  cursor: pointer;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 32px;

  @media ${MOBILE_QUERY} {
    /* 48px: comfortably past the 44px floor, for a target aimed at while
       talking to a room. */
    height: 48px;
    ${(p) =>
      p.$primaryOnMobile
        ? `flex: 1 1 auto;
           width: auto;
           font-size: 15px;
           font-weight: 600;
           font-family: inherit;`
        : "width: 48px;"}
  }
  border-radius: 8px;
  border: 1px solid var(--border-chip);
  background: var(--bg-chip);
  color: var(--text-secondary);
  padding: 0;
  &:hover:not(:disabled) {
    background: var(--bg-chip-hover);
  }
  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const CopyLinkIcon = () => (
  <svg {...ICON_SVG_PROPS} width={14} height={14}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// The platform share glyph — the box-with-an-arrow iOS and Android both use for
// exactly this gesture. Drawn rather than borrowed so it matches the stroke
// weight of every other icon here, but deliberately the shape a phone user
// already reads as "send this somewhere".
const ShareIcon = () => (
  <svg {...ICON_SVG_PROPS} width={16} height={16}>
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </svg>
);

const QRWrap = styled.div`
  align-self: center;
  background: #fff;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid var(--border-soft);
  line-height: 0;
`;

// --- Host controller shell (verified host: roster | presenting | share) ------
// A three-zone app layout: a left roster rail (who's in the room), the centre
// presenting column (preview / notes / PREV-NEXT), and a right rail for the
// host-facing controls (invite link, slide-control lock, and a collapsible
// "control from your phone" QR). On mobile the roster rail collapses into a
// hamburger-triggered drawer and the right rail stacks under the controls.

const MenuIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg {...ICON_SVG_PROPS}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// Roster moderation icons. Sized to 15px so a pair of them sits inside a
// 14px-text row without making the name look like the secondary element.
const PauseIcon = () => (
  <svg {...ICON_SVG_PROPS} width={15} height={15}>
    <path d="M9 5v14M15 5v14" />
  </svg>
);

// Resume is a play triangle, not a "paused" badge — the button says what
// pressing it DOES, which is the same contract the pause icon has.
const ResumeIcon = () => (
  <svg {...ICON_SVG_PROPS} width={15} height={15}>
    <path d="M7 4.5v15l12-7.5z" />
  </svg>
);

// Remove-from-room: a person with a minus, not a bare ×. The roster's × would
// read as "dismiss this row", and the row would come straight back on the next
// roster broadcast if the person were still there.
const RemoveIcon = () => (
  <svg {...ICON_SVG_PROPS} width={15} height={15}>
    <path d="M15 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1" />
    <circle cx="9" cy="8" r="3.5" />
    <path d="M17 11h5" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg {...ICON_SVG_PROPS} width={16} height={16}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

// Speech-bubble icon for the "Live chat" menu entry.
const ChatIcon = () => (
  <svg {...ICON_SVG_PROPS} width={18} height={18}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

/**
 * The rail's sections.
 *
 * An array, not a switch, so the next feature is one entry rather than a fourth
 * place to remember to edit — which is the whole reason the rail was split.
 */
type RailSectionId = "room" | "qa" | "phone";

interface RailSection {
  readonly id: RailSectionId;
  readonly label: string;
  readonly Icon: () => JSX.Element;
}

/** What QaPanel reports back, for the tab badge. */
interface QaRoomSummary {
  readonly code: string;
  readonly open: boolean;
  readonly waiting: number;
  /** The overlay is up on the presented screen. Drives QaOnDeck, below. */
  readonly showingCode: boolean;
}

/**
 * The join code as the ROOM sees it, drawn on the host's own slide preview.
 *
 * The host presses "30s" in a rail on their laptop and the thing it affects is
 * on another screen, often behind them. Until the extension's overlay ships
 * there is nothing at all to confirm it worked, and afterwards there is still
 * nothing on the surface they are actually looking at — so the preset was a
 * button pressed on faith.
 *
 * A FACSIMILE, not a screenshot. Previews are Slides API thumbnails of the slide
 * itself; the overlay is drawn by the extension into the presented page and is
 * not part of the deck, so it can never appear in one. This redraws it — same
 * plate, same corner, same content — which is honest about position and content
 * and deliberately not to scale: rendered at its true ~16% of the screen width
 * the code would be four pixels tall in a preview this size, which confirms
 * nothing. It is a reminder that the code is up and where, not a proof sheet.
 */
const QaOnDeck = styled.div`
  position: absolute;
  right: 4%;
  bottom: 5%;
  display: flex;
  /* A column, like the real card: the address runs UNDER the QR, which is what
     lets the whole thing sit on one line instead of being split into a host and
     a code that the reader has to join back up. */
  flex-direction: column;
  align-items: center;
  gap: 5px;
  max-width: 60%;
  padding: 7px 9px;
  border-radius: 9px;
  /* A solid plate, like the real one: a QR over an arbitrary slide background
     does not scan, and the slide underneath is not ours to predict. */
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
  color: #1f1f1f;
  /* The preview is not interactive and neither is the overlay it is standing
     in for — clicks belong to the card underneath. */
  pointer-events: none;
`;

// A heading, not a caption — the deck's card leads with this at 20px bold so
// the back row can tell the plate is an invitation rather than decoration, and
// a facsimile that keeps it as small grey text misreports the emphasis.
const QaOnDeckHint = styled.span`
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  color: #1f1f1f;
  white-space: nowrap;
`;

// The address, for everyone the QR does not reach: the back row, anyone on a
// laptop, anyone whose camera app refuses. One string in one font — split into
// a host and a code at two sizes it read as two facts rather than as somewhere
// to go.
const QaOnDeckUrl = styled.span`
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

// The dashboard. Not a section of this rail — it leaves the controller — so it
// sits above the sections rather than among them.
const HomeIcon = () => (
  <svg {...ICON_SVG_PROPS} width={20} height={20}>
    <path d="M3.5 10.5 12 3.5l8.5 7" />
    <path d="M5.5 9v11h13V9" />
    <path d="M10 20v-5.5h4V20" />
  </svg>
);

// Two people — the copresenters section. Deliberately not a single silhouette:
// the section is about who ELSE is in the room, and one head reads as "account".
const PeopleIcon = () => (
  <svg {...ICON_SVG_PROPS} width={20} height={20}>
    <path d="M16 20v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 18.4V20" />
    <circle cx="9.5" cy="7.5" r="3.4" />
    <path d="M21 20v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.4 4.2a3.4 3.4 0 0 1 0 6.6" />
  </svg>
);

// Pair the deck with a phone — its own section now rather than a block wedged
// under the roster. It is a thing you do once at the start of a talk, so it has
// no business taking permanent space in a section you read throughout one.
const PhoneIcon = () => (
  <svg {...ICON_SVG_PROPS} width={20} height={20}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.4" />
    <path d="M11 18.6h2" />
  </svg>
);

const RAIL_SECTIONS: readonly RailSection[] = [
  // "Copresent", not "Room": the word the product is named for, and the one
  // that says what the section is FOR. "Room" named the container and left the
  // host to work out what went in it — and it collided with the Q&A room the
  // tab beside it opens, which is a different room with a different audience.
  { id: "room", label: "Copresent", Icon: PeopleIcon },
  { id: "qa", label: "Q&A", Icon: ChatIcon },
  { id: "phone", label: "Phone", Icon: PhoneIcon },
];

// Phone pairing is desktop-only (there is no second phone to pair with from a
// phone), so the touch layout's menu offers the other two.
const MOBILE_MENU_SECTIONS = RAIL_SECTIONS.filter((s) => s.id !== "phone");

const DesktopHamburger = styled(IconButton)`
  display: none;
`;

// Hamburger only exists on mobile, where the roster + invite controls live in a
// drawer instead of the side rails.
const Hamburger = styled(IconButton)`
  @media ${DESKTOP_QUERY} {
    display: none;
  }
`;

// Host body: three full-height columns — roster | presenting | share/lock/QR.
// The rails are equal width and span the whole height; the title, slide count
// and status live in the CENTRE column's header (mirroring the copresenter
// chrome). On mobile both rails collapse into the hamburger drawer and the
// centre column goes full-width.
const HostBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
  display: flex;
  /* Positioning context for the floating collapse tab on the left divider. */
  position: relative;
`;

// Collapsing takes the DETAILS away, never the icons.
//
// It used to hide the whole rail, which on a split rail would take the only way
// back to Q&A with it — you'd collapse to see more slide and lose the navigation
// as well. The strip is the app's spine now, so it stays put and the panel is
// what folds.
/**
 * 352px, of which 64 is the icon strip and 32 is padding — so the PANEL is
 * 256px wide.
 *
 * It was 304 (a 208px panel), which was set when the rail held a roster and a
 * link and nothing else. Sections put a Q&A queue, a join card and a live
 * status row in the same column, and at 208 they were all fighting: question
 * text wrapped every three or four words, and the overlay's status row broke
 * "On your deck" across three lines to make room for a countdown and a button.
 *
 * Still narrower than the notes rail opposite (360), which is the right way
 * round — notes are read as prose, this is scanned.
 */
const RosterRail = styled.aside<{ $collapsed: boolean }>`
  flex: 0 0 ${(p) => (p.$collapsed ? HOST_RAIL_STRIP : HOST_RAIL_WIDTH)}px;
  width: ${(p) => (p.$collapsed ? HOST_RAIL_STRIP : HOST_RAIL_WIDTH)}px;
  box-sizing: border-box;
  border-right: 1px solid var(--border-soft);
  /* Icon strip | panel. Padding belongs to each, not to the rail: the strip's
     active-tab highlight has to run to its own edges. */
  display: flex;
  flex-direction: row;
  /* Visible, not hidden: the account menu opens out of the 64px strip and over
     the panel, and clipping it here would leave a menu 52px wide. */
  overflow: visible;
  transition: flex-basis 0.18s ease, width 0.18s ease;

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

// The section switcher.
//
// A vertical icon strip on desktop and a horizontal one across the top of the
// mobile drawer — the same two destinations either way, because a host who
// learns the app on a laptop and then presents from a phone should not have to
// learn it twice.
//
// It exists because the rail used to be one scroll holding everything: who is in
// the room, the invite link, Q&A, the phone QR and the account. That was already
// long, and every feature added to it made the next one harder to find. Sections
// are what let a feature arrive without crowding the ones already there.
/**
 * How far the strip is padded on its right, and therefore how far the selected
 * tab has to reach to touch the panel. One constant so the two cannot drift.
 */
const SECTION_NAV_PAD = 6;
/** Radius of the tab's rounded left corners and of the notches beside it. */
const SECTION_TAB_RADIUS = 10;

const SectionNav = styled.nav`
  /* The touch layout reaches sections from its menu instead (see
     MOBILE_MENU_SECTIONS), so a strip here would repeat it. */
  display: none;

  @media ${DESKTOP_QUERY} {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 0 0 64px;
    width: 64px;
    padding: 12px ${SECTION_NAV_PAD}px;
    /* No divider against the panel: the panel's own raised surface already
       separates the two, and a rule on top of that reads as clutter. */
    border-right: none;
  }
`;

// Pushes whatever follows it to the foot of the strip — the account avatar.
const SectionNavSpacer = styled.span`
  display: none;

  @media ${DESKTOP_QUERY} {
    display: block;
    flex: 1 1 auto;
  }
`;

const SectionTab = styled.button<{ $active: boolean; $connected: boolean }>`
  appearance: none;
  cursor: pointer;
  position: relative;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  border: none;
  border-radius: ${SECTION_TAB_RADIUS}px;
  padding: 8px 4px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  background: ${(p) => (p.$active ? "var(--bg-chip)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--link)" : "var(--text-muted)")};

  &:hover {
    color: var(--link);
  }

  @media ${DESKTOP_QUERY} {
    flex: 0 0 auto;
  }

  /* The selected tab is not a chip beside the panel — it is the panel, reaching
     back into the strip to say where its contents came from.
     
     So it takes the panel's own colour, loses its right-hand corners, and grows
     the 6px that separated the two, leaving no seam. The right padding grows
     with it and the left does not, which keeps the icon centred in the part
     that still LOOKS like a tab rather than in the wider box.
     
     Only while the panel is actually there: collapsed, the tab would be a shape
     flowing into nothing, so it goes back to being a chip. */
  ${(p) =>
    p.$active &&
    p.$connected &&
    `@media ${DESKTOP_QUERY} {
       background: var(--bg-fallback);
       border-radius: ${SECTION_TAB_RADIUS}px 0 0 ${SECTION_TAB_RADIUS}px;
       margin-right: -${SECTION_NAV_PAD}px;
       padding-right: ${4 + SECTION_NAV_PAD}px;

       /* The notches above and below, where the strip's colour tucks into the
          join. Without them the tab meets the panel at two hard right angles,
          which reads as a rectangle that happens to touch rather than as one
          surface. Each is a square of PANEL colour with a disc masked out of
          its inner corner, leaving the concave wedge. */
       &::before,
       &::after {
         content: "";
         position: absolute;
         right: 0;
         width: ${SECTION_TAB_RADIUS}px;
         height: ${SECTION_TAB_RADIUS}px;
         background: var(--bg-fallback);
         pointer-events: none;
       }

       &::before {
         top: -${SECTION_TAB_RADIUS}px;
         -webkit-mask: radial-gradient(
           circle at 0 0,
           transparent ${SECTION_TAB_RADIUS}px,
           #000 ${SECTION_TAB_RADIUS}px
         );
         mask: radial-gradient(
           circle at 0 0,
           transparent ${SECTION_TAB_RADIUS}px,
           #000 ${SECTION_TAB_RADIUS}px
         );
       }

       &::after {
         bottom: -${SECTION_TAB_RADIUS}px;
         -webkit-mask: radial-gradient(
           circle at 0 100%,
           transparent ${SECTION_TAB_RADIUS}px,
           #000 ${SECTION_TAB_RADIUS}px
         );
         mask: radial-gradient(
           circle at 0 100%,
           transparent ${SECTION_TAB_RADIUS}px,
           #000 ${SECTION_TAB_RADIUS}px
         );
       }
     }`}
`;

/**
 * Unanswered questions, on the Q&A tab, while the host is looking at Room.
 *
 * Not decoration — it is what makes hiding a section safe. Q&A behind a tab is
 * a room quietly filling with questions the host cannot see, and the whole
 * point of the feature is that they get seen. The panel stays mounted and
 * streaming while the tab is inactive precisely so this stays live.
 */
const TabBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 6px;
  min-width: 16px;
  height: 16px;
  box-sizing: border-box;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--link);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

// Everything to the right of the icon strip: the active section, then the
// pinned footer. Scrolling happens INSIDE the section, so the footer stays put.
//
// Raised off the page colour on purpose. The strip sits on the page background
// and this sits one step above it, so the two read as separate surfaces without
// a dividing rule — which is what lets the strip feel like chrome and the panel
// feel like content.
const RailBody = styled.div<{ $collapsed: boolean }>`
  display: ${(p) => (p.$collapsed ? "none" : "flex")};
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  background: var(--bg-fallback);

  @media ${DESKTOP_QUERY} {
    padding: 18px 16px;
    overflow: hidden;
  }
`;

/**
 * The Q&A section's box.
 *
 * Hidden with display:none rather than unmounted, which also takes it out of
 * the accessibility tree. Unmounting would drop the question stream the moment
 * the host looked at the roster — so the badge would go stale, the restore
 * would re-run on every tab switch, and a host flicking between sections would
 * reconnect an SSE stream each time.
 */
// Desktop only — a phone has no second phone to pair with — and hidden the same
// way the other sections are.
const PhonePane = styled.div<{ $hidden: boolean }>`
  display: none;

  @media ${DESKTOP_QUERY} {
    ${(p) =>
      p.$hidden
        ? "display: none;"
        : `display: flex;
           flex-direction: column;
           align-items: flex-start;
           gap: 10px;
           flex: 1 1 auto;
           min-height: 0;
           overflow-y: auto;`}
  }
`;

const QaPane = styled.div<{ $hidden: boolean }>`
  display: ${(p) => (p.$hidden ? "none" : "block")};

  @media ${DESKTOP_QUERY} {
    ${(p) =>
      p.$hidden
        ? "display: none;"
        : `display: flex;
           flex-direction: column;
           flex: 1 1 auto;
           min-height: 0;
           overflow-y: auto;`}
  }
`;

// Roster + invite live in this scroll region, so a growing room scrolls HERE
// rather than shoving the pinned footer (QR + account) off the bottom — the bug
// the old margin-top:auto pin caused, where enough copresenters pushed the
// footer below the fold with no way to scroll to it.
//
// Desktop only: flex:1 eats the rail's free space (which drops the footer to the
// foot), and min-height:0 + overflow-y lets it scroll internally. On mobile it's
// display:contents — no box at all — so the drawer flows and scrolls exactly as
// before.
// Hiding is spelled out in BOTH branches rather than appended as an override:
// stylis groups a block's plain declarations together and emits the media query
// after them, so a trailing `display: none` loses to the `display: flex` inside
// the query and the section stays on screen above the one that replaced it.
//
// The `hidden` ATTRIBUTE goes on alongside this (see the render), and the CSS is
// still required despite it: the UA's `[hidden] { display: none }` is beaten by
// any author rule that sets display, so `display: contents` below would happily
// show a subtree marked hidden. The two have to agree, and here they do.
const RosterScroll = styled.div<{ $hidden: boolean }>`
  display: ${(p) => (p.$hidden ? "none" : "contents")};

  @media ${DESKTOP_QUERY} {
    ${(p) =>
      p.$hidden
        ? "display: none;"
        : `display: flex;
           flex-direction: column;
           flex: 1 1 auto;
           min-height: 0;
           overflow-y: auto;`}
  }
`;

// Wrapper for the free plan's standing upgrade button. Middle element of the
// pinned footer (QR → this → account); RosterScroll's flex:1 above drops the
// whole group to the foot on desktop, so margin-top here is just a divider.
const StandingUpgrade = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-soft);

  /* Mobile keeps the auto margin: the QR is display:none here, so this is the
     bottom group's first element and drops it to the foot of the drawer. */
  @media ${MOBILE_QUERY} {
    margin-top: auto;
  }
`;

// ── Signed-in account, foot of the left rail ─────────────────────────
// The host controller otherwise never says who you are or what plan you're on,
// and both hang off the same Google account: the deck, the magic link, and the
// entitlement. A host with a work and a personal account has no other way to
// tell which one this room belongs to — the seat meter above only appears on
// the free plan, so its ABSENCE is the only current signal of Pro, which is no
// signal at all.
// Wraps the account row and, on Free, the usage line beneath it. Carries
// the divider from the content above, so both belong to one visual block.
const AccountBlock = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-soft);
  /* Anchors the sign-out menu, which opens upward from this row. */
  position: relative;
`;

// The identity half of the row (avatar + name/address) as the menu trigger.
//
// Deliberately NOT the whole AccountBar: the plan badge next to it is already a
// button on the free plan, and nesting a button inside a button is invalid and
// swallows the inner click.
const AccountIdentityButton = styled.button`
  appearance: none;
  border: 0;
  background: none;
  padding: 2px;
  margin: -2px;
  border-radius: 8px;
  cursor: pointer;
  /* Same box the avatar + text occupied before, so the row doesn't shift. */
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
  text-align: left;
  color: inherit;
  font: inherit;
  transition: background 0.12s;

  &:hover {
    background: var(--bg-chip);
  }

  &:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 1px;
  }
`;

// Opens UPWARD: this row sits at the foot of the left rail, so a menu dropping
// down would fall off the bottom of the drawer on mobile.
// Foot of the icon strip. Desktop only — the mobile drawer keeps the wide row,
// where there is room for it and no strip to put an avatar in.
const AccountStrip = styled.div`
  display: none;

  @media ${DESKTOP_QUERY} {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding-top: 8px;
    /* Anchors the menu, which opens upward and out over the panel. */
    position: relative;
  }
`;

/**
 * Plan, under the avatar: PRO or FREE.
 *
 * Three characters of state where a name, an address and a countdown used to
 * sit in the panel below. Which plan you are on is the only part of that a host
 * ever acts on mid-talk; the rest is identity they already know, and it was
 * taking the foot of every section to tell them.
 */
const PlanPill = styled.span<{ $muted: boolean }>`
  padding: 2px 8px;
  border-radius: 999px;
  /* Sentence case, and a little larger for it: the uppercase treatment the
     other chips use needs letter-spacing to stay readable, and at pill size
     that spends more width than the word is worth. */
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) =>
    p.$muted ? "var(--bg-chip)" : "rgba(99, 102, 241, 0.16)"};
  color: ${(p) => (p.$muted ? "var(--text-fade)" : "var(--link)")};
`;

const AccountAvatarButton = styled.button<{ $free: boolean }>`
  appearance: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 50%;
  background: none;
  /* A ring, not a "Free" pill: at this size a word is unreadable, and the ring
     still marks the account as unfinished business without shouting. */
  border: 1.5px solid ${(p) => (p.$free ? "var(--link)" : "transparent")};
  line-height: 0;

  &:hover {
    border-color: var(--link);
  }
`;

const AccountMenuHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 10px 9px;
  margin-bottom: 3px;
  border-bottom: 1px solid var(--border-soft);
`;

// The Free allowance in the menu: what's left, as words and as a meter, then
// when it comes back. Its own section, between who you are and what you can do.
const AllowanceBlock = styled.div`
  padding: 10px 10px 11px;
  margin-bottom: 3px;
  border-bottom: 1px solid var(--border-soft);
`;

const AllowanceCount = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

// One segment per presentation: at 5 a month, "how many" reads at a glance
// in a way a continuous bar doesn't.
const AllowanceMeter = styled.div`
  display: flex;
  gap: 3px;
  margin: 7px 0 6px;
`;

const AllowanceSegment = styled.span<{ $on: boolean; $tone: "ok" | "low" | "out" }>`
  flex: 1 1 0;
  height: 4px;
  border-radius: 2px;
  background: ${(p) =>
    !p.$on
      ? "var(--border-chip)"
      : p.$tone === "low"
      ? "#f59e0b"
      : "var(--link)"};
`;

const AllowanceReset = styled.div`
  font-size: 11px;
  color: var(--text-fade);
`;

const AccountMenu = styled.div<{ $wide?: boolean }>`
  position: absolute;
  bottom: calc(100% - 6px);
  left: 0;
  right: 0;
  /* Out of the strip and over the panel: 52px of usable width would truncate
     every address this menu exists to show. */
  ${(p) => p.$wide && "right: auto; min-width: 220px;"}
  z-index: 20;
  padding: 4px;
  border-radius: 10px;
  border: 1px solid var(--border-chip);
  background: var(--bg-card);
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.35);
`;

const AccountMenuItem = styled.button`
  appearance: none;
  width: 100%;
  border: 0;
  background: none;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  text-align: left;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: var(--bg-chip);
    color: var(--link);
  }

  &:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: -2px;
  }
`;

const AccountUpgradeItem = styled(AccountMenuItem)`
  color: var(--link);
`;

const AccountBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const AccountAvatar = styled.img`
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
  background: var(--bg-chip);
`;

// Shown when there's no picture, or Google's CDN refuses it. Not paranoia: a
// broken <img> would leave a torn-icon glyph in the corner of every session.
const AccountAvatarFallback = styled.div`
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--link);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
`;

const AccountText = styled.div`
  /* min-width:0 — without it a flex child refuses to shrink below its content,
     so the ellipsis below never engages and a long address blows out the rail. */
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const AccountName = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AccountEmail = styled.span`
  font-size: 11px;
  color: var(--text-fade);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Sentence case, matching PlanPill in the desktop strip — the same badge in the
// other place the account appears. Uppercase needs letter-spacing to stay
// readable, and at pill size that spends more width than the word is worth.
const PlanBadge = styled.span`
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  background: rgba(99, 102, 241, 0.16);
  color: var(--link);
`;

// The Free usage line, on its own line UNDER the account row. Indented to line
// up under the name, not the avatar.
const UsageLine = styled.div`
  margin-top: 8px;
  padding-left: 40px; /* 30px avatar + 10px row gap → aligns under the name */
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: var(--link);
`;

// Free is a button, Pro is a label. This is the one place the plan is named, so
// it's where someone looks when they want to change it — and there's nothing to
// change once you're Pro.
const PlanBadgeButton = styled.button`
  flex: 0 0 auto;
  appearance: none;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-chip);
  background: none;
  /* Matches PlanBadge above — the two sit in the same slot and must not differ
     in casing depending on which plan you are on. */
  font-size: 10.5px;
  font-weight: 700;
  color: var(--text-muted);
  transition: color 0.12s, border-color 0.12s;

  &:hover {
    color: var(--link);
    border-color: var(--link);
  }
`;

type MenuAllowance = { left: number; limit: number; resetsOn: string | null };

const HostAccountBar = ({
  profile,
  isPro,
  onUpgrade,
  onSignOut,
  usageLine = null,
  allowance = null,
  compact = false,
}: {
  profile: { email: string; name: string; picture: string } | undefined;
  // undefined/null = we don't know yet (still resolving, or a backend older
  // than the plan flag).
  isPro: boolean | null | undefined;
  onUpgrade: () => void;
  onSignOut: () => void;
  // "7 of 10 free presentations used this month". Null hides it: Pro, shadow
  // mode, or too far from the limit to be worth a line.
  usageLine?: string | null;
  // The Free allowance for the avatar menu. Null hides it: Pro, or the
  // backend hasn't said yet.
  allowance?: MenuAllowance | null;
  /**
   * Avatar only, for the 64px icon strip. Everything the wide row spells out —
   * who you are, which plan, how to upgrade — moves
   * into the menu rather than being dropped, because the strip is the only
   * place the desktop rail says any of it now.
   */
  compact?: boolean;
}) => {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const blockRef = useRef<HTMLDivElement | null>(null);

  // Dismiss on outside click or Escape. Without this the menu survives every
  // other interaction in the rail, and on mobile there is nowhere obvious to
  // tap to get rid of it.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!blockRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  if (!profile) return null;

  const label = profile.name || profile.email;
  const initial = (label || "?").trim().charAt(0).toUpperCase() || "?";

  const avatar =
    profile.picture && !avatarFailed ? (
      <AccountAvatar
        src={profile.picture}
        alt=""
        /* Google's avatar CDN 403s a request that carries our referrer, which
           would break the picture for every host rather than some. */
        referrerPolicy="no-referrer"
        onError={() => setAvatarFailed(true)}
      />
    ) : (
      <AccountAvatarFallback aria-hidden="true">{initial}</AccountAvatarFallback>
    );

  // Not a menuitem: it is information, and making it focusable would put a
  // dead stop in the menu's keyboard order.
  const allowanceSection = allowance && (
    <AllowanceBlock>
      <AllowanceCount>
        {allowance.left} of {allowance.limit} presentation{allowance.limit === 1 ? "" : "s"} left
      </AllowanceCount>
      {allowance.limit <= 10 && (
        <AllowanceMeter
          role="meter"
          aria-label="Free presentations left"
          aria-valuemin={0}
          aria-valuemax={allowance.limit}
          aria-valuenow={allowance.left}
        >
          {Array.from({ length: allowance.limit }, (_, i) => (
            <AllowanceSegment
              key={i}
              $on={i < allowance.left}
              $tone={allowance.left <= 1 ? "low" : "ok"}
            />
          ))}
        </AllowanceMeter>
      )}
      {allowance.resetsOn && <AllowanceReset>Resets on {allowance.resetsOn}</AllowanceReset>}
    </AllowanceBlock>
  );

  const upgradeItem = isPro === false && (
    <AccountUpgradeItem
      type="button"
      role="menuitem"
      onClick={() => {
        setMenuOpen(false);
        onUpgrade();
      }}
    >
      Upgrade to Pro
    </AccountUpgradeItem>
  );

  if (compact) {
    return (
      <AccountStrip ref={blockRef}>
        {menuOpen && (
          <AccountMenu role="menu" $wide>
            {/* The identity the wide row used to show inline. Not a menuitem —
                it is a label, and making it focusable would put a dead stop in
                the middle of the menu's keyboard order. */}
            <AccountMenuHead>
              <AccountName title={label}>{label}</AccountName>
              {profile.name && profile.email && (
                <AccountEmail title={profile.email}>{profile.email}</AccountEmail>
              )}
            </AccountMenuHead>
            {allowanceSection}
            {/* Only when the backend has actually said Free — see the badge
                note below; defaulting the unknown state would offer a paying
                host an upgrade on every reconnect. */}
            {upgradeItem}
            <AccountMenuItem
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onSignOut();
              }}
            >
              Sign out
            </AccountMenuItem>
          </AccountMenu>
        )}

        <AccountAvatarButton
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          // The whole meaning of an icon-only control lives in its name, and
          // "Account" alone would not say whose.
          aria-label={`Account — ${label}`}
          title={profile.email ? `Signed in as ${profile.email}` : "Account"}
          $free={isPro === false}
        >
          {avatar}
        </AccountAvatarButton>

        {/* Nothing at all until the backend has said — see the badge note in
            the wide row below. Defaulting the unknown state to "Free" would
            stamp it on a paying host on every reconnect. */}
        {isPro !== null && isPro !== undefined && (
          <PlanPill $muted={isPro === false}>
            {isPro === false ? "Free" : "Pro"}
          </PlanPill>
        )}
      </AccountStrip>
    );
  }

  return (
    <AccountBlock ref={blockRef}>
      {menuOpen && (
        <AccountMenu role="menu">
          {allowanceSection}
          <AccountMenuItem
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              onSignOut();
            }}
          >
            Sign out
          </AccountMenuItem>
        </AccountMenu>
      )}

      <AccountBar>
        <AccountIdentityButton
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title={profile.email ? `Signed in as ${profile.email}` : "Account"}
        >
          {avatar}

          <AccountText>
            {/* title= so a truncated name/address is still readable on hover. */}
            <AccountName title={label}>{label}</AccountName>
            {profile.name && profile.email && (
              <AccountEmail title={profile.email}>{profile.email}</AccountEmail>
            )}
          </AccountText>
        </AccountIdentityButton>

        {/* No badge until the backend has actually said. Defaulting the unknown
            state to "Free" would stamp it on a paying host every reconnect — the
            same reason isFreeHost tests `=== false` rather than falsy. */}
        {isPro === true && <PlanBadge title="Copresent Pro">Pro</PlanBadge>}
        {isPro === false && (
          <PlanBadgeButton
            type="button"
            onClick={onUpgrade}
            title="Upgrade to Copresent Pro"
          >
            Free
          </PlanBadgeButton>
        )}
      </AccountBar>

      {usageLine && <UsageLine>{usageLine}</UsageLine>}
    </AccountBlock>
  );
};

// Floating pill on the left divider that collapses / expands the roster rail
// (desktop only — on mobile the roster lives in the hamburger drawer). Sits
// astride the divider when expanded and tucks to the far-left edge when
// collapsed; the chevron points the way it will move the panel.
const CollapseTab = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  /* Straddles the rail's right edge — half on, half off — at whichever width
     the rail currently is. Collapsed still leaves the icon strip, so this no
     longer tucks to the window edge.
     Derived, never written out: see the constants for what a stale literal here
     costs. */
  left: ${(p) =>
    (p.$collapsed ? HOST_RAIL_STRIP : HOST_RAIL_WIDTH) - RAIL_TAB_WIDTH / 2}px;
  z-index: 6;
  width: ${RAIL_TAB_WIDTH}px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  background: var(--bg-card);
  color: var(--text-secondary);
  border: 1px solid var(--border-soft);
  border-radius: 13px;
  box-shadow: 0 2px 8px var(--shadow-far);
  transition: left 0.18s ease, background 0.12s, color 0.12s;

  &:hover {
    background: var(--bg-chip-hover);
    color: var(--text-primary);
  }

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const CollapseChevron = styled.span<{ $collapsed: boolean }>`
  display: inline-flex;
  transition: transform 0.18s ease;
  transform: rotate(${(p) => (p.$collapsed ? "180deg" : "0deg")});
`;

// Same floating pill, on the RIGHT divider, collapsing / expanding the notes
// rail. Straddles the divider when the rail is open and tucks to the far-right
// edge when collapsed.
const NotesCollapseTab = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  /* Derived, for the same reason the roster's is — see RAIL_TAB_WIDTH. */
  right: ${(p) =>
    p.$collapsed
      ? NOTES_TAB_EDGE_GAP
      : NOTES_RAIL_WIDTH - RAIL_TAB_WIDTH / 2}px;
  z-index: 6;
  width: ${RAIL_TAB_WIDTH}px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  background: var(--bg-card);
  color: var(--text-secondary);
  border: 1px solid var(--border-soft);
  border-radius: 13px;
  box-shadow: 0 2px 8px var(--shadow-far);
  transition: right 0.18s ease, background 0.12s, color 0.12s;

  &:hover {
    background: var(--bg-chip-hover);
    color: var(--text-primary);
  }

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const HostMain = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

// Right rail: speaker notes, shown by default and collapsible via the divider
// tab. The notes fill the sidebar directly (see below) rather than sitting in a
// bordered card, so the whole column reads as the notes surface.
const NotesRail = styled.aside`
  flex: 0 0 ${NOTES_RAIL_WIDTH}px;
  width: ${NOTES_RAIL_WIDTH}px;
  box-sizing: border-box;
  border-left: 1px solid var(--border-soft);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

const NotesRailHead = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const NotesRailFonts = styled.div`
  display: inline-flex;
  gap: 4px;
`;

// The notes text itself, filling the rail — no card border/background, just a
// scrolling document that owns the column.
const NotesRailBody = styled.div<{ $scale: number }>`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  color: var(--text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
  font-size: calc(clamp(15px, 0.5vw + 12px, 19px) * ${(p) => p.$scale});
  -webkit-overflow-scrolling: touch;
`;

const NotesRailViewport = styled.div`
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
`;

// Bottom scroll fade for the rail notes (rail has no card, so this spans the
// column and fades into the page background).
const NotesRailFade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 56px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: linear-gradient(to bottom, transparent, var(--bg-page) 88%);
`;

const NotesRailEmpty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-fade);
  font-size: 13px;
  font-style: italic;
  padding: 12px;
`;

// Centre-column header — title, slide count and connection status, matching the
// copresenter chrome — with the theme toggle pinned top-right and (on mobile)
// the menu button top-left. Side padding clears both corner controls.
const HostCenterHead = styled.header`
  flex: 0 0 auto;
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 16px 48px 10px;
  text-align: center;

  /* The touch layout has no corner controls to clear: the menu button sits in
     the title row (HeadTitleRow). */
  @media ${MOBILE_QUERY} {
    padding: calc(8px + env(safe-area-inset-top, 0px)) 8px 10px;
  }
`;

// Touch layout: the title with the menu button on the same line. A spacer the
// button's width on the left keeps the title centred, as it is everywhere else.
const HeadTitleRow = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 4px;
  min-height: 44px;
`;

const HeadMenuButton = styled(IconButton)`
  position: relative;
  width: 44px;
  height: 44px;

  svg {
    width: 22px;
    height: 22px;
  }
`;

// A dot on the menu button while questions are waiting. The Q&A section that
// used to carry a count on its own tab is inside the menu now, and a signal only
// visible once the menu is open would never tell a speaker to open it. A dot,
// not a number: at arm's length mid-sentence the question is "is anyone
// waiting?", and the count is one tap away on the Q&A row.
const MenuDot = styled.span`
  position: absolute;
  top: 8px;
  right: 7px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--link);
  box-shadow: 0 0 0 2px var(--bg-page);
  pointer-events: none;
`;

const HeadCornerRight = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  /* Above HostCenterHead, which follows in the DOM and is position:relative —
     without this it paints on top and swallows clicks wherever the centred
     header overlaps this corner. */
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 2px;
`;

// Host header theme toggle: top-right on desktop, but on mobile it moves to the
// LEFT so the hamburger (HeadCornerRight) owns the top-right corner. On desktop
// HeadCornerRight is empty (the hamburger is mobile-only), so they never clash.
const HeadCornerTheme = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  display: flex;
  align-items: center;
  @media ${MOBILE_QUERY} {
    right: auto;
    left: 10px;
  }
`;

// Full-screen loading state while we resolve whether a returning (already
// signed-in) host is verified — avoids flashing the sign-in card for a beat
// before the controller appears.
const HostLoading = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--text-muted);
  font-size: 14px;
`;

// Signed-in host whose deck has no live session yet. Calm, informational —
// never an error — and the controller keeps polling behind it, so it swaps to
// the live controls the instant the host starts presenting.
const HostIdleTitle = styled.div`
  font-size: 18px;
  font-weight: 650;
  color: var(--text-headline);
`;

const HostIdleBody = styled.div`
  max-width: 340px;
  text-align: center;
  line-height: 1.5;
  color: var(--text-muted);
`;

// The Free presentation limit screen. Same calm shape as the idle screen:
// it's a plan boundary, not an error.
const LimitActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: min(300px, 100%);
  margin-top: 6px;
`;

const LimitLink = styled.a`
  text-align: center;
  font-size: 14px;
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 3px;
`;

// How long the host controller waits on extension-drawn previews (COP-19)
// before offering "Enable live previews" again. The current slide is drawn
// first, so it normally lands in 2–5 s; a 150-slide /embed is slower to fetch.
const PREVIEW_LOADING_TIMEOUT_MS = 30_000;

// A slide image the host's extension drew and uploaded (COP-19/COP-27). Those
// are free for every host, unlike Slides API renders, which stay Pro. Their
// URLs carry the drawing's hash as the revision: /preview/img/{deck}/{page}/u.{hash}/…
const isUploadedPreview = (url?: string | null): boolean =>
  !!url && /\/u\.[0-9a-f]{32,64}\//.test(url);

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid var(--border-chip);
  border-top-color: var(--link);
  animation: copresentSpin 0.7s linear infinite;

  @keyframes copresentSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// A labelled block below the roster list (invite link, previews nudge), set off
// with a hairline divider so it reads as distinct from the roster.
const RosterSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--border-soft);
`;

// Same block, but only inside the mobile drawer — on desktop the preview window
// carries the enable-previews call to action, so this would be a duplicate.
const MobileRosterSection = styled(RosterSection)`
  @media ${DESKTOP_QUERY} {
    display: none;
  }
`;

// The free copresent pass, laid out like the plan section of the avatar menu:
// a label with the count beside it, a meter, then one line on what to know.
const PassHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
`;

const PassLabel = styled.span`
  font-weight: 600;
  color: var(--text-secondary);
`;

const PassCount = styled.span`
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const FreeCopresentPass = ({
  pass,
}: {
  pass: { used: number; limit: number; remaining: number; today: boolean };
}) => {
  const left = Math.max(0, pass.remaining);
  const usedUp = left === 0 && !pass.today;
  return (
    <div>
      <PassHead>
        <PassLabel>Free copresenting</PassLabel>
        {!usedUp && (
          <PassCount>
            {left} of {pass.limit} left
          </PassCount>
        )}
      </PassHead>
      {!usedUp && pass.limit <= 10 && (
        <AllowanceMeter
          role="meter"
          aria-label="Free copresented sessions left"
          aria-valuemin={0}
          aria-valuemax={pass.limit}
          aria-valuenow={left}
        >
          {Array.from({ length: pass.limit }, (_, i) => (
            <AllowanceSegment key={i} $on={i < left} $tone={left <= 1 ? "low" : "ok"} />
          ))}
        </AllowanceMeter>
      )}
      <RailHint style={usedUp ? { marginTop: 4 } : undefined}>
        {pass.today
          ? "Copresenting is free for the rest of today."
          : usedUp
            ? "You've used your free copresented sessions."
            : "A session is used only when someone joins."}
      </RailHint>
    </div>
  );
};

const RailHint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-muted);
`;

// ── Touch layout: the menu sheet ─────────────────────────────────────────
//
// One menu button, in the title row, opens a full-width sheet from the top.
// Its menu leads with the two sections a speaker uses on stage (Copresent and
// Q&A), then the timer, then everything they touch once (home, theme, chat, the
// account). A section opened from it has a way back to the menu at its top.
//
// This replaced a fixed tab bar across the top (Copresent · Q&A · menu). The
// bar cost a permanent 60px row on every screen to reach things a speaker
// opens a few times a talk, which on a phone was more than the slide could
// spare once it became the centrepiece.

// The touch layout's menu: a grid of large, colour-coded tiles with the label
// underneath, the way a phone's own control centre or home screen lays out
// destinations.
//
// It used to be a column of identical bordered buttons. Seven equal boxes read
// as seven equal choices and had to be read word by word; a colour and a shape
// are recognised before a word is, which is what a speaker glancing down
// mid-talk needs. Each tile carries a one-line status under its label ("2 in
// the room", "3 waiting", "On") so the menu answers the question that made
// someone open it before they tap. Three columns on a phone, more on a tablet.
const MobileMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
`;

// No box around each tile: the coloured square IS the target's visible edge,
// and a border per tile turned a light grid of icons into a wall of cards.
// Space does the separating instead.
const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  row-gap: 22px;
  column-gap: 8px;
  padding: 6px 0 4px;
`;

const MenuTile = styled.button`
  appearance: none;
  cursor: pointer;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 16px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
  transition: transform 80ms ease;

  &:active {
    transform: scale(0.94);
  }

  &:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 2px;
  }
`;

// The coloured square. Solid with a white glyph normally; for a switch that is
// OFF, a faint wash of the same colour with the glyph in it, so on and off read
// at a glance without a separate toggle control.
const MenuTileIcon = styled.span<{ $color: string; $off?: boolean }>`
  position: relative;
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: ${(p) => (p.$off ? `${p.$color}2b` : p.$color)};
  color: ${(p) => (p.$off ? p.$color : "#fff")};

  svg {
    width: 26px;
    height: 26px;
  }
`;

const MenuTileBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 22px;
  height: 22px;
  box-sizing: border-box;
  padding: 0 6px;
  border-radius: 11px;
  background: #ef4444;
  border: 2px solid var(--bg-fallback);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;

const MenuTileLabel = styled.span`
  max-width: 100%;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuTileCaption = styled.span<{ $tone?: "accent" | "warn" | "over" }>`
  max-width: 100%;
  margin-top: -4px;
  font-size: 12px;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) =>
    p.$tone === "accent"
      ? "var(--link)"
      : p.$tone === "over"
      ? "var(--danger, #FE4657)"
      : p.$tone === "warn"
      ? "var(--warning, #F29339)"
      : "var(--text-muted)"};
`;

// The account, under the grid behind one hairline. AccountBlock brings its own
// top margin and rule for the foot of the desktop rail; here they would draw
// the second line the old menu had above the account.
const MenuAccountCard = styled.div`
  margin-top: 22px;
  padding: 16px 4px 4px;
  border-top: 1px solid var(--border-soft);

  ${AccountBlock} {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

// One colour per destination, the same in both themes: the glyph is white on
// it, so it only has to hold contrast against white.
const MENU_COLORS = {
  room: "#6366f1",
  qa: "#10b981",
  timer: "#f59e0b",
  previews: "#a855f7",
  theme: "#64748b",
  home: "#0ea5e9",
  chat: "#ec4899",
} as const;

const SheetScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 46;
  background: rgba(0, 0, 0, 0.55);

  @media ${DESKTOP_QUERY} {
    display: none;
  }
`;

const Sheet = styled.aside<{ $open: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 47;
  max-height: 88dvh;
  display: ${(p) => (p.$open ? "flex" : "none")};
  flex-direction: column;
  box-sizing: border-box;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 16px 0;
  background: var(--bg-fallback);
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 18px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.45);
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: copresentSheetIn 0.18s ease-out;

  @keyframes copresentSheetIn {
    from {
      transform: translateY(-14px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

/**
 * The section half of the sheet, hidden while the menu is showing.
 *
 * `display: contents` when visible, so its children stay DIRECT flex children of
 * the sheet — the rail sections size themselves against that column, and an
 * ordinary wrapper would collapse them into one item.
 *
 * Which is also why hiding has to be spelled out: `display: contents` overrides
 * the `hidden` attribute's UA rule, so the attribute alone would leave the
 * sections on screen underneath the menu.
 */
const SectionPane = styled.div<{ $hidden: boolean }>`
  display: ${(p) => (p.$hidden ? "none" : "contents")};
`;

// The grab handle, and the close button. One target doing both jobs rather than
// a titled header row with an X — that header spent sixty pixels of a phone
// screen saying "MENU" to someone who had just tapped a labelled tab.
//
// At the BOTTOM edge, because the panel hangs from the top: a handle sits on
// the edge a sheet moves toward, and this one closes upward.
const SheetHandle = styled.button`
  appearance: none;
  cursor: pointer;
  flex: 0 0 auto;
  /* Sticky, not merely last: the panel scrolls, and a close control that
     scrolled away with the content would strand a speaker who had paged down
     through a long queue. */
  position: sticky;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  border: none;
  background: var(--bg-fallback);
  /* The bar is 4px; the padding is what makes the TARGET 44px tall. */
  padding: 14px 40px;
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: "";
    display: block;
    width: 42px;
    height: 4px;
    border-radius: 2px;
    background: var(--border-chip);
  }
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  background: rgba(0, 0, 0, 0.5);
  @media ${DESKTOP_QUERY} {
    display: none;
  }
`;

const DrawerPanel = styled.aside`
  width: 80%;
  max-width: 300px;
  height: 100%;
  box-sizing: border-box;
  background: var(--bg-card);
  border-right: 1px solid var(--border-soft);
  padding: 16px 16px;
  overflow-y: auto;
  animation: copresentDrawerIn 0.16s ease-out;

  @keyframes copresentDrawerIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;







// Back from a section to the menu that opened it.
const MenuBack = styled.button`
  appearance: none;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  margin: -12px 0 4px -8px;
  padding: 0 8px;
  border: none;
  background: none;
  color: var(--link);
  font-size: 14px;
  font-weight: 600;
`;



// Copresenter mobile header slot for the hamburger (top-right; the theme toggle
// moves to the left). The copresenter has no side rails, so the hamburger's
// only job is to open the menu drawer that houses live chat.
const MobileMenuSlot = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 2;
  @media ${DESKTOP_QUERY} {
    display: none;
  }
`;

// Shared route shell: validates the magicId, sets noindex, then mounts a
// ControllerSession in the requested mode. The copresenter (default) and host
// routes differ only by the `mode` passed through.
const ControllerRoute = ({ mode }: { mode: ControllerMode }) => {
  const { magicId, presentationId } = useParams();

  // Controller URLs are per-session join tokens, not landing pages — keep
  // them out of search indices so crawlers don't generate phantom joiners
  // on canonicalised junk paths.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Host controller is keyed to the DECK: /host/deck/:presentationId. It carries
  // the stable Google deck id and resolves the live session's magicId at load,
  // so it never goes stale when the magic link rotates between sessions.
  if (mode === "host") {
    if (!presentationId || !DECK_ID_PATTERN.test(presentationId)) {
      return <Navigate to="/" replace />;
    }
    return <ControllerSession deckId={presentationId} mode="host" />;
  }

  if (!magicId || !MAGIC_ID_PATTERN.test(magicId)) {
    return <Navigate to="/" replace />;
  }

  return <ControllerSession magicId={magicId} mode="copresenter" />;
};

const Controller = () => <ControllerRoute mode="copresenter" />;

export const HostController = () => <ControllerRoute mode="host" />;

const ControllerSession = ({
  magicId: magicIdProp,
  deckId,
  mode = "copresenter",
}: {
  // Copresenter route passes a magicId directly. Host route passes a deckId and
  // the session resolves its magicId from the backend (null until resolved).
  magicId?: string;
  deckId?: string;
  mode?: ControllerMode;
}) => {
  const isHostMode = mode === "host";
  const [searchParams, setSearchParams] = useSearchParams();
  // Host mode resolves the live session's magicId from the deck id; copresenter
  // mode gets it straight from the URL. `magicId` below is the single value the
  // rest of the component uses — null in host mode until resolution completes,
  // which just defers the WebSocket (socketUrl is null-guarded).
  const [resolvedMagicId, setResolvedMagicId] = useState<string | null>(null);
  // What the copresenter link carries. Deliberately NOT the magicId any more:
  // when the two were the same string, killing a leaked link meant killing the
  // session, which evicted everyone already in the room — so nobody ever did it.
  // Null until the resolve returns one; the link falls back to the magicId,
  // which still joins.
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  // "pending" until the first resolve returns; "ready" once we have a magicId;
  // "no_session" when the deck has no live session yet (host hasn't started);
  // "unauthenticated" when the stored host token is missing or expired.
  //
  // There is no "needs_host_link" any more. It meant "this deck is keyed and
  // your URL carried no key", which was a dead end polling could not fix and so
  // had a screen of its own. With the host key retired there is no key to be
  // missing — an older backend that still answers 403 is read as
  // "unauthenticated", which is both the honest description and the one
  // recovery that works against either backend.
  // "limit" when the resolve answered 402: a Free account that has used its
  // presentations for the month. Polling carries on behind the wall, so an
  // upgrade (or the 1st) lets the host straight back in.
  const [deckResolveStatus, setDeckResolveStatus] = useState<
    "pending" | "ready" | "no_session" | "unauthenticated" | "limit"
  >(isHostMode ? "pending" : "ready");
  // This month's Free presentation allowance, from the resolve and every host
  // JOIN reply. Null for Pro, or when the backend couldn't count.
  const [presentationAllowance, setPresentationAllowance] =
    useState<PresentationAllowance | null>(null);
  // Latest-ref for the watchdog interval, which closes over its first render.
  const presentationAllowanceBlockedRef = useRef(false);
  presentationAllowanceBlockedRef.current = presentationAllowance?.blocked === true;
  // A host with the upgrade dialog open, not yet Pro: they may be paying in
  // the Stripe tab right now. Assigned below, once isProNow exists; read by the
  // watchdog and the foreground check, which declare before it.
  const awaitingPaymentRef = useRef(false);
  // There is no anonymous host device limit any more.
  //
  // It capped how many keyed, account-less devices could drive one deck at
  // once — two, because the host key was a bearer token in a shareable URL and
  // without a bound on it, forwarding that URL was unlimited free copresenters.
  // Every host has an account now, and a signed-in device never counted against
  // the cap in the first place, so the thing it bounded cannot occur.
  // A copresenter link can be superseded while it's sitting in someone's chat:
  // the deck rotates (24h idle, extension reinstall) and the backend forwards
  // the join to the session that replaced it. When that happens the JOIN reply
  // comes back under a DIFFERENT magicId than we asked for, and since every
  // frame is filtered by magicId we'd drop our own reply and sit there waiting.
  // So we adopt the canonical id and use it from then on. Host mode already
  // follows session moves through resolvedMagicId, so this is copresenter-only.
  const [adoptedMagicId, setAdoptedMagicId] = useState<string | null>(null);
  // Set when the id change is a forwarding, not a real session switch, so the
  // session-reset effect below leaves the freshly-applied JOIN reply alone.
  const skipSessionResetRef = useRef(false);
  const magicId = isHostMode
    ? resolvedMagicId
    : adoptedMagicId ?? magicIdProp ?? null;
  const posthog = usePostHog();
  // [timing] Mark t=0 at first render so every subsequent phase logs a
  // delta from page-load. Survives re-renders via useRef.
  const t0Ref = useRef<number>(performance.now());
  const tlog = useCallback((phase: string, extra?: Record<string, unknown>) => {
    const t = Math.round(performance.now() - t0Ref.current);
    // eslint-disable-next-line no-console
    console.log(`[clicker t=${t}ms] ${phase}`, extra ?? "");
  }, []);
  // presenterId is loaded synchronously in the initializer so the WS URL
  // is available on the FIRST render — no need to wait for a useEffect →
  // setPresenterId → re-render → setSocketUrl chain. StrictMode double-
  // invokes the initializer in dev, but the localStorage read/write is
  // idempotent (read-then-write-if-missing) so the same id is returned
  // both passes; no duplicate UUIDs.
  // There is no host key any more.
  //
  // The extension used to mint one alongside the magic id and put it in the
  // controller URL's `#k=` fragment, so a QR-paired phone could drive a deck
  // with no account at all. That was the credential the anonymous controller
  // ran on, and the anonymous controller is gone — the account is now the only
  // way in, so a second bearer capability in a shareable URL bought nothing and
  // cost a whole authorisation path on both backends.
  //
  // A shipped extension still appends the fragment. It is simply ignored, on
  // this side and on the backend's, which is what lets the two roll out
  // independently rather than needing a Web Store release to land first.

  // Whether this browser holds a host session, read SYNCHRONOUSLY at first
  // render.
  //
  // The derived `hostSignedIn` further down is the one the UI renders on, but
  // it depends on `deckResolveStatus`, which depends on the resolve — so the
  // resolve cannot be gated on it without a cycle. This reads localStorage
  // directly instead: it is the cheap, always-available half of the question,
  // and the expensive half (is the token still valid) is exactly what the
  // resolve is for.
  //
  // A ref as well as state, because the resolve effect's interval closes over
  // its first render's values and would otherwise keep polling with a stale
  // answer after a sign-in.
  // No setter: signing in is a full-page redirect through Google, so the only
  // thing that ever changes this value is a fresh page load, which re-runs the
  // initializer. A setter here would be a second, slower source of truth for
  // something the browser already resets for us.
  //
  // A phone paired from the laptop's QR passes the gate too, with no account:
  // it holds a pairing for THIS deck (COP-17, see devicePairing.ts). Captured
  // off the URL fragment here, in the first initializer that runs, so the token
  // is out of the address bar before anything renders.
  const [pairedDevice] = useState<boolean>(() => {
    if (!isHostMode || !deckId) return false;
    captureDevicePairing(deckId);
    return isPairedDevice(deckId);
  });
  // The pairing stopped working (expired, or the backend refused it) — the
  // wall then says so, rather than asking a phone that never signed in to
  // "sign in to continue" as if it had.
  //
  // Also true from the first render when this phone's stored pairing ran out
  // while it was away — opened the next day, say.
  const [pairingExpired, setPairingExpired] = useState<boolean>(
    () =>
      isHostMode &&
      !pairedDevice &&
      !getStoredHostSession() &&
      hasExpiredDevicePairing(deckId)
  );
  const [hostSignedInGate] = useState<boolean>(
    () => isHostMode && (!!getStoredHostSession() || pairedDevice)
  );
  const pendingEmailSlide = useRef<{ slide: number; at: number } | null>(null);
  const hostSignedInRef = useRef(hostSignedInGate);
  hostSignedInRef.current = hostSignedInGate;

  const [presenterId] = useState<string>(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("presenterId") : null;
    if (stored) return stored;
    const next = uuidv4();
    if (typeof window !== "undefined") localStorage.setItem("presenterId", next);
    return next;
  });

  // Common wrapper for lightweight UI telemetry — every clicker/host control
  // event carries the session's mode (host | copresenter), magic id and
  // presenter id. presenter_id + mode are also registered as super-properties
  // (see the identify effect) so they ride every event, not just these.
  const captureUi = (event: string, props?: Record<string, unknown>) => {
    posthog.capture(event, {
      mode,
      magic_id: magicId,
      presenter_id: presenterId,
      ...props,
    });
  };

  const [presentation, setPresentation] = useState<Presentation>();
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  // The slide shown, reconciled against the server's versioned slide and our
  // own unanswered taps. Every write to currentSlide goes through
  // commitSlideSync — see ../slideSync for why a raw setCurrentSlide from a
  // server frame put the phone a slide behind the deck.
  const slideSyncRef = useRef<SlideSyncState>(initialSlideSync);
  const moveCounterRef = useRef(0);
  const commitSlideSync = useCallback((next: SlideSyncState) => {
    slideSyncRef.current = next;
    setCurrentSlide(next.shown);
  }, []);
  // Slides API preview URL for the current slide, rendered server-side and
  // pushed by the backend. Empty until the first image arrives (or if the
  // host isn't signed in, in which case no preview is shown).
  const [currentSlideImage, setCurrentSlideImage] = useState<string>("");
  // Pre-rendered URLs for every slide, indexed 1-based. Populated by
  // streaming PRELOAD_SLIDE_IMAGE messages (and a snapshot in DETAILS_UPDATED
  // for late joiners). We warm the browser cache for each URL the moment it
  // arrives so the eventual <img src> swap on slide change is a cache hit and
  // the new slide appears instantly with no network round-trip.
  //
  // State (not ref) so the component re-renders when a preload lands — that
  // way slide N's preview will switch from the UPDATE_SLIDE_IMAGE URL to the
  // already-cached URL as soon as it arrives, with no race against navigation.
  const [slideImages, setSlideImages] = useState<Map<number, string>>(
    () => new Map()
  );
  // Click-step drawings of animated slides (1-based → step 0 first) and the
  // step the deck last reported, so previews follow the animations (see
  // slideSteps.ts). Empty for a deck without them, or an older extension.
  const [stepImages, setStepImages] = useState<Map<number, string[]>>(() => new Map());
  const [liveStep, setLiveStep] = useState<LiveStep | null>(null);
  // Which way we arrived at the current slide: Google shows a slide you go
  // back to with its animations played. Updated during render so the first
  // frame on the new slide already knows.
  const arrivalRef = useRef({ slide: currentSlide, back: false });
  if (arrivalRef.current.slide !== currentSlide) {
    arrivalRef.current = { slide: currentSlide, back: currentSlide < arrivalRef.current.slide };
  }
  const previewSources: PreviewSources = {
    slideImages,
    stepImages,
    live: liveStep,
    cameBack: arrivalRef.current.back,
  };
  // Tracks the URL that most recently failed to load in the preview <img>.
  // When the active src equals this value, we render the fallback instead of
  // the broken image. Cleared automatically once a new src loads successfully
  // (e.g. host navigates to a slide whose pre-rendered URL is still fresh, or
  // the backend re-issues a URL after the Slides API 30-min contentUrl TTL
  // expires).
  const [failedPreviewSrc, setFailedPreviewSrc] = useState<string | null>(null);
  // Thumbnail URLs that failed to load, kept apart from failedPreviewSrc above —
  // see renderNavThumb for why sharing that single slot would loop.
  const [failedThumbs, setFailedThumbs] = useState<Set<string>>(() => new Set());

  // `force` bypasses the mobile skip, for the handful of slides the nav
  // buttons' thumbnails actually show.
  const warmBrowserCache = useCallback((url: string, force = false) => {
    if (!url) return;
    // Skip the BULK warm on mobile. The nav thumbnails mean phones now render
    // previews after all, but only two slides at a time — pulling the whole
    // deck (~22MB) down a phone connection to show 2 images of it is still the
    // wrong trade, so mobile warms just around the current slide (see the
    // effect below) and everything else loads on demand. Inline matchMedia
    // keeps this independent of the isMobileLayout React state declared below.
    if (
      !force &&
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches
    ) {
      return;
    }
    // `new Image().src = url` is the standard browser-cache pre-warm trick.
    // The fetched bytes land in the HTTP cache; later <img src> for the same
    // URL hits cache instead of reissuing a network request.
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = url;
  }, []);
  const [status, setStatus] = useState(PresentationStatus.CONNECTING);
  // Bumped to force an immediate reconnect (see the visibility handler). It
  // rides on the socket URL as a query param, which react-use-websocket treats
  // as a new connection: it drops the pending backoff timer and dials now.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  // Socket errors since the last successful open. Only the first of a run is
  // reported — a phone left offline keeps retrying, one error per attempt.
  const wsErrorsSinceOpenRef = useRef(0);
  const [name, setName] = useState<string | null>(null);

  // --- Host controller state (only meaningful when mode === "host") ---------
  // Whether the backend verified this client as the host (from the JOIN reply's
  // youAreHost). Until confirmed, the host page shows a "verifying…" state.
  const [youAreHost, setYouAreHost] = useState(false);
  // Live copresenter roster, pushed via CO_PRESENTERS_CHANGED to host sockets.
  const [coPresenters, setCoPresenters] = useState<Presenter[]>([]);
  // Whether the host has locked slide control. Drives the host's lock toggle
  // and (on copresenter clients) the view-only state.
  const [controlLocked, setControlLocked] = useState(false);
  // Whether the host has paused THIS client specifically (Pro). Separate from
  // controlLocked, which is the whole-room lock — a copresenter can be under
  // either, or both, and they're lifted independently.
  const [selfPaused, setSelfPaused] = useState(false);
  // The host removed this copresenter. While true the socket URL is null, so
  // react-use-websocket closes it and every reconnect path (backoff, watchdog,
  // the foreground revalidate) has nothing to dial — otherwise the clicker's
  // retry-forever design would put them straight back in the room. The ref
  // mirrors it for socket callbacks, which can close over a stale render.
  const [removedByHost, setRemovedByHost] = useState(false);
  const removedByHostRef = useRef(false);
  // The copresenter seat limit, from the host's JOIN reply. null means
  // unlimited (Pro) — which is why "no limit" and "not told yet" have to be
  // different values, hence undefined for the latter. The seats-USED count
  // isn't held here: it's derived from the rendered roster (see
  // onlineCoPresenterCount) so the counter can't disagree with the list.
  const [seatLimit, setSeatLimit] = useState<number | null | undefined>(undefined);
  // The free copresent pass (COP-33), from the host's JOIN reply. Display only:
  // seatLimit above is what the backend actually enforces.
  const [freeCopresent, setFreeCopresent] = useState<FreeCopresent | null>(null);
  // Whether this session's host is on Pro, from the host's JOIN reply. undefined
  // until the backend says — so we never flash a paywall at a paying host on a
  // slow connection, or at an older backend that doesn't send the flag.
  const [isPro, setIsPro] = useState<boolean | undefined>(undefined);
  // WHY they're Pro. Analytics only — every gate in this file keys off isPro.
  const [planSource, setPlanSource] = useState<"none" | "paid" | undefined>(
    undefined
  );
  // Whether the ROOM is on Pro, as opposed to whether THIS person is. The only
  // plan fact a copresenter is given, and what gates their timer — isPro is
  // host-only and stays that way, so without this a copresenter had nothing to
  // gate on at all. Undefined until the backend says, exactly like isPro: a
  // copresenter in a Pro room briefly seeing no timer is fine; one being shown
  // a locked timer they've paid for is not.
  const [roomPro, setRoomPro] = useState<boolean | undefined>(undefined);
  // Someone the free plan turned away, from JOIN_BLOCKED. Drives the host's
  // upgrade modal — the strongest pitch we get, because there's a named person
  // waiting at the door rather than an abstract limit.
  const [blockedJoiner, setBlockedJoiner] = useState<string | null>(null);
  // Whether the Pro offer dialog is open.
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // A paired phone tapped something that needs the account (upgrade, live
  // previews). It says where to do it instead of opening a sign-in.
  const [accountOnlyOpen, setAccountOnlyOpen] = useState(false);
  // Which paywall opened the offer — goes to Stripe as the checkout source.
  const [upgradeSource, setUpgradeSource] = useState("controller");
  // The payment-received confirmation, raised by ?upgraded=1 when the Stripe
  // tab bounces back here (see ../upgradeReturn).
  const [paidOpen, setPaidOpen] = useState(false);
  // Live-preview status for the host's deck: true once a preview renders or the
  // backend confirms it can read the deck; false when it can't (drive.file not
  // granted). null = unknown, so we don't nag before we know.
  const [previewOn, setPreviewOn] = useState<boolean | null>(null);
  // The extension is drawing the deck's previews itself (COP-19), so they're
  // coming with no grant: show a loading state in place of "Enable live
  // previews". Cleared by the first image (previewOn), a failed run, or the
  // timeout below.
  const [previewLoading, setPreviewLoading] = useState(false);
  // A run that dies without reporting (tab closed, extension reloaded) would
  // otherwise spin forever; after this long the enable button comes back.
  useEffect(() => {
    if (!previewLoading || previewOn === true) return;
    const t = window.setTimeout(() => setPreviewLoading(false), PREVIEW_LOADING_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [previewLoading, previewOn]);
  // Host layout: whether the mobile roster drawer is open.
  const [rosterOpen, setRosterOpen] = useState(false);
  // The phone's hamburger. Two of them — the host shell's and the copresenter
  // page's — so the source says which, since on a phone this drawer is the ONLY
  // way to the roster, invite link and account, and a drawer nobody opens hides
  // every feature behind it. Closing isn't tracked: it carries no intent.
  const openMenu = (source: string) => {
    captureUi("menu_opened", { source });
    setRosterOpen(true);
  };
  // Which rail section the host is looking at.
  //
  // Deliberately NOT persisted, unlike the collapse states above. Those record a
  // preference about chrome; this records where someone was mid-talk, and a
  // controller that reopened on Q&A days later would hide the roster from a host
  // who is only trying to see who joined. Room is the safe landing every time.
  const [railSection, setRailSection] = useState<RailSectionId>("room");
  /**
   * Phones only: whether the sheet is showing a rail section or the hamburger's
   * menu.
   *
   * A separate flag rather than a fourth RailSectionId, because it is not one —
   * the desktop strip has no such entry, and widening that union would put an
   * account menu in the rail's switcher on every laptop.
   *
   * Both views share the ONE sheet, so switching between them cannot leave two
   * open or make the tab bar disagree with what is on screen.
   */
  const [sheetView, setSheetView] = useState<"section" | "menu">("section");
  // Questions still waiting, for the badge on the Q&A tab. Reported by QaPanel,
  // which keeps streaming while its section is hidden — see QaPane.
  const [qaRoom, setQaRoom] = useState<QaRoomSummary | null>(null);
  const qaWaiting = qaRoom?.open ? qaRoom.waiting : 0;

  // Desktop: whether the host has collapsed the left roster rail. Persisted so
  // the choice sticks across reloads.
  const [rosterCollapsed, setRosterCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(ROSTER_COLLAPSED_KEY) === "true";
  });
  const toggleRosterCollapsed = () => {
    captureUi("sidebar_toggled", { visible: rosterCollapsed });
    setRosterCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(ROSTER_COLLAPSED_KEY, String(next));
      return next;
    });
  };
  // Desktop: whether the host has collapsed the right notes rail. Persisted.
  const [notesCollapsed, setNotesCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(NOTES_COLLAPSED_KEY) === "true";
  });
  const toggleNotesCollapsed = () => {
    captureUi("notes_toggled", { visible: notesCollapsed, source: "rail" });
    setNotesCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(NOTES_COLLAPSED_KEY, String(next));
      return next;
    });
  };
  // Safety valve for the "resolving host" spinner: if a stored session never
  // gets us verified as host (wrong account, deck never registered), fall back
  // to the sign-in card instead of spinning forever.
  const [hostAuthTimedOut, setHostAuthTimedOut] = useState(false);
  // Name modal (themed replacement for window.prompt). Shown only when the WS
  // opens and we have no cached name. `pendingJoinRef` records that the JOIN
  // was deferred so submitName() knows to fire it once the user confirms.
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const pendingJoinRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const [fontScale, setFontScale] = useState<number>(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(FONT_SCALE_KEY) : null;
    const parsed = stored ? parseFloat(stored) : NaN;
    return Number.isFinite(parsed) ? Math.min(MAX_FONT_SCALE, Math.max(MIN_FONT_SCALE, parsed)) : 1;
  });
  // User preference: hide notes even when the deck has them. Persisted globally
  // (not per-presentation) — if you set "hide", you probably want it everywhere.
  // The toggle button is only rendered when the deck actually has notes.
  const [notesVisible, setNotesVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(NOTES_VISIBLE_KEY);
    return stored === null ? true : stored !== "false";
  });
  // Notes until chosen otherwise: a deck WITH notes has been showing them on a
  // phone all along, and notes decks are where hosts lean on the phone most.
  const [mobileView, setMobileView] = useState<"slide" | "notes">(() =>
    typeof window !== "undefined" &&
    window.localStorage.getItem(MOBILE_VIEW_KEY) === "slide"
      ? "slide"
      : "notes"
  );
  // Stage view (see Stage). `stageRequested` is the expand button.
  // `stageDismissed` is the exit button on a phone held sideways, which would
  // otherwise reopen the stage on the very next render.
  const [stageRequested, setStageRequested] = useState(false);
  const [stageDismissed, setStageDismissed] = useState(false);
  // Sibling of notesVisible, but three-state rather than boolean: null means the
  // user has never touched the toggle, and the DEFAULT then depends on the plan
  // (see previewVisible below). A plain boolean couldn't express that — the
  // stored value is read here, at mount, long before the JOIN reply says whether
  // this host is Pro.
  const [storedPreviewPref] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(PREVIEW_VISIBLE_KEY);
    return stored === null ? null : stored !== "false";
  });
  // A choice made in THIS session, which always wins. Separate from the stored
  // one because the stored one is only trusted for a host who can actually use
  // previews — see previewVisible.
  const [previewChoice, setPreviewChoice] = useState<boolean | null>(null);
  // The touch layout's Preview switch. On unless this device turned it off.
  const [mobilePreviewOn, setMobilePreviewOn] = useState<boolean>(
    () =>
      typeof window === "undefined" ||
      window.localStorage.getItem(MOBILE_PREVIEW_VISIBLE_KEY) !== "false"
  );
  // Theme preference. Defaults to the OS-level `prefers-color-scheme` so a
  // dark-mode-loving user gets it without having to click; once they flip
  // the toggle the explicit choice in localStorage wins over the OS pref.
  const [theme, setTheme] = useState<"light" | "dark">(readStoredTheme);

  // Featurebase live chat: native floating bubble on desktop, opened from the
  // mobile menu below. Mounted here so it lives only on the controller pages
  // (host + copresenter), not the dashboard. Theme-synced to the toggle above.
  // `chatEnabled` is the PostHog kill switch — when off, the widget never boots
  // and we drop its menu entries too.
  const { openMessenger, enabled: chatEnabled } = useFeaturebaseMessenger({
    theme,
    disabled: pairedDevice,
  });

  // Reaching for support is a friction signal — someone stuck enough to type at
  // a human — so the menu entries that open the messenger get an event. Only the
  // two menu entries route through here; the desktop floating bubble is
  // Featurebase's own and we never see that click.
  const openLiveChat = (source: string) => {
    captureUi("live_chat_opened", { source });
    setRosterOpen(false);
    openMessenger();
  };

  // Computed every render but the string is byte-identical on subsequent
  // renders (presenterId is stable from useState, magicId is stable from
  // useParams), so react-use-websocket sees the same URL and does NOT
  // disconnect/reconnect. The WS starts on first render — no extra render
  // cycle waiting for a setState chain.
  // Null in host mode until there is an account, for the same reason the
  // resolve is: an open socket is a seat on the session, and a visitor behind
  // the wall must not have one. In practice magicId is already null there
  // (nothing resolved it), so this is belt and braces — but the belt is what
  // keeps a future change to the resolve from silently reopening the socket.
  const socketUrl =
    magicId && !removedByHost && (!isHostMode || hostSignedInGate)
      ? `${process.env.REACT_APP_WEB_SOCKET_URL}?presenterId=${presenterId}&magicId=${magicId}`
      : null;

  // Host mode: keep the controller pointed at the deck's CURRENT session.
  //
  // It resolves the deck id to a magicId at load, but the canonical session can
  // MOVE afterwards — the extension may register a fresh magicId (rotation /
  // reinstall), and START promotes a session to live that isn't necessarily the
  // one the controller first landed on. So we re-resolve on a tick until we're
  // ONLINE and follow the magicId if it changed (which reconnects the socket to
  // the live session and flips the controller to connected). Without this the
  // controller stays pinned to its first session and only a manual browser
  // refresh would pick up the switch — exactly the "host stays waiting until I
  // refresh" bug. Once ONLINE the session is stable, so we stop (no socket churn).
  // Copresenter mode has its magicId from the URL, so this is a no-op there.
  useEffect(() => {
    if (!isHostMode || !deckId) return;
    // Nothing is resolved before there is an account. This is the enforcement
    // half of the sign-in wall: the wall itself is a blurred skeleton, and it
    // is only honest scenery because no request has been made — a resolve here
    // would hand an unauthenticated visitor holding a deck id the session's
    // magic id and its invite token, which is the capability to join the room.
    if (!hostSignedInRef.current) return;
    if (status === PresentationStatus.ONLINE) return;
    let cancelled = false;
    const attempt = async () => {
      const result = await resolveHostSession(deckId, hostBearerFor(deckId));
      if (cancelled) return;
      if (typeof result === "object" && "limit" in result) {
        setPresentationAllowance(result.limit);
        setDeckResolveStatus("limit");
      } else if (typeof result === "object") {
        if (result.presentationAllowance !== undefined) {
          setPresentationAllowance(result.presentationAllowance ?? null);
        }
        setResolvedMagicId((prev) =>
          prev !== result.magicId ? result.magicId : prev
        );
        // Seed the token once, on the first resolve that carries one; never let a
        // later poll overwrite it. Rotation is the ONLY thing that legitimately
        // changes the token, it's driven locally (newInviteLink → setInviteToken),
        // and it's newer than any in-flight poll's answer. `prev ?? …` is what
        // makes the comment true: a `prev !== result` swap would still let a stale
        // poll clobber a just-rotated token back to the revoked one on screen.
        if (result.inviteToken) {
          setInviteToken((prev) => prev ?? result.inviteToken!);
        }
        setDeckResolveStatus("ready");
      } else if (result === "unauthenticated") {
        if (pairedDevice) {
          clearDevicePairing(deckId);
          setPairingExpired(true);
        }
        setDeckResolveStatus("unauthenticated");
      } else if (result === "needs_host_link") {
        // Only an older backend still answers this — the key check is gone
        // there too. Treated as "not signed in", which is the one recovery that
        // now exists and, on a backend that still enforces a key, is also the
        // one that works: the account match was always accepted alongside it.
        setDeckResolveStatus("unauthenticated");
      } else if (result === "unavailable") {
        // Transient (backend blip / deploy): the answer is unknown. Do NOTHING —
        // keep the current resolved session and retry next tick. Acting on this
        // would tear down a working socket during a hiccup.
      } else if (!resolvedMagicId) {
        // Authoritative no_session, and we've never resolved one: show "not
        // running". Once we HAVE a session, a lone no_session doesn't flash idle
        // or drop the socket (a real stop arrives as a NOT_STARTED broadcast).
        setDeckResolveStatus("no_session");
      }
    };
    void attempt();
    const id = window.setInterval(attempt, WATCHDOG_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isHostMode, deckId, status, resolvedMagicId, hostSignedInGate, pairedDevice]);

  // Session-switch hygiene: when the effective magicId changes (the host
  // controller followed a rotated / reassigned session), EVERY piece of
  // per-session state must reset. Otherwise the UI carries the previous session's
  // identity and content into the new one — most dangerously staying "verified
  // host" (`youAreHost`) for a deck the new session's owner is someone else, but
  // also showing the old deck's slides, roster, and lock state. youAreHost /
  // previewOn keep their deliberate only-flip-true semantics against broadcasts;
  // resetting here (then letting the NEW session's JOIN reply re-establish them)
  // is what makes a session switch re-verify from scratch. No-op on the very
  // first resolve (state is already default).
  //
  // EXCEPT when the id changed because we were forwarded off a superseded link.
  // That's the same session under its canonical name, and the JOIN reply that
  // told us about it was applied in the same handler — so resetting here would
  // immediately wipe the state we just received and blank the UI until the next
  // periodic re-JOIN.
  useEffect(() => {
    if (skipSessionResetRef.current) {
      skipSessionResetRef.current = false;
      return;
    }
    setYouAreHost(false);
    setHostAuthTimedOut(false);
    setPreviewOn(null);
    setPreviewLoading(false);
    setControlLocked(false);
    // A pause belongs to the session it was issued in. A rotated link is a new
    // room, so carrying it over would silence someone the host never paused
    // there — with no control in the new room to lift it.
    setSelfPaused(false);
    setCoPresenters([]);
    // Cleared outright, not left to the debounced clear: a rotated session must
    // not inherit the previous room's turned-away notice, even for the few
    // seconds that clear would otherwise take.
    setBlockedJoiner(null);
    setSlideImages(new Map());
    setStepImages(new Map());
    setLiveStep(null);
    setCurrentSlideImage("");
    setFailedPreviewSrc(null);
    commitSlideSync(initialSlideSync);
    setPresentation(undefined);
  }, [magicId, commitSlideSync]);

  // presenter_id and role ride every event (incl. autocapture) as properties, so
  // telemetry can be segmented by who and by host vs copresenter.
  //
  // Deliberately NOT identify(presenterId) any more. presenterId is a
  // per-browser UUID, so identifying on it did two things wrong at once. It
  // burned the one identify a person gets while they were still anonymous —
  // leaving nothing to merge their pre-sign-in history INTO when they later
  // signed in, because PostHog won't merge one identified person into another.
  // And it forked a single human into two people the moment they scanned the
  // QR onto a phone, which is exactly the anonymous → signed-in step this is
  // supposed to measure.
  //
  // Now PostHog keeps its own anonymous id until there's a real account to
  // claim it (see the identify effect below), which is what makes the journey
  // one person from first anonymous session through to payment.
  // The top of the funnel, and the number this whole change lives or dies by.
  //
  // Every other event here reports something someone chose to do. This one
  // reports the moment the product asked for something before it would work,
  // which is the denominator for "how many hosts bounced at the wall" — and
  // there is no way to reconstruct it afterwards, because a host who bounces
  // leaves no other trace at all.
  //
  // `has_prior_session` separates a first-timer from a returner whose token
  // expired; `is_small_viewport` separates the QR-paired phone, which is the
  // likeliest place to lose someone — typing a Google password minutes before
  // speaking is a different ask from clicking through on a laptop.
  //
  // Fires once per mount, not once per render, and only in host mode.
  const wallReported = useRef(false);
  useEffect(() => {
    if (!isHostMode || hostSignedInGate || wallReported.current) return;
    wallReported.current = true;
    captureUi("host_auth_wall_shown", {
      deck_id: deckId ?? null,
      // Both read inline rather than through the derived values further down —
      // this effect runs above their declarations, and both are cheap
      // synchronous reads that cannot change between here and there.
      has_prior_session: !!getStoredHostSession(),
      is_small_viewport:
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 767px)").matches,
    });
    // captureUi is rebuilt every render, so it is deliberately not a dependency
    // — the ref is what makes this once-only, and listing it would re-run the
    // effect on every render for no gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHostMode, hostSignedInGate, deckId]);

  useEffect(() => {
    posthog.register({ presenter_id: presenterId, mode });
    tlog("presenterId resolved");
  }, [posthog, presenterId, mode, tlog]);

  // Claim the anonymous history for the Google account, the moment there is one.
  //
  // The sub is the join key to everything outside PostHog — Firestore's
  // users/{sub} and the Stripe webhook's client_reference_id both key on it —
  // so identifying on anything else leaves "did they ever pay?" unanswerable.
  // The backend's own events use the same distinct_id, so a server-side
  // pro_activated lands on this same person.
  //
  // Idempotent: posthog-js no-ops an identify with an unchanged distinct_id, so
  // running this on every render pass costs nothing.
  const identitySub = getStoredHostSub();
  const identityEmail = getStoredHostSession()?.profile?.email;
  useEffect(() => {
    if (!identitySub) return;
    posthog.identify(identitySub, { email: identityEmail, sub: identitySub });
  }, [posthog, identitySub, identityEmail]);

  // Plan state on every event, so any of them can be segmented — or made a
  // funnel step — by what the person was on AT THE TIME. Without this you can
  // see that someone clicked Upgrade but not what plan they were on.
  useEffect(() => {
    if (isPro === undefined) return; // not told yet — don't assert a plan
    posthog.register({ is_pro: isPro, plan_source: planSource ?? "none" });
  }, [posthog, isPro, planSource]);

  // room_pro separately, because is_pro above never fires for a copresenter —
  // isPro is host-only, so it stays undefined and the effect returns early.
  // That left every copresenter event with no entitlement property at all,
  // which makes "did copresenters use the timer" unanswerable: the ones who
  // COULD (a Pro room) are indistinguishable from the ones who were never shown
  // it. This is the denominator.
  useEffect(() => {
    if (roomPro === undefined) return;
    posthog.register({ room_pro: roomPro });
  }, [posthog, roomPro]);

  const heartbeatRef = useRef<number | null>(null);

  // Timestamp of the last frame the backend sent us (any action). The liveness
  // watchdog uses it to tell "idle but alive" from "silently dead".
  const lastServerMessageRef = useRef<number>(Date.now());

  // Autofocus the name field once the modal mounts. Deferred a tick so the
  // focus lands after the open animation's first frame (focusing mid-layout
  // can be dropped by some browsers).
  useEffect(() => {
    if (!showNameModal) return;
    const id = window.setTimeout(() => nameInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [showNameModal]);

  // Builds and sends the JOIN message once a display name is known. Extracted
  // so it can fire either immediately (name already cached) or after the user
  // confirms the name modal (first join from this browser).
  const sendJoin = (
    displayName: string,
    hostCreds?: {
      googleAccessToken?: string;
      sessionToken?: string;
      deviceToken?: string;
      asHost?: boolean;
      timeZone?: string;
    }
  ) => {
    if (!magicId) return;
    const message: JoinPresentation = {
      action: ActionType.JOIN_PRESENTATION,
      magicId: magicId,
      presenterId: presenterId,
      name: displayName,
      ...hostCreds,
    };

    sendJsonMessage(message);
    tlog("JOIN sent");
    posthog.capture("presentation_joined", {
      magic_id: magicId,
      presenter_name: displayName,
      mode,
    });

    heartbeat();
  };

  // Host-mode JOIN. Reuses the extension's existing Google sign-in (Path A): we
  // ask the extension for a short-lived Google access token and send it so the
  // backend can verify us as the host. No name modal — the host already has an
  // identity; we use any cached name or fall back to "Host". When the token is
  // unavailable (extension absent / not signed in / different device), we still
  // JOIN — the backend just treats us as a copresenter and youAreHost stays
  // false, which the UI surfaces. (Path B web sign-in is added separately.)
  const sendHostJoin = () => {
    const displayName =
      name ||
      (typeof window !== "undefined"
        ? window.localStorage.getItem("presenterName")
        : null) ||
      "Host";
    if (!name) setName(displayName);

    // One credential: the signed-in account. It proves who this is, claims the
    // deck, and is what attaches a plan to the session.
    //
    // The host key that used to ride alongside it is retired. It existed so a
    // scanned phone with no account could drive the deck, and the controller no
    // longer opens without an account — see the note where the key state used
    // to live.
    //
    // A phone paired from the QR has no account; it sends its pairing instead,
    // which the backend honours only on a session for this deck.
    const session = getStoredHostSession();
    const pairing = session ? null : getDevicePairing(deckId);
    sendJoin(displayName, {
      // Still sent: this JOIN came from the host controller route, and it is
      // what tells the backend apart from an anonymous copresenter — who must
      // NOT be seated as a host.
      asHost: true,
      ...(session ? { sessionToken: session.sessionToken } : {}),
      ...(pairing ? { deviceToken: pairing } : {}),
      // Fixes the local day and month the Free allowance counts in.
      timeZone: browserTimeZone(),
    });
  };

  // Latest-ref so the watchdog effect can re-trigger a host JOIN without listing
  // the (every-render-new) sendHostJoin function in its deps.
  const sendHostJoinRef = useRef(sendHostJoin);
  sendHostJoinRef.current = sendHostJoin;

  // WS onOpen. If we already know the presenter's name (React state or a
  // previous visit cached it in localStorage) we JOIN right away; otherwise we
  // defer the JOIN and open a themed name modal, sending the JOIN the moment
  // they submit. Replaces the old blocking window.prompt().
  const joinPresentation = (event: WebSocketEventMap["open"]) => {
    tlog("ws onOpen");
    if (!magicId) return;

    // Host controller: skip the name modal and authenticate via the extension.
    if (isHostMode) {
      void sendHostJoin();
      return;
    }

    const cached =
      name ||
      (typeof window !== "undefined"
        ? window.localStorage.getItem("presenterName")
        : null);

    if (cached) {
      // Keep state in sync so a later reconnect skips the lookup entirely.
      if (!name) setName(cached);
      sendJoin(cached);
      return;
    }

    // First join on this browser — ask for a name before sending JOIN.
    pendingJoinRef.current = true;
    setShowNameModal(true);
  };

  // Confirms the name from the modal: persist it, close the modal, and fire
  // the JOIN that onOpen deferred. No-op on empty input (the submit button is
  // also disabled in that state, this just guards the Enter-key path).
  const submitName = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    if (typeof window !== "undefined") {
      window.localStorage.setItem("presenterName", trimmed);
    }
    setName(trimmed);
    setShowNameModal(false);
    if (pendingJoinRef.current) {
      pendingJoinRef.current = false;
      sendJoin(trimmed);
    }
  };

  const messageReceived = (event: WebSocketEventMap["message"]) => {
    // Any frame from the backend counts as proof of life for the watchdog.
    lastServerMessageRef.current = Date.now();
    const message: Message = JSON.parse(event.data);
    if (message.action === ActionType.DETAILS_UPDATED) {
      tlog("DETAILS_UPDATED rx", { bytes: event.data.length });
      const detailsMessage: Presentation = JSON.parse(event.data);

      // The backend forwarded us: the reply carries the id we ASKED for in
      // forwardedFrom, which is the only reason we can tell it apart from a frame
      // for some other session. Adopt the canonical id so ongoing broadcasts
      // (labelled with it) aren't filtered out.
      //
      // Two reasons the id can differ, handled differently in the address bar:
      //
      //   - Our link was SUPERSEDED (tokenResolved false): the id we hold is
      //     dead. Rewrite the URL to the live session id, so a copresenter who
      //     re-copies the URL out of their browser shares a working link.
      //   - We presented an INVITE TOKEN (tokenResolved true): the token is
      //     alive and is the revocable link. KEEP it in the address bar — never
      //     rewrite to the session id, which is the durable, non-revocable id
      //     shared by everyone. Rewriting would let a re-copied URL bypass a
      //     later rotation (the magicId still joins via the legacy fallback),
      //     defeating the entire point of the token.
      //
      // Copresenter mode ONLY. The host controller is keyed to the deck, not the
      // link: its URL is /host/deck/{deckId} and it follows session moves through
      // resolvedMagicId already. Rewriting there would replace the host's URL with
      // a copresenter one — reload and they'd lose the rail entirely — and since
      // host mode ignores adoptedMagicId, magicId wouldn't change, so the
      // skip-reset flag below would never be consumed and would go on to swallow
      // the next genuine session switch (carrying `youAreHost` into a deck owned
      // by someone else — the exact hazard the reset exists to prevent).
      if (
        !isHostMode &&
        detailsMessage?.forwardedFrom &&
        detailsMessage.forwardedFrom === magicId &&
        detailsMessage.magicId
      ) {
        tlog("magicId forwarded", {
          from: detailsMessage.forwardedFrom,
          to: detailsMessage.magicId,
          tokenResolved: detailsMessage.tokenResolved,
        });
        skipSessionResetRef.current = true;
        setAdoptedMagicId(detailsMessage.magicId);
        // Supersession rewrites the URL to the live id; a token trade keeps the
        // token in the address bar (see above).
        if (!detailsMessage.tokenResolved) {
          window.history.replaceState(
            null,
            "",
            `/presentation/${detailsMessage.magicId}${window.location.search}`
          );
        }
      }

      if (
        detailsMessage &&
        (detailsMessage.magicId === magicId ||
          detailsMessage.forwardedFrom === magicId)
      ) {
        // Host-controller join refused: the backend no longer seats an
        // unverified host controller as a copresenter (it used to, which put a
        // ghost named "Host" on the roster, holding a free seat). We are NOT in
        // the room. Show the wall and process nothing else — the refusal reply
        // carries no roster, plan or preview state worth reading.
        //
        // Refusal now means one thing: the session token was rejected. So it
        // maps to "unauthenticated", which puts the wall up rather than the old
        // go-find-your-link screen. Recoverable without a refresh — the resolve
        // poll keeps running, and signing in flips it back to a real join.
        if (isHostMode && detailsMessage.hostRefused) {
          tlog("host join refused — showing the sign-in wall");
          // A paired phone refused mid-session: its pairing ran out (or was
          // never good). Say so — the resolve poll that would otherwise notice
          // is not running while the socket is up.
          if (pairedDevice) {
            clearDevicePairing(deckId);
            setPairingExpired(true);
          }
          setDeckResolveStatus("unauthenticated");
          return;
        }
        // Sliding renewal: every host JOIN on a pairing returns a fresh one.
        if (isHostMode && pairedDevice && detailsMessage.deviceToken) {
          storeDevicePairing(deckId, detailsMessage.deviceToken);
        }
        // The anonymous-host-device refusal was handled here too, with a screen
        // of its own. An older backend can still send the flag; it is ignored,
        // because a signed-in device never counted against that cap and every
        // host is signed in now.
        setPresentation(detailsMessage);
        // Host controller: the JOIN reply tells us whether we were recognised as
        // the host, and the current lock state. Only flip youAreHost true here
        // (a later broadcast DETAILS_UPDATED leaves it undefined and mustn't
        // clear a confirmed host).
        if (detailsMessage.youAreHost) setYouAreHost(true);
        // Roster seeded on the host's JOIN reply — surfaces copresenters who
        // were already in the room before the controller opened (later joiners
        // arrive via CO_PRESENTERS_CHANGED). Present only on host JOIN replies.
        if (detailsMessage.coPresenters) {
          setCoPresenters(detailsMessage.coPresenters);
        }
        if (typeof detailsMessage.controlLocked === "boolean") {
          setControlLocked(detailsMessage.controlLocked);
        }
        // Seat info rides on host JOIN replies only, and seatsInUse is the
        // marker for one — seatLimit can't be, because its null (= unlimited,
        // Pro) is meaningful and indistinguishable from "absent" on a
        // copresenter reply.
        if (detailsMessage.seatsInUse != null) {
          setSeatLimit(detailsMessage.seatLimit ?? null);
          // Same marker, same reason: the allowance rides on host JOIN replies
          // only, and its null (Pro, or uncountable) is meaningful.
          if (detailsMessage.presentationAllowance !== undefined) {
            setPresentationAllowance(detailsMessage.presentationAllowance ?? null);
          }
          setFreeCopresent(detailsMessage.freeCopresent ?? null);
        }
        if (typeof detailsMessage.isPro === "boolean") {
          setIsPro(detailsMessage.isPro);
        }
        // Guarded on the type, not on truthiness, and for a sharper reason than
        // isPro's: roomPro is sent ONLY on the JOIN reply, so every broadcast
        // DETAILS_UPDATED omits it. Assigning unconditionally would clear a
        // copresenter's timer the moment anyone else's event reached them.
        if (typeof detailsMessage.roomPro === "boolean") {
          setRoomPro(detailsMessage.roomPro);
        }
        // Additive, and independent of isPro above.
        if (detailsMessage.planSource) {
          setPlanSource(detailsMessage.planSource);
        }
        // Two dead ends map onto the raw status, and both must stop the
        // watchdog: EXPIRED (host idle past the resume window, link can never
        // come back) and ROOM_FULL (turned away — the free plan's seats are
        // taken, and only the host can open one). Both flags are absent on
        // older backends, which fall through to the raw status untouched.
        // Carries a pause across a reload. Only trusted when the backend
        // actually sent it — an older backend omits the field, and treating
        // undefined as false is right, but reading it unconditionally would let
        // a malformed payload silently un-pause the UI while the server keeps
        // refusing moves.
        if (typeof detailsMessage.paused === "boolean") {
          setSelfPaused(detailsMessage.paused);
        }
        // Two dead ends map onto the raw status, and both must stop the
        // watchdog: EXPIRED (host idle past the resume window, link can never
        // come back) and ROOM_FULL (turned away — the free plan's seats are
        // taken, and only the host can open one). Both flags are absent on
        // older backends, which fall through to the raw status untouched.
        setStatus(
          detailsMessage.roomFull
            ? PresentationStatus.ROOM_FULL
            : detailsMessage.expired
            ? PresentationStatus.EXPIRED
            : detailsMessage.status
        );
        commitSlideSync(
          receiveSlide(
            slideSyncRef.current,
            {
              slide: detailsMessage.currentSlide ?? 0,
              seq: detailsMessage.slideSeq,
              epoch: detailsMessage.slideEpoch,
            }
          )
        );
        // Seed the preview with the latest image the backend has rendered
        // this session (present when joining mid-presentation and the host
        // is signed in).
        if (detailsMessage.currentSlideImage) {
          setCurrentSlideImage(detailsMessage.currentSlideImage);
          setPreviewOn(true); // a rendered image proves previews are working
        }
        // The backend cached the deck (drive.file grant took) → previews are on,
        // even if no slide has rendered yet (e.g. enabled before starting). Keeps
        // the "previews on" state across a reload; only ever flips ON (a missing
        // flag means "unknown", not "off", so it never clears a working preview).
        if (detailsMessage.previewEnabled) {
          setPreviewOn(true);
        }
        if (detailsMessage.previewLoading) setPreviewLoading(true);
        // Late-joiner snapshot of pre-rendered URLs for the whole deck.
        // Populate the map synchronously (cheap), then defer the actual
        // image-byte prefetches past the render commit so the status flip
        // to ONLINE paints FIRST. Without this, 44× `new Image()` runs
        // inside React's commit phase and visibly delays the "Connected"
        // transition by ~30–80ms.
        if (detailsMessage.slideStepImages) {
          const steps = Object.entries(detailsMessage.slideStepImages);
          setStepImages(
            new Map(
              steps
                .map(([idx, urls]) => [Number(idx), urls] as [number, string[]])
                .filter(([idx, urls]) => Number.isFinite(idx) && Array.isArray(urls) && urls.length > 0)
            )
          );
          for (const [, urls] of steps) urls?.forEach((u) => warmBrowserCache(u));
        }
        if (detailsMessage.stepSlide && typeof detailsMessage.currentStep === "number") {
          setLiveStep({ slide: detailsMessage.stepSlide, step: detailsMessage.currentStep });
        }
        if (detailsMessage.slideImages) {
          const entries = Object.entries(detailsMessage.slideImages);
          setSlideImages((prev) => {
            const next = new Map(prev);
            for (const [idxStr, url] of entries) {
              const idx = Number(idxStr);
              if (Number.isFinite(idx) && url) next.set(idx, url);
            }
            return next;
          });
          // requestIdleCallback when available (Chrome/Edge/Firefox/Safari 17+),
          // setTimeout(0) fallback for older Safari. Either way the warming
          // runs in a separate task so React can commit + paint first.
          const scheduleWarm: (cb: () => void) => void =
            typeof window !== "undefined" &&
            typeof (window as { requestIdleCallback?: unknown }).requestIdleCallback ===
              "function"
              ? (cb) => (window as Window).requestIdleCallback!(cb)
              : (cb) => {
                  setTimeout(cb, 0);
                };
          scheduleWarm(() => {
            tlog("warm loop start (deferred)", { count: entries.length });
            for (const [, url] of entries) {
              if (typeof url === "string" && url) warmBrowserCache(url);
            }
            tlog("warm loop end");
          });
        }

        addOrUpdateSession({
          magicId: magicId!,
          title: detailsMessage.title || "Untitled Presentation",
          lastActiveAt: new Date().toISOString(),
          totalSlides: detailsMessage.totalSlides,
          lastSlide: detailsMessage.currentSlide,
        });

        if (detailsMessage.status === PresentationStatus.ONLINE) {
          posthog.capture("presentation_connected", {
            magic_id: magicId,
            title: detailsMessage.title,
            total_slides: detailsMessage.totalSlides,
            current_slide: detailsMessage.currentSlide,
          });
        }
      }
    } else if (message.action === ActionType.UPDATE_SLIDE) {
      const updateSlideMessage: UpdateSlide = JSON.parse(event.data);

      if (updateSlideMessage && updateSlideMessage.magicId === magicId) {
        const pending = pendingEmailSlide.current;
        if (pending && pending.slide === updateSlideMessage.newSlide && Date.now() - pending.at < 30_000) {
          pendingEmailSlide.current = null;
          void recordEmailMilestone("remote_controlled", deckId);
        }
        commitSlideSync(
          receiveSlide(
            slideSyncRef.current,
            {
              slide: updateSlideMessage.newSlide,
              seq: updateSlideMessage.slideSeq,
              epoch: updateSlideMessage.slideEpoch,
            }
          )
        );
        // The deck's step report for the new slide follows this message.
        setLiveStep((prev) => (prev && prev.slide === updateSlideMessage.newSlide ? prev : null));
        addOrUpdateSession({
          magicId: magicId!,
          title: presentation?.title || "Untitled Presentation",
          lastActiveAt: new Date().toISOString(),
          lastSlide: updateSlideMessage.newSlide,
        });
      }
    } else if (message.action === ActionType.UPDATE_SLIDE_IMAGE) {
      const imageMessage: UpdateSlideImage = JSON.parse(event.data);

      if (imageMessage && imageMessage.magicId === magicId) {
        setCurrentSlideImage(imageMessage.image ?? "");
        if (imageMessage.image) setPreviewOn(true);
        // File it under its slide too. The per-slide map wins over the live
        // image when rendering (it is indexed, so it can never show the wrong
        // slide mid-navigation), which meant a stale entry there hid every
        // fresh render of that slide for as long as the session lasted.
        const renderedSlide = imageMessage.slideIndex;
        const renderedImage = imageMessage.image;
        if (renderedSlide && renderedSlide > 0 && renderedImage) {
          setSlideImages((prev) => {
            if (prev.get(renderedSlide) === renderedImage) return prev;
            const next = new Map(prev);
            next.set(renderedSlide, renderedImage);
            return next;
          });
        }
      }
    } else if (message.action === ActionType.PRELOAD_SLIDE_IMAGE) {
      const preload: PreloadSlideImage = JSON.parse(event.data);
      if (preload && preload.magicId === magicId && preload.image) {
        setSlideImages((prev) => {
          const next = new Map(prev);
          next.set(preload.slideIndex, preload.image);
          return next;
        });
        warmBrowserCache(preload.image);
      }
    } else if (message.action === ActionType.SLIDE_STEP_IMAGES) {
      const steps: SlideStepImages = JSON.parse(event.data);
      if (steps && steps.magicId === magicId && steps.slideIndex > 0) {
        setStepImages((prev) => {
          const next = new Map(prev);
          if (steps.images?.length) next.set(steps.slideIndex, steps.images);
          else next.delete(steps.slideIndex);
          return next;
        });
        steps.images?.forEach((u) => warmBrowserCache(u));
      }
    } else if (message.action === ActionType.UPDATE_SLIDE_STEP) {
      const step: UpdateSlideStep = JSON.parse(event.data);
      if (step && step.magicId === magicId && step.slideIndex > 0 && step.step >= 0) {
        setLiveStep({ slide: step.slideIndex, step: step.step });
      }
    } else if (message.action === ActionType.CO_PRESENTERS_CHANGED) {
      // Host controller: live roster of copresenters.
      const roster: CoPresentersChanged = JSON.parse(event.data);
      if (roster && roster.magicId === magicId) {
        setCoPresenters(roster.coPresenters ?? []);
      }
    } else if (message.action === ActionType.SET_CONTROL_LOCK) {
      // Lock toggled live by the host — copresenters switch to / out of
      // view-only; the host's other clients stay in sync.
      const lock: SetControlLock = JSON.parse(event.data);
      if (lock && lock.magicId === magicId) {
        setControlLocked(Boolean(lock.locked));
      }
    } else if (message.action === ActionType.SET_PRESENTER_PAUSED) {
      // Arrives at the paused copresenter. Guarded on targetPresenterId so a
      // frame about someone else — which shouldn't reach us, but the room is a
      // broadcast medium — can't put this client into view-only.
      const pause: SetPresenterPaused = JSON.parse(event.data);
      if (
        pause &&
        pause.magicId === magicId &&
        pause.targetPresenterId === presenterId
      ) {
        setSelfPaused(Boolean(pause.paused));
      }
    } else if (message.action === ActionType.REMOVE_PRESENTER) {
      // Arrives at the removed copresenter just before the backend closes our
      // socket. Same targetPresenterId guard as pause: a frame about someone
      // else must never take this client out of the room.
      const removal: RemovePresenter = JSON.parse(event.data);
      if (
        removal &&
        removal.magicId === magicId &&
        removal.targetPresenterId === presenterId
      ) {
        markRemovedByHost();
      }
    } else if (message.action === ActionType.JOIN_BLOCKED) {
      // Host controller: the free plan turned someone away. They're already
      // rejected by the time this arrives — this is purely the host's cue.
      const blocked: JoinBlocked = JSON.parse(event.data);
      if (blocked && blocked.magicId === magicId) {
        setBlockedJoiner(blocked.blockedName?.trim() || "Someone");
        if (typeof blocked.seatLimit === "number") setSeatLimit(blocked.seatLimit);
        captureUi("join_blocked_shown", { seat_limit: blocked.seatLimit });
      }
    } else if (message.action === ActionType.PREVIEW_READY) {
      setPreviewOn(true);
    } else if (message.action === ActionType.PREVIEW_LOADING) {
      const loading: { magicId?: string; loading?: boolean } = JSON.parse(event.data);
      if (loading.magicId === magicId) setPreviewLoading(Boolean(loading.loading));
    } else if (message.action === ActionType.PREVIEW_PERMISSION_REQUIRED) {
      setPreviewOn(false);
    }
  };

  // Take this copresenter out of the room locally. Idempotent — the frame and
  // the close code that follows it both land here.
  function markRemovedByHost() {
    if (removedByHostRef.current) return;
    removedByHostRef.current = true;
    setRemovedByHost(true);
    setStatus(PresentationStatus.REMOVED);
    setSelfPaused(false);
    captureUi("removed_by_host");
  }

  const { sendJsonMessage, readyState, getWebSocket } = useWebSocket(socketUrl, {
    shouldReconnect: (closeEvent: CloseEvent) => {
      // Removed by the host: reconnecting would re-JOIN and undo it. The close
      // code covers a REMOVE_PRESENTER frame that never arrived.
      if (removedByHostRef.current || closeEvent.code === REMOVED_CLOSE_CODE) {
        return false;
      }
      console.log("WebSocket - Reconnecting...", closeEvent);
      return true;
    },
    // Retry effectively forever so a backend restart (dotnet watch can take
    // ~5-10s; an EB deploy longer) never leaves the clicker stranded. The
    // exponential backoff caps each delay at 30s, so the load stays bounded.
    reconnectAttempts: Number.MAX_SAFE_INTEGER,
    reconnectInterval: (attemptNumber: number) =>
      Math.min(30000, 500 * Math.pow(2, attemptNumber)),
    queryParams: reconnectNonce ? { r: reconnectNonce } : undefined,
    onOpen: (event) => {
      wsErrorsSinceOpenRef.current = 0;
      joinPresentation(event);
    },
    onMessage: messageReceived,
    onClose: (event: CloseEvent) => {
      console.log("WebSocket - Closed");
      if (heartbeatRef.current !== null) {
        window.clearTimeout(heartbeatRef.current);
      }

      if (event.code === REMOVED_CLOSE_CODE && !removedByHostRef.current) {
        markRemovedByHost();
      }
      // Removed is not "Reconnecting…" — nothing is coming back on its own.
      if (removedByHostRef.current) return;

      setStatus(PresentationStatus.CLOSED);
      posthog.capture("presentation_disconnected", {
        magic_id: magicId,
      });
    },
    // A socket error is routine on phones — the OS drops the connection on
    // screen lock, app switch or a network change, and we reconnect. It used
    // to be sent as a hand-built `$exception`, which error tracking rejected
    // (no `$exception_list`) and which flooded the error stream with an
    // expected transient. It's a plain event now, with the context needed to
    // tell a lock-screen drop from a real outage.
    onError: (event: Event) => {
      console.log("WebSocket error: ", event);
      wsErrorsSinceOpenRef.current += 1;
      if (wsErrorsSinceOpenRef.current > 1) return;
      posthog.capture("ws_error", {
        magic_id: magicId,
        presenter_id: presenterId,
        visibility_state:
          typeof document !== "undefined" ? document.visibilityState : null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
        ms_since_last_frame: Date.now() - lastServerMessageRef.current,
      });
    },
  });

  // Liveness watchdog — keeps the clicker honestly attached to the live
  // presentation no matter how the host comes and goes.
  //
  // The cached `status` can't be trusted on its own:
  //  - Connected BEFORE the host started → backend has nothing to join, replies
  //    NOT_STARTED and doesn't track us; the host starting later reaches no one.
  //  - Host closes their tab and reopens minutes later → the backend may have
  //    swapped the in-memory presentation, orphaning us while we still show a
  //    stale "Connected".
  //  - Socket goes half-open (sleep/Wi-Fi) → we think we're connected but no
  //    frames flow.
  //
  // So on a steady tick, while the socket is OPEN and we know a name:
  //  - status !== ONLINE → rejoin (we're waiting; the first rejoin after the
  //    host (re)starts attaches us to the live presentation — adding us to its
  //    presenter list so slide broadcasts resume — and the reply flips us live);
  //  - ONLINE but no frame for a while → send one rejoin as a liveness probe
  //    (an idle-but-present host replies ONLINE, harmless; a vanished one
  //    replies NOT_STARTED, dropping us into the waiting path above);
  //  - no frame for far too long, even after probing → the socket is dead, so
  //    close it and let react-use-websocket reconnect and rejoin from scratch.
  //
  // Rejoins here are retries, not fresh joins: no analytics, no heartbeat.
  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return;
    if (!magicId) return;
    const displayName =
      name ||
      (typeof window !== "undefined"
        ? window.localStorage.getItem("presenterName")
        : null);
    if (!displayName) return; // still waiting on the name modal

    const rejoin = (why: string) => {
      tlog(`watchdog: re-JOIN (${why})`);
      // Host mode re-sends credentials so a controller that connected before
      // the host started presenting (HostUserSub not yet set) gets upgraded to
      // host on a later tick — not just on a full reconnect.
      if (isHostMode) {
        void sendHostJoinRef.current();
        return;
      }
      const message: JoinPresentation = {
        action: ActionType.JOIN_PRESENTATION,
        magicId,
        presenterId,
        name: displayName,
      };
      sendJsonMessage(message);
    };

    const id = window.setInterval(() => {
      // A dead link is terminal — stop hammering it. The host can never return
      // to this magicId (their extension has rotated to a new link), so there's
      // nothing to wait for; no re-JOIN, no forced reconnect.
      if (status === PresentationStatus.EXPIRED) return;
      // Likewise a full room: re-JOINing can only be rejected again, and each
      // attempt fires a fresh JOIN_BLOCKED at the host. Only the host can open
      // a seat, and the user has an explicit "Try again" for when they do.
      if (status === PresentationStatus.ROOM_FULL) return;
      // Removed by the host: the socket is already gone, and re-JOINing would
      // undo the removal. Opening the link again is how they come back.
      if (status === PresentationStatus.REMOVED) return;
      // And a refused host-controller join: re-sending the same rejected token
      // can only be refused again, and without this the device re-JOINs every
      // 2.5s forever against a door that will not open. Unlike the two above it
      // CAN self-heal — the resolve poll flips deckResolveStatus back to
      // "ready" once there is a working session, and rejoining resumes.
      if (isHostMode && deckResolveStatus === "unauthenticated") return;

      const quietMs = Date.now() - lastServerMessageRef.current;

      if (quietMs > CONNECTION_DEAD_AFTER_MS) {
        tlog("watchdog: socket stale, forcing reconnect", { quietMs });
        // Reset first so we don't hammer close() before readyState flips.
        lastServerMessageRef.current = Date.now();
        try {
          getWebSocket?.()?.close();
        } catch {
          /* ignore — reconnect is best-effort */
        }
        return;
      }

      if (status !== PresentationStatus.ONLINE) {
        rejoin("waiting for host");
      } else if (isHostMode && presentationAllowanceBlockedRef.current) {
        // Behind the Free limit wall. An ONLINE controller otherwise only
        // re-JOINs when the socket goes quiet, and a live deck keeps it busy —
        // so an upgrade in the other tab would not lift the wall until the
        // room fell silent. The backend answers from a cache, so this is cheap.
        rejoin("presentation limit");
      } else if (isHostMode && awaitingPaymentRef.current) {
        // The plan only reaches this page on a JOIN reply. Mid-slideshow the
        // next one would be the 20s liveness probe, so a payment took up to
        // that long to show. The webhook drops the backend's plan cache, so
        // each of these sees the new plan as soon as it's written.
        rejoin("awaiting payment");
      } else if (quietMs > ONLINE_PROBE_AFTER_MS) {
        rejoin("liveness probe");
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [
    status,
    deckResolveStatus,
    readyState,
    magicId,
    presenterId,
    name,
    isHostMode,
    sendJsonMessage,
    getWebSocket,
    tlog,
  ]);

  // Revalidate the connection the instant the tab is foregrounded. Background
  // tabs — Safari is especially aggressive — freeze our heartbeat and watchdog
  // timers, so the socket often dies while we're away. Without this we'd wait
  // for the watchdog's next tick (≤2.5s), or, for a socket that still *looks*
  // OPEN but is actually dead (suspended-tab zombie), up to
  // CONNECTION_DEAD_AFTER_MS (40s) before noticing. On return we check now: if
  // the socket isn't healthy, force an immediate reconnect (onOpen re-JOINs)
  // so it's back before the "Reconnecting…" flash is even noticeable.
  useEffect(() => {
    if (!magicId) return;
    if (typeof document === "undefined") return;

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;

      // Back from the Stripe tab: ask for the plan now rather than on the
      // watchdog's next tick.
      if (
        isHostMode &&
        awaitingPaymentRef.current &&
        readyState === ReadyState.OPEN
      ) {
        void sendHostJoinRef.current();
      }

      const quietMs = Date.now() - lastServerMessageRef.current;
      const healthy =
        readyState === ReadyState.OPEN && quietMs < ONLINE_PROBE_AFTER_MS;
      if (healthy) return; // open and recently heard from — leave it alone

      tlog("visibility: foregrounded, revalidating", { readyState, quietMs });
      // Measure the dead-timer from now so the watchdog doesn't also pile on.
      lastServerMessageRef.current = Date.now();

      if (readyState === ReadyState.OPEN) {
        // Looks open but stale (suspended-tab zombie) → force a clean reconnect.
        try {
          getWebSocket?.()?.close();
        } catch {
          /* best-effort — reconnect is driven by react-use-websocket */
        }
      } else if (readyState === ReadyState.CLOSED) {
        // Already closed, waiting out the backoff. The delay doubles per
        // failed attempt (capped at 30s) and keeps growing while the phone is
        // asleep, so someone unlocking mid-talk could wait up to 30s for the
        // remote to work. Dial now instead. CONNECTING is left alone: an
        // attempt is already in flight, and tearing it down would make its
        // close event schedule a second, stray reconnect.
        setReconnectNonce((n) => n + 1);
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    // Coming back online is the same moment for a socket that died with the
    // network: don't wait for the backoff either.
    window.addEventListener("online", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onVisible);
    };
  }, [readyState, magicId, isHostMode, getWebSocket, tlog]);

  // Copresenters can't move while the host has locked control; the host (and
  // backend) bypass the lock. Mirrors the server-side guard so the UI doesn't
  // optimistically advance a move the backend will drop.
  // Two independent ways control is taken away, and either one blocks moves:
  // the whole-room lock, and a pause aimed at this client. Hosts bypass the
  // lock, and the backend refuses to pause a host at all, so `youAreHost`
  // guards only the former.
  //
  // This mirrors the server-side guard so the UI doesn't optimistically advance
  // a move the backend is going to drop — it is NOT the enforcement. That lives
  // in WebSocketController.MayMove, which is what makes the pause hold for a
  // client that never got the message.
  const controlBlocked = (controlLocked && !youAreHost) || selfPaused;

  // Turned away: the host's free plan is out of copresenter seats, so this
  // client never entered the room and there's nothing to control.
  const roomFull = status === PresentationStatus.ROOM_FULL;
  const removed = status === PresentationStatus.REMOVED;

  // Seats are about who's in the room NOW, so OFFLINE roster entries (which
  // linger for up to ~20min before cleanup) don't count — same rule the backend
  // enforces. Derived from the rendered roster so the counter can never
  // contradict the list it sits under.
  const onlineCoPresenterCount = coPresenters.filter(
    (p) => p.status === "ONLINE"
  ).length;

  // The plan as the backend last stated it. `undefined` means not told yet.
  const isProNow: boolean | undefined = isPro;
  awaitingPaymentRef.current = isHostMode && upgradeOpen && isProNow !== true;

  // A host the backend has CONFIRMED is on the free plan. Gates everything
  // plan-shaped in the shell: the standing upgrade button, the at-capacity
  // pitch, and hiding the Pro-only preview panel.
  //
  // `=== false`, never `!isProNow` — `undefined` means "not told yet"
  // (resolve in flight, or a backend older than this flag), and treating that as
  // free would flash a paywall at a paying host mid-presentation. An anonymous
  // host resolves to free server-side (Plans.IsPro(null) is false), so this
  // covers "not signed in" without a separate test.
  const isFreeHost = isHostMode && isProNow === false;

  // Is the preview panel showing?
  //
  // Three inputs, in strict order:
  //  1. a choice made in THIS session — always wins, either way;
  //  2. the stored preference — trusted ONLY for a host who is confirmed Pro;
  //  3. otherwise closed.
  //
  // Rule 2 is the load-bearing one. The stored key is a plain boolean with no
  // notion of who wrote it, so a `true` saved while on Pro would keep
  // re-opening a PAYWALL on every load once Pro lapsed — an advert that
  // reinstates itself forever because of a click that meant "show me the
  // feature", back when it was a feature. Nobody consented to that.
  //
  // Rule 3 also covers `isPro === undefined` (plan not resolved yet), so a
  // first open is closed rather than briefly open. A Pro host's panel appears a
  // beat late instead; that's the right way round — appearing is a far smaller
  // insult than vanishing.
  //
  // The plan test applies to the HOST controller ONLY. A copresenter is never
  // sent isPro (it's host-only on the JOIN reply) and never sees the paywall
  // panel, so gating them on it would leave every copresenter's preview
  // permanently closed — with nothing on screen explaining why. Their gate is
  // the backend: a free room is sent no images, so there is simply nothing to
  // show and the panel stays out of the way on its own.
  // The touch layout (phones and tablets, see ../layout), mirroring the CSS
  // MOBILE_QUERY rules so the layout below knows which tree it is building.
  // Updates live when the window crosses it or the device rotates.
  const isMobileLayout = useMediaQuery(MOBILE_QUERY);
  // Previews the extension uploads are free, so a free host gets them too:
  // their images, and the loading state while they're on the way.
  const uploadedPreviews =
    previewLoading ||
    isUploadedPreview(currentSlideImage) ||
    Array.from(slideImages.values()).some(isUploadedPreview);
  // Previews are on every plan since COP-33, so the plan no longer decides.
  const previewPlanTrusted = true;
  // The touch layout has no Preview pill. Its switch is a tile in the host's
  // menu, so only a verified host's stored "off" is honoured there: anyone
  // else (a copresenter on the same phone) has no switch to turn it back on.
  const previewVisible = isMobileLayout
    ? previewPlanTrusted && (!(isHostMode && youAreHost) || mobilePreviewOn)
    : previewChoice ?? (previewPlanTrusted ? storedPreviewPref ?? true : false);

  // At the cap right now: the pitch for more seats only makes sense once the
  // seats are actually gone.
  const seatsFull =
    isFreeHost && seatLimit != null && onlineCoPresenterCount >= seatLimit;
  // Copresenting is closed to this free host: the backend says zero seats,
  // which since COP-33 means their free copresented sessions are used up (or
  // an older backend with no pass). While a session is left it sends no limit.
  const copresentLocked = isFreeHost && seatLimit === 0;
  // The free copresent pass, for a free host while it has something to say.
  const freeCopresentPass =
    isFreeHost && freeCopresent ? <FreeCopresentPass pass={freeCopresent} /> : null;

  // Keep the turned-away notice on the host's radar until the situation TRULY
  // resolves — not on the routine ONLINE flicker of a copresenter reconnecting
  // or backgrounding a tab. That flicker briefly drops seatsFull; an earlier
  // version cleared the name on the first dip, so a passing blip made the notice
  // "show, then vanish" while the room was still full and the person still shut
  // out. The rules:
  //   • Upgraded to Pro → the cap is gone, clear at once.
  //   • A seat stays free for a few seconds → a real departure, clear.
  //   • Still full, or a sub-second blip → keep showing (the timer is cancelled
  //     the moment seatsFull flips back true).
  //
  // At the shipped ZERO seats the middle rule can never fire: no seat can free
  // up, because there were none. So the notice stands until the host upgrades or
  // dismisses it, which is the honest reading — the person at the door is still
  // shut out, and nothing except buying Pro will change that. The dismiss button
  // on the notice is what stops it being permanent furniture.
  useEffect(() => {
    if (!blockedJoiner) return;
    if (isProNow === true) {
      setBlockedJoiner(null);
      return;
    }
    if (seatsFull) return;
    const timer = window.setTimeout(() => setBlockedJoiner(null), 5000);
    return () => window.clearTimeout(timer);
  }, [blockedJoiner, seatsFull, isProNow]);

  const hostProfile = getStoredHostSession()?.profile;
  const hostProfileEmail = hostProfile?.email;

  // The host pays in the Stripe tab; this tab's 2.5s re-JOIN picks up the new
  // plan (the webhook invalidates the cached one), so the offer can close
  // itself the moment it's been accepted rather than sit there stale. Still
  // needed with the bounce: the ORIGINAL tab is left open behind Stripe and
  // gets no ?upgraded=1 of its own, so this is what un-caps it.
  useEffect(() => {
    if (isProNow === true) setUpgradeOpen(false);
  }, [isProNow]);

  // Arriving back from Stripe. The offer dialog is dismissed alongside — the
  // question it asks has just been answered, and stacking the confirmation on
  // top of a live paywall reads as if the payment didn't take.
  useEffect(() => {
    if (searchParams.get(UPGRADED_PARAM) !== "1") return;
    setUpgradeOpen(false);
    setPaidOpen(true);
    // Strip the flag so a reload (or Back) doesn't re-congratulate them.
    const next = new URLSearchParams(searchParams);
    next.delete(UPGRADED_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Open the Pro offer. A dialog, not a navigation — every caller fires while
  // the host is presenting, so we must not take the controller off screen.
  const openUpgrade = (source: string) => {
    // A paired phone is not signed in, so there is no account to sell to here.
    if (pairedDevice) {
      captureUi("paired_device_account_action", { action: "upgrade", source });
      // Close the menu sheet first, or the note opens underneath it.
      setRosterOpen(false);
      setAccountOnlyOpen(true);
      return;
    }
    captureUi(UPGRADE_CLICKED, {
      source,
      seats_in_use: onlineCoPresenterCount,
      seat_limit: seatLimit,
    });
    setUpgradeSource(source);
    setUpgradeOpen(true);
  };

  const closeUpgrade = () => {
    captureUi("upgrade_dismissed");
    setUpgradeOpen(false);
  };

  // The backend sends the limit it enforced; fall back to the shipped default
  // rather than render "up to undefined copresenters" if an older backend (or a
  // reconnect that dropped the flag) leaves it out.
  const seatLimitLabel = presentation?.seatLimit ?? FREE_COPRESENTER_SEATS;



  // Signed in with a still-valid host token (the deck-session resolve didn't come
  // back 401). getStoredHostSession() alone can hold a stale token; the resolve
  // status is the authority on whether it still works.
  //
  // This is now the gate on the controller ITSELF, not just on the plan. It used
  // to mean only "has a Google account attached" — the controller opened for
  // anyone holding the deck's host key, and signing in was optional rather than
  // the price of entry. Every host has an account now.
  const hostSignedIn =
    isHostMode &&
    (!!getStoredHostSession() || pairedDevice) &&
    deckResolveStatus !== "unauthenticated";

  // Whether the controller may render at all.
  //
  // One condition now, where there used to be two independent ones — a host key
  // OR an account — plus a third state for a keyed deck opened without its key.
  // All three collapsed the moment sign-in became mandatory: the account is the
  // only credential, so "authorised" and "signed in" are the same question.
  const hostAuthorized = hostSignedIn;

  // The Pro paywall.
  //
  // One ask: the purchase. Everyone who reaches this is signed in, and there
  // is no trial to offer (COP-33).
  const openProPaywall = (source: string) => {
    captureUi(PRO_PAYWALL_SHOWN, { source, offer: "purchase" });
    openUpgrade(source);
  };

  // The Free presentation allowance, spoken only once it is enforced and the
  // host is close enough for it to matter. In shadow mode it is counted and
  // logged server-side but never shown — nothing on screen may describe a
  // limit that isn't being applied.
  const usageLine =
    presentationAllowance &&
    presentationAllowance.enforced &&
    !presentationAllowance.blocked &&
    presentationAllowance.used >= METER_FROM
      ? `${Math.min(presentationAllowance.used, presentationAllowance.limit)} of ${
          presentationAllowance.limit
        } free presentation${presentationAllowance.limit === 1 ? "" : "s"} used this month`
      : null;

  // For the avatar menu: what's left this allowance month and when it resets.
  // The month starts on the day the account signed up, so the reset date is
  // the one thing a host can't work out for themselves.
  const menuAllowance: MenuAllowance | null =
    isFreeHost && presentationAllowance?.enforced
      ? {
          left: Math.max(0, presentationAllowance.limit - presentationAllowance.used),
          limit: presentationAllowance.limit,
          resetsOn: formatResetDate(presentationAllowance.resetsAt),
        }
      : null;

  // Out of free presentations for the month. From the resolve's 402 (before a
  // socket is opened) or from a JOIN reply (a controller already connected,
  // e.g. left open overnight). The backend already refuses this controller's
  // moves; this is the explanation, and the way out.
  const hostLimitReached =
    isHostMode &&
    hostSignedIn &&
    (deckResolveStatus === "limit" || presentationAllowance?.blocked === true);

  // Once per wall, not per poll: the resolve keeps answering 402 every tick.
  const limitShownRef = useRef(false);
  useEffect(() => {
    if (!hostLimitReached) {
      limitShownRef.current = false;
      return;
    }
    if (limitShownRef.current) return;
    limitShownRef.current = true;
    captureUi("presentation_limit_shown", {
      used: presentationAllowance?.used,
      limit: presentationAllowance?.limit,
    });
    // captureUi is stable for our purposes; the wall is what we key on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostLimitReached]);

  // Host controller: pause or resume ONE copresenter's slide control.
  //
  // On every plan since COP-33: it goes with copresenting, which a free host
  // now gets through their free copresented sessions.
  //
  // Not optimistic, unlike the control lock: the backend echoes the change to
  // the target and re-broadcasts the roster, and a local flip would show the
  // host a paused row that the server had actually refused (a lapsed plan, a
  // race with the person leaving). The round-trip is one hop on a socket that's
  // already open.
  const pauseActionLabel = (target: Presenter) => {
    const who = target.name || "Guest";
    return target.paused
      ? `Resume ${who}'s slide control`
      : `Pause ${who}'s slide control`;
  };

  // Tell the backend a thumbnail it gave us is dead (a 403 on an expired Slides
  // contentUrl). It cannot find this out by itself — the browser fetches the URL
  // straight from googleusercontent — so its own 25-minute cache would keep
  // serving the same corpse to every render until that entry aged out.
  //
  // Safe to call from every <img> onError: each element fires once per URL
  // (we then stop rendering that src), and the server collapses reports to one
  // re-render per slide per minute, so a full room costs one render between
  // them. The reply is an ordinary PRELOAD_SLIDE_IMAGE with the replacement,
  // which the existing handler already files by index.
  const reportImageFailure = (slideIndex: number, image: string) => {
    if (!magicId || !image || slideIndex < 1) return;
    sendJsonMessage({
      action: ActionType.PREVIEW_IMAGE_FAILED,
      magicId,
      presenterId,
      slideIndex,
      image,
    });
  };

  const togglePresenterPaused = (target: Presenter) => {
    const next = !target.paused;
    captureUi("presenter_pause_toggled", { paused: next });
    sendJsonMessage({
      action: ActionType.SET_PRESENTER_PAUSED,
      magicId,
      presenterId,
      targetPresenterId: target.presenterId,
      paused: next,
    });
  };

  // Host controller: remove ONE copresenter from the room.
  //
  // Not Pro-gated, unlike pause: removal frees a seat, and the free plan is
  // where a seat matters most. Not optimistic either — the backend re-broadcasts
  // the roster, and a local removal could hide someone the server refused (a
  // host-class target, a race with them leaving). No confirm step: removal is
  // not a ban (the same link still works), so a mis-tap doesn't cost a seat.
  const removeCopresenter = (target: Presenter) => {
    captureUi("presenter_removed");
    sendJsonMessage({
      action: ActionType.REMOVE_PRESENTER,
      magicId,
      presenterId,
      targetPresenterId: target.presenterId,
    });
  };

  // Signed-in host whose deck has no live session yet (they haven't opened or
  // started it). Not an error and NOT a reason to re-sign-in — we show a "not
  // running" screen and keep polling, so it flips to the controller the moment
  // the deck goes live.
  const hostDeckIdle =
    isHostMode && !youAreHost && hostAuthorized && deckResolveStatus === "no_session";

  // Host whose JOIN hasn't come back yet: show a spinner rather than a
  // half-built controller for a beat. Every host is authorized now, so this is
  // purely "waiting on the round-trip", not "waiting to find out if you're
  // allowed".
  const hostResolving =
    isHostMode &&
    !youAreHost &&
    hostAuthorized &&
    !hostDeckIdle &&
    !hostAuthTimedOut;

  // Arm the fallback only once we actually have a session to JOIN (a resolved
  // magicId). Before that we're either spinning on the deck resolve or sitting
  // on the idle screen, neither of which should time out.
  //
  // It no longer drops to a sign-in card — there isn't one. All it does is end
  // the spinner so a host whose verification never lands gets the controller
  // shell instead of spinning forever.
  useEffect(() => {
    if (!isHostMode || youAreHost) return;
    if (!resolvedMagicId) return;
    const id = window.setTimeout(() => setHostAuthTimedOut(true), 8000);
    return () => window.clearTimeout(id);
  }, [isHostMode, youAreHost, resolvedMagicId]);

  // Send one move. The screen doesn't step: the tap may only play an animation
  // on the deck, so the slide shown changes when the server says the deck
  // moved — see ../slideSync. The server answers every move by its moveId.
  const sendMove = useCallback(
    (action: ActionType.MOVE_NEXT | ActionType.MOVE_PREV) => {
      if (!magicId) return;
      moveCounterRef.current += 1;
      const moveId = `${Date.now().toString(36)}-${moveCounterRef.current}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const message: MoveSlide = { action, magicId, presenterId, moveId };
      sendJsonMessage(message);
    },
    [magicId, presenterId, sendJsonMessage]
  );

  const moveNext = useCallback(() => {
    if (controlBlocked) return;
    // Nothing to drive until the deck is live. This used to be enforced only by
    // the PREV/NEXT buttons' `disabled`, which left every other route in —
    // swipe and the arrow keys both call straight through here, so a swipe
    // while "Waiting for presentation to start…" fired a MOVE at a room that
    // has no slideshow.
    if (status !== PresentationStatus.ONLINE) return;
    // Not stopped at the last slide: it can still have animations to play, and
    // the clicker can't see them (COP-92). A tap past the real end does nothing.
    if (magicId && presentation && presentation.totalSlides) {
      const target = Math.min(currentSlide + 1, presentation.totalSlides);
      sendMove(ActionType.MOVE_NEXT);
      pendingEmailSlide.current = { slide: target, at: Date.now() };
      posthog.capture("slide_changed", {
        magic_id: magicId,
        direction: "next",
        // `current_slide` (not `new_slide`) to match the rest of the analytics
        // schema — the old name landed as NULL in PostHog's `current_slide`
        // column and broke "did the audience reach the last slide?" reporting.
        // The slide the tap heads for: it may only play an animation.
        current_slide: target,
        total_slides: presentation.totalSlides,
        // Attribute the advance to the specific copresenter who drove it
        // (joinable against the Firestore presenters subcollection).
        driver_presenter_id: presenterId,
      });
    }
  }, [controlBlocked, status, magicId, presentation, currentSlide, presenterId, sendMove, posthog]);

  const movePrev = useCallback(() => {
    if (controlBlocked) return;
    // Same gate as moveNext — see the note there.
    if (status !== PresentationStatus.ONLINE) return;
    // Not stopped at the first slide either: its animations can be reversed.
    if (magicId) {
      const target = Math.max(currentSlide - 1, 1);
      sendMove(ActionType.MOVE_PREV);
      pendingEmailSlide.current = { slide: target, at: Date.now() };
      posthog.capture("slide_changed", {
        magic_id: magicId,
        direction: "prev",
        current_slide: target,
        // Was silently missing on the prev branch — completion math needs it.
        total_slides: presentation?.totalSlides,
        driver_presenter_id: presenterId,
      });
    }
  }, [controlBlocked, status, magicId, presentation, currentSlide, presenterId, sendMove, posthog]);

  // Host controller: flip the slide-control lock. Optimistic — the backend
  // re-broadcasts SET_CONTROL_LOCK to confirm and to fan out to everyone else.
  // NOTE: slide-control locking is temporarily hidden from the UI (planned as a
  // premium feature). The receive side stays wired — `controlLocked` still
  // drives the copresenter view-only banner if a lock ever arrives — but there's
  // no host toggle to originate one for now. The `toggleControlLock` sender was
  // removed with the button; restore it from git history when the feature ships.

  // The three sign-in doors that used to live here — startTrial,
  // signInExisting and openSignUp, with their trial_start_clicked /
  // returning_sign_in_clicked / sign_up_opened events — are gone with the
  // anonymous controller they served. There is one door now, on the wall, and
  // it reports itself as `host_auth_started` with an `intent` that carries the
  // distinction those three names used to.

  // Path B: start the web Google sign-in, returning to this exact /host page.
  const signInAsHost = () => {
    void beginGoogleSignIn(window.location.pathname + window.location.search);
  };

  // Sign out of the host account.
  //
  // A full reload rather than clearing React state: the session seeds the deck
  // resolve, the plan, the entitlement and the roster, and unpicking all of that
  // in place is far more likely to leave a stale fragment than starting clean.
  //
  // Note this signs out of COPRESENT only — the Google account stays signed in
  // to the browser. Signing back in will silently reuse it, because our OAuth
  // call asks for prompt=consent without select_account, so Google never offers
  // the picker. Switching accounts still means signing out of Google itself.
  // To the host's dashboard. In this tab, like any app's home button: the
  // dashboard shows the live deck with a "Return to presentation" link, and
  // the slideshow itself keeps running in the Slides tab meanwhile.
  const navigate = useNavigate();
  const goHome = (e?: React.MouseEvent) => {
    e?.preventDefault();
    captureUi("home_clicked");
    navigate("/");
  };

  const signOutHost = () => {
    captureUi("host_sign_out_clicked");
    clearHostSession();
    window.location.reload();
  };

  // "Enable live previews" — the drive.file Picker grant.
  //
  // Instrumented because this is the step that decides whether previews work at
  // all: a Pro host who never completes the grant gets the feature they paid for
  // and sees nothing. It was the one control in here with no event of any kind,
  // so the drop-off between "was offered previews" and "actually has them" was
  // invisible.
  //
  // `opened` carries openPreviewGrant's return value, which is false when the
  // deck or magic id is missing — a button that silently does nothing. Both call
  // sites pass `?? ""`, so that state is reachable rather than theoretical, and
  // it's worth being able to see it in the data instead of guessing at a funnel
  // that stalls for no visible reason.
  //
  // A phone goes same-tab rather than opening a popup — see openPreviewGrant for
  // why a popup can't finish there. The event is captured BEFORE we navigate,
  // because an event queued after assign() may never be flushed.
  const enableLivePreviews = (source: string) => {
    // The Drive grant page needs the host's account; a paired phone has none.
    if (pairedDevice) {
      captureUi("paired_device_account_action", { action: "previews", source });
      // Close the menu sheet first, or the note opens underneath it.
      setRosterOpen(false);
      setAccountOnlyOpen(true);
      return;
    }
    const deck = presentation?.presentationId ?? "";
    const magic = magicId ?? "";

    if (!deck || !magic) {
      captureUi("preview_grant_clicked", { source, opened: false, same_tab: false });
      return;
    }

    if (isMobileLayout) {
      captureUi("preview_grant_clicked", { source, opened: true, same_tab: true });
      openPreviewGrant(deck, magic, { sameTab: true });
      return;
    }

    const opened = openPreviewGrant(deck, magic);
    captureUi("preview_grant_clicked", { source, opened, same_tab: false });
    // Popup blocked. The host clicked and nothing appeared, so finish in this
    // tab instead of leaving them with a button that visibly does nothing —
    // the grant page knows its way back here.
    if (!opened) openPreviewGrant(deck, magic, { sameTab: true });
  };

  // A prior sign-in on THIS device — the stored session token exists even if it
  // has expired (an expired token still resolves via the host key, so we stay
  // "anonymous" without losing the fact that this person has signed in here).
  // The one signal that lets us lead with "Sign in" for a returning host; a
  // QR-paired phone has none, so it defaults to showing both. It also picks the sign-up door's LABEL — see AuthChoice.
  const hasPriorSession = !!getStoredHostSession();

  // Copy-to-clipboard with brief "Copied" feedback, keyed by which link.
  const [copiedLink, setCopiedLink] = useState<"join" | "host" | null>(null);
  const copyLink = (which: "join" | "host", url: string) => {
    captureUi("invite_link_copied", { link_type: which });
    const done = () => {
      if (which === "join") void recordEmailMilestone("team_intent", deckId);
      setCopiedLink(which);
      window.setTimeout(() => setCopiedLink(null), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {});
    } else {
      done();
    }
  };

  /**
   * The invite link, through the phone's own share sheet.
   *
   * "Copied" is a promise the app cannot keep on a phone: the clipboard is one
   * slot, the host still has to leave the app, find the thread and paste, and
   * anything they copy on the way (a room code, a URL from the deck) silently
   * eats it. The share sheet skips all of that — it opens the list of people and
   * apps the phone already knows about, and the link lands in the chat in one
   * gesture without the app ever holding it.
   *
   * Desktop keeps Copy. navigator.share exists in some desktop browsers, but
   * there the link is on screen in a field beside the button, and a modal
   * "share to…" panel is a worse answer than a clipboard the host can paste
   * into the window they already have open.
   */
  const shareInviteLink = async (url: string) => {
    captureUi("invite_link_shared", { link_type: "join" });
    try {
      await navigator.share({
        // Titles are advisory — most targets show the URL and ignore this —
        // but the ones that do use it (mail, notes) get the deck's name.
        title: presentation?.title
          ? `Follow "${presentation.title}"`
          : "Join my presentation",
        url,
      });
    } catch (error) {
      // Dismissing the sheet is the ordinary outcome of opening one, not a
      // failure — it must not leave a "Copied" behind, and it must not fall
      // through to a clipboard write the host did not ask for.
      if ((error as DOMException | undefined)?.name === "AbortError") return;
      // Anything else (no permission, no target, an older WebView) still has to
      // leave the host holding the link, so the old behaviour is the fallback.
      copyLink("join", url);
    }
  };

  const heartbeat = () => {
    if (
      readyState === ReadyState.CONNECTING ||
      readyState === ReadyState.OPEN
    ) {
      const heartbeatMessage = {
        action: ActionType.HEARTBEAT,
        presenterId: presenterId,
      };

      sendJsonMessage(heartbeatMessage);
      heartbeatRef.current = window.setTimeout(heartbeat, 10000); // 10 seconds ping
    }
  };

  // Before the slideshow starts, currentSlide is 0 — fall back to the FIRST
  // slide's note so the host (and waiting copresenters) can preview the opening
  // note instead of an empty panel.
  const noteIndex = currentSlide > 0 ? currentSlide - 1 : 0;
  const currentNote = cleanNoteText(presentation?.notes?.[noteIndex] ?? "");
  // Deck has notes if ANY slide has a non-empty note. Controls toggle visibility.
  const deckHasNotes = !!presentation?.notes?.some((n) => n && n.length > 0);

  const isPresenterShape = useMediaQuery(PRESENTER_VIEW_QUERY);
  const isPhoneLandscape = useMediaQuery(PHONE_LANDSCAPE_QUERY);

  // --- Touch layout: slide views (see BigSlideArea) ----------------------
  // The slide to show large. Before the slideshow starts there is no current
  // slide, so it's the first one, the same fallback the host preview uses.
  const bigSlideIndex = currentSlide > 0 ? currentSlide : 1;
  const bigSlideSrc = shownImage(bigSlideIndex, previewSources) || currentSlideImage;
  // Previews are on every plan since COP-33, so no plan check here.
  const slideViewAvailable = isMobileLayout && previewVisible && !!bigSlideSrc;
  // Only a deck with notes has anything to switch between.
  const showViewSwitch = slideViewAvailable && deckHasNotes;
  const mobileShowsSlide =
    slideViewAvailable && (!deckHasNotes || mobileView === "slide");
  const presenterView = mobileShowsSlide && isPresenterShape;

  // A phone turned sideways opens the stage by itself, until its exit button
  // is pressed. Turning back upright re-arms it for the next time.
  useEffect(() => {
    if (!isPhoneLandscape) setStageDismissed(false);
  }, [isPhoneLandscape]);
  //
  // Once requested, the stage depends on having a slide, NOT on the layout.
  // Entering browser full screen resizes the window: a narrow desktop window
  // grows past 768px and flips to the desktop layout. Tied to the layout, the
  // stage vanished there and left the browser full screen with no way out on
  // the page.
  const stageHasSlide =
    previewVisible && !!bigSlideSrc;
  const stageOpen =
    stageHasSlide &&
    (stageRequested ||
      (isMobileLayout && isPhoneLandscape && !stageDismissed));
  // The notes *panel* shows whenever the deck has notes anywhere AND the user
  // hasn't hidden them — even on slides where the current note is empty, we
  // still render the panel (with a placeholder) so the layout doesn't jump as
  // the host advances through note-less slides.
  //
  // With the Slide | Notes switch on screen, the switch decides instead of the
  // Notes pill (which it replaces).
  const showNotesPanel =
    deckHasNotes && (showViewSwitch ? !mobileShowsSlide : notesVisible);
  // Whether the current slide specifically has a note to show. Drives the
  // "content vs. empty placeholder" branch *inside* the panel — not whether
  // the panel renders.
  const currentSlideHasNote = currentNote.length > 0;

  // Current slide preview. Renders once we have a URL for the current slide
  // from either source — UPDATE_SLIDE_IMAGE (live current) or the preload map
  // (already rendered ahead of time). Signed-out hosts never produce images
  // at all, so the preview area stays hidden rather than showing a permanent
  // "waiting…" placeholder. `previewPrimary` (notes panel is closed) just
  // controls sizing when it does show: large/centerpiece, vs compact strip
  // when the notes panel is alongside.
  const previewPrimary = !showNotesPanel;
  // Whether the deck supports preview at all (any image has arrived this
  // session). Drives the toggle-button gate in the header — if no image has
  // ever shown up, there's nothing to toggle, so we don't render the chip.
  const deckHasPreview =
    !!currentSlideImage || slideImages.size > 0;
  // Render the preview only when (a) the user hasn't hidden it AND (b) the
  // current slide actually has an image URL we can show.
  const showPreview =
    previewVisible &&
    (!!currentSlideImage || slideImages.has(currentSlide));

  // Phone + a browser that has a share sheet. Read on every render rather than
  // captured once: the value has to track the viewport, and feature-detecting
  // navigator.share is the only honest test of whether the sheet exists — the
  // API is absent on desktop Firefox and inside some in-app WebViews.
  const canShareInvite =
    isMobileLayout && typeof navigator.share === "function";

  // Dragging a window narrow swaps the rail for the sheet, which has no Phone
  // section — so a rail left pointing at it would open the sheet on nothing.
  useEffect(() => {
    if (isMobileLayout && railSection === "phone") setRailSection("room");
  }, [isMobileLayout, railSection]);

  // The phone QR's pairing (COP-17). Asked for only when the Phone section is
  // actually open — most hosts never look at it, and each one is a 24-hour
  // capability for this deck that is better not minted for nobody. Refreshed
  // when it is within an hour of expiring, so a laptop left open all day keeps
  // showing a QR that works. Until it arrives (or if it fails) the QR is the
  // plain controller URL, which still works for a host willing to sign in.
  const [phonePairing, setPhonePairing] = useState<{
    token: string;
    expiresAt: number;
  } | null>(null);
  useEffect(() => {
    if (!isHostMode || !deckId || pairedDevice || isMobileLayout) return;
    if (railSection !== "phone" || deckResolveStatus !== "ready") return;
    if (phonePairing && phonePairing.expiresAt - Date.now() > 60 * 60 * 1000) {
      return;
    }
    let cancelled = false;
    void requestDevicePairing(deckId).then((issued) => {
      if (!cancelled && issued) {
        setPhonePairing(issued);
        captureUi("phone_pairing_issued");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHostMode, deckId, pairedDevice, isMobileLayout, railSection, deckResolveStatus, phonePairing]);

  // --- Notes scroll affordance ------------------------------------------
  // The notes card scrolls (overflow-y: auto). Historical notes routinely
  // overflow the visible box (median ~470 chars, p90 ~1100), so show a bottom
  // fade + "More ↓" hint whenever there's content below the fold, and hide it
  // once the presenter scrolls to the end.
  const notesCardRef = useRef<HTMLDivElement | null>(null);
  const [notesHasMore, setNotesHasMore] = useState(false);

  const recomputeNotesOverflow = useCallback(() => {
    const el = notesCardRef.current;
    if (!el) {
      setNotesHasMore(false);
      return;
    }
    // 8px slack so sub-pixel rounding doesn't leave the hint stuck on.
    setNotesHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  }, []);

  // New slide → start its note at the top rather than inheriting the previous
  // slide's scroll position.
  useEffect(() => {
    if (notesCardRef.current) notesCardRef.current.scrollTop = 0;
  }, [currentNote]);

  // Recompute when the content, font size, or layout (viewport / preview
  // presence) changes, and on window resize.
  useEffect(() => {
    recomputeNotesOverflow();
    window.addEventListener("resize", recomputeNotesOverflow);
    return () => window.removeEventListener("resize", recomputeNotesOverflow);
  }, [
    recomputeNotesOverflow,
    currentNote,
    fontScale,
    showNotesPanel,
    currentSlideHasNote,
    showPreview,
    isMobileLayout,
  ]);

  // True only when the preview is actually rendered (image present AND not
  // hidden by the mobile breakpoint). Drives the controls' size decision below.
  const previewActuallyVisible = showPreview && !isMobileLayout;

  // Thumbnails of the neighbouring slides, inside PREV/NEXT. Every viewport: on
  // a phone they stand in for the preview panel there's no room for, on desktop
  // they flank it to make prev · current · next.
  //
  // deckHasPreview, not showPreview: showPreview needs an image for the CURRENT
  // slide, and these buttons want the NEIGHBOURING ones. A deck mid-preload can
  // easily have slide 3 and not slide 2.
  //
  // Not in the presenter view, which shows the next slide full size beside the
  // current one. A second, smaller copy of it inside NEXT would be noise.
  const navThumbsVisible = previewVisible && deckHasPreview && !presenterView;

  // Only the phone trades away half its notes for them. Desktop has the room to
  // show both at full size, so nothing there needs to give anything up.
  const notesCapHalf = isMobileLayout && navThumbsVisible;

  // Mobile: warm the slides one step BEYOND the two on screen, so tapping NEXT
  // swaps in a thumbnail that's already in cache instead of leaving an empty box
  // for a beat on every single move. Four images around the cursor, not the
  // whole deck — the bulk warm stays off on phones (see warmBrowserCache).
  useEffect(() => {
    if (!isMobileLayout || !previewVisible) return;
    for (const i of [
      currentSlide - 2,
      currentSlide - 1,
      currentSlide + 1,
      currentSlide + 2,
    ]) {
      const url = slideImages.get(i);
      if (url) warmBrowserCache(url, true);
    }
  }, [isMobileLayout, previewVisible, currentSlide, slideImages, warmBrowserCache]);
  // Default to the big-button layout — the nav owns the centerpiece unless
  // notes or a real preview is there to take it. Means no compact-to-big
  // flash on page load (the previous version waited 1.5s before promoting);
  // signed-in users see the buttons shrink once when the first image arrives,
  // which is a single, expected transition, not a delayed surprise.
  const controlsPrimary = !showNotesPanel && !previewActuallyVisible;

  // Both panels showing — opts into the wide-desktop side-by-side variant.
  // The actual layout swap is media-query-gated inside NotesArea/PreviewArea
  // (kicks in only ≥1200px); below that, the prop is harmless and the panels
  // still stack in the column flow. Suppressed in host mode: the host layout's
  // right rail (share/lock/QR) already occupies that space, so notes stack
  // below the preview in the centre column instead of popping out to the side.
  const sideBySide = showNotesPanel && previewActuallyVisible && !isHostMode;

  // Keyboard shortcuts (desktop). Space / ArrowRight / PageDown → next,
  // ArrowLeft / PageUp → prev. Ignored while user is typing in the name prompt.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        moveNext();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        movePrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moveNext, movePrev]);

  // Swipe navigation (mobile). Swipe LEFT advances, swipe RIGHT goes back —
  // matches photo-gallery / stories convention. Vertical scroll in the notes
  // card is not hijacked because react-swipeable axis-locks to the dominant
  // initial direction.
  //
  // A swipe that starts ON a PREV/NEXT button can also produce a synthetic
  // click on it: the browser only reliably suppresses that after a scroll, and
  // nothing here scrolls. Left alone, one swipe would move two slides, or —
  // swiping left off PREV — move forward and straight back. So each swipe is
  // stamped and the buttons ignore a click that lands right behind one.
  const lastSwipeAt = useRef(0);
  const swipeConfig = {
    onSwipedLeft: () => {
      lastSwipeAt.current = Date.now();
      moveNext();
    },
    onSwipedRight: () => {
      lastSwipeAt.current = Date.now();
      movePrev();
    },
    delta: 60,
    trackMouse: false,
    preventScrollOnSwipe: false,
  };
  const swipeHandlers = useSwipeable(swipeConfig);
  // A SECOND instance rather than spreading one handler object onto two
  // elements: useSwipeable hands back a single ref callback, so attaching it in
  // two places leaves only the last-mounted element tracked — and the notes
  // panel would quietly stop responding.
  const controlsSwipeHandlers = useSwipeable(swipeConfig);
  // And a third for the stage view.
  const stageSwipeHandlers = useSwipeable(swipeConfig);

  // 300ms: long enough to cover the synthetic click that trails a touchend,
  // short enough that a deliberate tap after a swipe still registers.
  const isClickFromSwipe = () => Date.now() - lastSwipeAt.current < 300;

  const decreaseFont = () => {
    captureUi("font_scale_changed", {
      direction: "decrease",
      scale: nextFontScale(fontScale, -1),
    });
    setFontScale((prev) => {
      const v = nextFontScale(prev, -1);
      window.localStorage.setItem(FONT_SCALE_KEY, String(v));
      return v;
    });
  };
  const increaseFont = () => {
    captureUi("font_scale_changed", {
      direction: "increase",
      scale: nextFontScale(fontScale, 1),
    });
    setFontScale((prev) => {
      const v = nextFontScale(prev, 1);
      window.localStorage.setItem(FONT_SCALE_KEY, String(v));
      return v;
    });
  };

  const chooseMobileView = (view: "slide" | "notes") => {
    if (view === mobileView) return;
    captureUi("mobile_view_changed", { view, presenter: isPresenterShape });
    setMobileView(view);
    window.localStorage.setItem(MOBILE_VIEW_KEY, view);
  };

  const toggleNotesVisible = () => {
    captureUi("notes_toggled", { visible: !notesVisible, source: "toggle" });
    setNotesVisible((prev) => {
      const next = !prev;
      window.localStorage.setItem(NOTES_VISIBLE_KEY, String(next));
      return next;
    });
  };

  // The touch layout's "Live previews" menu tile (previewTile). Its jobs
  // used to belong to the header's Preview pill, which the touch layout no
  // longer has. Once previews are showing, it is their on/off switch (COP-30).
  //
  // It sells Pro to a free host: the desktop preview PANEL, where a free host
  // meets the pitch, doesn't exist in the touch layout, and without this a free
  // host on a phone never learned the feature existed.
  // Not when the extension is uploading this deck's previews: they're free.
  // Nothing left to sell here since COP-33: previews are on every plan.
  const previewMenuSellsPro = false;

  // And it starts the drive.file grant for an entitled host whose previews
  // aren't on yet.
  // Without it the grant is two levels down (Menu → Copresent → the nudge),
  // which is where the original "I click Preview on mobile and nothing happens"
  // report came from.
  const previewMenuGrants =
    isMobileLayout &&
    isHostMode &&
    !deckHasPreview &&
    previewOn !== true;

  // Desktop's Preview pill. Flips from what's on screen NOW, not from the
  // stored pref — the two differ whenever the plan default is in play, and
  // toggling off something already closed would look broken.
  //
  // Still writes the store on free: it's inert while they're free (rule 2 above
  // ignores it) and becomes their preference the moment they're Pro.
  const togglePreviewVisible = () => {
    const next = !previewVisible;
    captureUi("preview_toggled", { visible: next, was_default: previewChoice === null });
    setPreviewChoice(next);
    window.localStorage.setItem(PREVIEW_VISIBLE_KEY, String(next));
  };

  // The touch layout's Preview switch. Off hides the PREV/NEXT thumbnails and
  // the slide view, which gives the notes their full height back.
  const toggleMobilePreview = () => {
    const next = !previewVisible;
    captureUi("preview_toggled", { visible: next, source: "mobile_menu" });
    setMobilePreviewOn(next);
    window.localStorage.setItem(MOBILE_PREVIEW_VISIBLE_KEY, String(next));
  };

  // One tile of the touch layout's menu (see MenuGrid). The label is the
  // accessible name, kept exact so a screen reader hears "Q&A", not "Q&A 3
  // waiting 3"; the caption is attached as its description instead.
  const menuTile = (t: {
    id: string;
    label: string;
    Icon: () => JSX.Element;
    color: string;
    onClick: () => void;
    caption?: string;
    captionTone?: "accent" | "warn" | "over";
    // Set for a switch, which then reads as on/off by its colour.
    switchOn?: boolean;
    badge?: number;
  }) => {
    const captionId = `menu-tile-${t.id}-caption`;
    return (
      <MenuTile
        key={t.id}
        type="button"
        onClick={t.onClick}
        role={t.switchOn === undefined ? undefined : "switch"}
        aria-checked={t.switchOn}
        aria-label={t.label}
        aria-describedby={t.caption ? captionId : undefined}
      >
        <MenuTileIcon $color={t.color} $off={t.switchOn === false} aria-hidden="true">
          <t.Icon />
          {t.badge ? (
            <MenuTileBadge>{t.badge > 9 ? "9+" : t.badge}</MenuTileBadge>
          ) : null}
        </MenuTileIcon>
        <MenuTileLabel aria-hidden="true">{t.label}</MenuTileLabel>
        {t.caption && (
          <MenuTileCaption id={captionId} $tone={t.captionTone}>
            {t.caption}
          </MenuTileCaption>
        )}
      </MenuTile>
    );
  };

  const previewTile = previewMenuSellsPro
    ? menuTile({
        id: "previews",
        label: "Live previews",
        Icon: PreviewIcon,
        color: MENU_COLORS.previews,
        caption: "Pro",
        captionTone: "accent",
        onClick: () => openProPaywall("preview_mobile"),
      })
    : previewMenuGrants
    ? menuTile({
        id: "previews",
        label: "Live previews",
        Icon: PreviewIcon,
        color: MENU_COLORS.previews,
        caption: "Turn on",
        captionTone: "accent",
        onClick: () => enableLivePreviews("preview_menu"),
      })
    : isMobileLayout && deckHasPreview
    ? menuTile({
        id: "previews",
        label: "Live previews",
        Icon: PreviewIcon,
        color: MENU_COLORS.previews,
        switchOn: previewVisible,
        caption: previewVisible ? "On" : "Off",
        onClick: toggleMobilePreview,
      })
    : null;

  const toggleTheme = () => {
    captureUi("theme_toggled", { theme: theme === "dark" ? "light" : "dark" });
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      window.localStorage.setItem(THEME_KEY, next);
      return next;
    });
  };

  // The verified-host three-zone shell is active whenever we're in host mode and
  // the backend confirmed us as host. hostNeedsSignIn and the copresenter path
  // keep the original single-column layout.
  const hostShellActive = isHostMode && youAreHost;

  // Slide-count label. While waiting to start there's no current slide to point
  // at, so show the deck size ("44 slides"); once live, show position in the
  // deck ("Slide 3 of 44"). Null until we know the total.
  const slideCountLabel = presentation?.totalSlides
    ? status === PresentationStatus.ONLINE
      ? `Slide ${currentSlide} of ${presentation.totalSlides}`
      : `${presentation.totalSlides} slides`
    : null;

  // The same switch as a menu ROW, for the phone's hamburger. An IconButton in a
  // list of labelled items is a glyph nobody can name — and a menu has the room
  // to spell it out.
  //
  // A SWITCH, not a button whose label flips. A flipping label has to be read
  // twice to be understood — "Light mode" is either what you are in or what you
  // would get, and only the icon beside it hints which. A switch states one
  // setting and shows whether it is on, which is the same shape every other
  // preference in every other app on the phone has.
  const themeTile = menuTile({
    id: "theme",
    label: "Dark mode",
    Icon: MoonIcon,
    color: MENU_COLORS.theme,
    switchOn: theme === "dark",
    caption: theme === "dark" ? "On" : "Off",
    onClick: toggleTheme,
  });

  const themeToggle = (
    <IconButton
      type="button"
      onClick={toggleTheme}
      aria-pressed={theme === "dark"}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    >
      {theme === "dark" ? <MoonIcon /> : <SunIcon />}
    </IconButton>
  );

  // Notes / preview visibility toggles — shared by the copresenter header and
  // the host header.
  // On desktop host, notes live in the right rail controlled by its divider tab,
  // so the header notes toggle would be a dead control there — hide it. It stays
  // for the copresenter layout and mobile host (where notes are in the centre).
  // The Slide | Notes switch replaces it wherever there's a slide to switch to.
  const showHeaderNotesToggle =
    deckHasNotes && !(isHostMode && !isMobileLayout) && !showViewSwitch;
  // A host always gets the chip (desktop only, see showPreviewPill): it governs
  // the preview PANEL, which the host shell always has a slot for. A
  // copresenter earns it once an image arrives.
  const showHeaderPreviewToggle = isHostMode || deckHasPreview;

  // --- Presentation timer -------------------------------------------------

  // Each side reads its own entitlement, and they are genuinely different
  // questions. The host asks "am I on Pro right now" — isProNow. A copresenter asks "is
  // this room on Pro", which is the only plan fact they're given.
  //
  // Tri-state on both, and `=== true` rather than truthy: undefined means the
  // backend hasn't said, and a locked timer flashed at a paying host every
  // reconnect is worse than a control that appears a beat late.
  //
  // Superseded by COP-33: the timer is on every plan, for host and copresenter.
  const timerEntitled = true;
  const timerLocked = false;

  // The timer sits in its own row under the toolbar on BOTH viewports, revealed
  // by a pill beside Notes and Preview. Desktop had the width to keep the
  // cluster inline permanently, but one behaviour everywhere beats a saved tap:
  // a host who runs the controller on a laptop and a paired phone shouldn't
  // have to learn the control twice.
  //
  // Default OFF. Space is contested by notes, preview and the nav controls, so
  // a timer nobody asked for shouldn't claim a row; the labelled pill is the
  // discovery, and it doesn't need to be open to be found.
  const [timerVisible, setTimerVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("copresent.timer.visible") === "1";
  });
  useEffect(() => {
    window.localStorage.setItem("copresent.timer.visible", timerVisible ? "1" : "0");
  }, [timerVisible]);

  // The live value, lifted out of the timer so the collapsed pill can render it.
  // Without this, hiding the row would hide a RUNNING clock, and the honest
  // alternatives are both bad: pause on collapse (surprising, destroys the run)
  // or refuse to collapse while running (a disabled control with no
  // explanation). Instead the pill becomes the readout and collapsing is a
  // deliberate compact mode.
  const [timerReadout, setTimerReadout] = useState<{
    remainingSeconds: number;
    running: boolean;
    dirty: boolean;
    tone: "default" | "warn" | "over";
  } | null>(null);

  const onTimerTick = useCallback(
    (state: {
      remainingSeconds: number;
      running: boolean;
      dirty: boolean;
      tone: "default" | "warn" | "over";
    }) => setTimerReadout(state),
    []
  );

  // Entitlement is read when the timer STARTS, not continuously. Once a run
  // exists it finishes, whatever the plan does underneath.
  //
  // This matters because it isn't hypothetical: a copresenter re-JOINs on every
  // socket reconnect, and a host's plan can lapse mid-talk. Without the latch,
  // the clock someone is pacing a live presentation to vanishes at the least
  // recoverable possible moment — worse than never having offered it.
  //
  // The readout IS the latch; no extra state. While `dirty` holds, the cluster
  // stays mounted, so it keeps ticking and keeps reporting `dirty` — and reset
  // ends both together. A stale run in localStorage can't resurrect it, because
  // an unentitled timer never mounts to report anything in the first place.
  const timerRunLatched = timerReadout?.dirty === true;

  // What every timer control actually keys off. Entitled, or already running.
  const timerUsable = timerEntitled || timerRunLatched;

  const showTimerToggle = timerUsable || timerLocked;

  const toggleTimer = () => {
    if (!timerUsable) {
      openProPaywall("timer");
      return;
    }
    const next = !timerVisible;
    setTimerVisible(next);
    // `running` matters as much as `visible`: collapsing a RUNNING timer is the
    // compact mode, collapsing an idle one is dismissal. Same button, opposite
    // meanings — counted together they'd both read as rejection.
    captureUi("timer_toggled", {
      visible: next,
      running: Boolean(timerReadout?.running),
    });
  };

  // Touch layout: the timer's switch lives in the menu, not the header (see
  // headerToolbar). While it's hidden and running, the row shows the time left,
  // which is what the header pill used to do.
  const timerTile = !showTimerToggle
    ? null
    : timerUsable
    ? menuTile({
        id: "timer",
        label: "Timer",
        Icon: TimerIcon,
        color: MENU_COLORS.timer,
        switchOn: timerVisible,
        // A running clock is the one thing worth reading off the tile.
        caption: timerReadout?.running
          ? `${formatRemaining(timerReadout.remainingSeconds)} left`
          : timerVisible
          ? "On"
          : "Off",
        captionTone:
          timerReadout?.running && timerReadout.tone !== "default"
            ? timerReadout.tone
            : undefined,
        onClick: toggleTimer,
      })
    : menuTile({
        id: "timer",
        label: "Timer",
        Icon: TimerIcon,
        color: MENU_COLORS.timer,
        caption: "Pro",
        captionTone: "accent",
        onClick: toggleTimer,
      });

  // magicId is null until the host's deck resolves. No session means no key to
  // scope the persisted run to, and nothing worth timing yet.
  const timerCluster = timerUsable && magicId ? (
    <PresentationTimer
      magicId={magicId}
      fill={isMobileLayout}
      onTick={onTimerTick}
      onEvent={captureUi}
    />
  ) : null;
  // Its own row now, so an empty Toolbar would leave a 10px gap under the
  // status for nothing. Render nothing at all when neither toggle applies.
  // Touch layout: no Preview or Timer pills. The timer's switch is in the menu
  // (timerTile), and Preview is dropped: with the Slide | Notes switch and
  // the slide view, hiding the slide is a choice the header no longer needs to
  // offer. Turning previews on, or the Pro offer, stay reachable from the menu
  // (the Copresent section's previews nudge, the account's upgrade).
  const showPreviewPill = showHeaderPreviewToggle && !isMobileLayout;
  // A copresenter's menu (touch layout only, see MobileMenuSlot) exists when it
  // has something in it: live chat, or the timer that left the header.
  const copresenterHasMenu = chatEnabled || showTimerToggle;
  const showTimerPill = showTimerToggle && !isMobileLayout;
  const headerToolbar = (showViewSwitch ||
    showHeaderNotesToggle ||
    showPreviewPill ||
    showTimerPill) && (
    <Toolbar>
      {showViewSwitch && (
        <ViewSwitch role="group" aria-label="Show">
          <ViewSwitchOption
            type="button"
            aria-pressed={mobileShowsSlide}
            onClick={() => chooseMobileView("slide")}
          >
            <PreviewIcon />
            Slide
          </ViewSwitchOption>
          <ViewSwitchOption
            type="button"
            aria-pressed={!mobileShowsSlide}
            onClick={() => chooseMobileView("notes")}
          >
            <NotesIcon />
            Notes
          </ViewSwitchOption>
        </ViewSwitch>
      )}
      {showHeaderNotesToggle && (
        <ViewToggle
          type="button"
          onClick={toggleNotesVisible}
          aria-pressed={notesVisible}
          title={notesVisible ? "Hide notes" : "Show notes"}
        >
          <NotesIcon />
          Notes
        </ViewToggle>
      )}
      {showPreviewPill && (
        <ViewToggle
          type="button"
          onClick={togglePreviewVisible}
          aria-pressed={previewVisible}
          title={previewVisible ? "Hide preview" : "Show preview"}
        >
          <PreviewIcon />
          Preview
        </ViewToggle>
      )}
      {/* Last in the row on purpose, on both viewports. Notes and Preview are
          existing muscle memory, and inserting ahead of them moves buttons
          under a thumb already reaching for one, mid-presentation. */}
      {showTimerPill && (
        <ViewToggle
          type="button"
          onClick={toggleTimer}
          /* No aria-pressed when it sells Pro rather than toggling — the same
             rule the preview chip follows. Announcing "not pressed" would
             promise a state this button can't reach. */
          aria-pressed={timerUsable ? timerVisible : undefined}
          title={
            !timerUsable
              ? "Presentation timer (Copresent Pro)"
              : timerVisible
              ? "Hide timer"
              : "Show timer"
          }
        >
          {timerUsable ? <TimerIcon /> : <LockIcon />}
          {/* A running timer keeps its value on the pill while the row is
              hidden, so collapsing is a compact mode rather than a way to lose
              the clock you're pacing to. */}
          {timerUsable && !timerVisible && timerReadout?.running ? (
            <PillTime $tone={timerReadout.tone}>
              {formatRemaining(timerReadout.remainingSeconds)}
            </PillTime>
          ) : (
            "Timer"
          )}
        </ViewToggle>
      )}
    </Toolbar>
  );

  // HIDDEN with CSS rather than unmounted, which is the whole reason the pill
  // can be a readout. Unmounting takes the running clock with it: the component
  // owns the tick, so nothing would be left to report a value while collapsed,
  // and the pill would fall back to the word "Timer" over a live run. The run
  // itself survives in localStorage either way — what's lost is the display,
  // which is exactly what the compact mode is for.
  //
  // display:none also drops the buttons from the accessibility tree, so there's
  // no ghost control to tab into.
  const timerRow = timerCluster ? (
    <TimerRow $hidden={!timerVisible}>{timerCluster}</TimerRow>
  ) : null;

  // The presenting column — preview, notes, PREV/NEXT. Shared verbatim between
  // the copresenter layout and the host shell's centre column.
  // Notes / preview / controls extracted as small renderers so the copresenter
  // layout (notes stacked/side-by-side inside the centre) and the host layout
  // (notes in their own right rail) can compose them with different flags.
  const renderNotesPanel = (alongPreview: boolean) => (
    <NotesArea
      $alongPreview={alongPreview}
      $capHalf={notesCapHalf}
      {...swipeHandlers}
    >
      {currentSlideHasNote ? (
        <>
          <NotesViewport>
            <NotesCard
              ref={notesCardRef}
              onScroll={recomputeNotesOverflow}
              $scale={fontScale}
            >
              <FontControls>
                <FontButton
                  type="button"
                  onClick={decreaseFont}
                  disabled={fontScale <= MIN_FONT_SCALE + 1e-6}
                  aria-label="Decrease notes text size"
                >
                  A−
                </FontButton>
                <FontButton
                  type="button"
                  onClick={increaseFont}
                  disabled={fontScale >= MAX_FONT_SCALE - 1e-6}
                  aria-label="Increase notes text size"
                >
                  A+
                </FontButton>
              </FontControls>
              {currentNote}
            </NotesCard>
            {notesHasMore && (
              <ScrollFade aria-hidden="true">
                <MoreHint>More ↓</MoreHint>
              </ScrollFade>
            )}
          </NotesViewport>
        </>
      ) : (
        <EmptyNotesCard>No notes for this slide</EmptyNotesCard>
      )}
    </NotesArea>
  );

  const renderPreview = (primary: boolean, alongNotes: boolean) => {
    const previewSrc = shownImage(currentSlide, previewSources) || currentSlideImage;
    // Key "offline" on the actual SOCKET, not the presentation status: pre-start
    // the deck is NOT_STARTED but the socket is open and the backend now renders
    // slide 1, so a waiting copresenter should SEE it — not a "Reconnecting…"
    // card. Only a genuinely dropped socket falls back. (The header's connection
    // status still surfaces a real disconnect.)
    const wsOffline = readyState !== ReadyState.OPEN;
    const imageBroken = !!previewSrc && previewSrc === failedPreviewSrc;
    const showFallback = wsOffline || imageBroken;
    return (
      <PreviewArea
        $primary={primary}
        $alongNotes={alongNotes}
        aria-label="Current slide preview"
      >
        <SlideCard $empty={showFallback}>
          {showFallback ? (
            <PreviewFallback role="status" aria-live="polite">
              <strong>
                {wsOffline ? "Reconnecting…" : "Preview unavailable"}
              </strong>
              <span>
                {wsOffline
                  ? "We'll refresh the slide as soon as you're back online."
                  : "The slide image couldn't be loaded — we'll retry on the next slide change."}
              </span>
            </PreviewFallback>
          ) : (
            <img
              src={previewSrc}
              alt={`Slide ${currentSlide}`}
              draggable={false}
              referrerPolicy="no-referrer"
              onError={() => {
                setFailedPreviewSrc(previewSrc);
                reportImageFailure(currentSlide, previewSrc);
              }}
              onLoad={() => {
                if (failedPreviewSrc) setFailedPreviewSrc(null);
              }}
            />
          )}
        </SlideCard>
      </PreviewArea>
    );
  };

  // The slide either side of you, shown inside its own nav button.
  //
  // Costs nothing to fetch: PreloadAllSlidesAsync already renders the whole deck
  // and broadcasts every slide as PRELOAD_SLIDE_IMAGE, and warmBrowserCache has
  // already pulled each into the browser cache — so these are the same URLs the
  // desktop preview uses, served from cache.
  //
  // Plan gating is inherited rather than re-implemented: a free room is sent no
  // images at all, so the map stays empty and the buttons quietly render exactly
  // as they did before. Deliberately NO upgrade prompt in here — this is the one
  // control someone taps under time pressure mid-presentation, and the pitch
  // already has two homes (the preview panel and the rail).
  // `target` is what that tap will show (slideSteps.ts): on a slide with click
  // animations, the next or previous step of the same slide.
  const renderNavThumb = (target: { slide: number; image?: string } | null) => {
    if (!navThumbsVisible || !target) return null;
    const { slide: slideIndex, image: src } = target;
    // Its OWN failure set, deliberately not the main preview's failedPreviewSrc.
    // That's a single value, so two panels writing to it would take turns
    // clearing each other's failure — each clear re-renders the other's broken
    // URL, which re-fails, which clears back. An unbounded render loop from a
    // 404 thumbnail.
    const usable = !!src && !failedThumbs.has(src);
    return (
      // Reserved whether or not there's an image — see NavThumb. aria-hidden
      // because the button's own label already names the action; the thumbnail
      // is a visual aid, and "Slide 4" read out inside "PREV" is noise.
      <NavThumb aria-hidden="true">
        {usable && (
          <NavThumbImg
            src={src}
            alt=""
            draggable={false}
            referrerPolicy="no-referrer"
            onError={() => {
              setFailedThumbs((prev) => new Set(prev).add(src));
              reportImageFailure(slideIndex, src);
            }}
          />
        )}
      </NavThumb>
    );
  };

  // The buttons are the biggest touch target on the screen, so a swipe aimed at
  // the deck usually starts on one of them — and did nothing, because the
  // gesture only lived on the notes panel. Now the whole control block swipes.
  // Neither stops at an end of the deck: the first and last slides can still
  // have animations to reverse or play, which the clicker can't see (COP-92).
  const prevDisabled = status !== PresentationStatus.ONLINE || controlBlocked;
  const nextDisabled = prevDisabled;

  const renderControls = (primary: boolean, fill: boolean, wide = false) => (
    <Controls
      $primary={primary}
      $fill={fill}
      $wide={wide}
      {...controlsSwipeHandlers}
    >
      <NavButton
        disabled={prevDisabled}
        onClick={() => {
          if (isClickFromSwipe()) return;
          movePrev();
        }}
      >
        {renderNavThumb(afterPrev(currentSlide, previewSources))}
        PREV
      </NavButton>
      <NavButton
        disabled={nextDisabled}
        onClick={() => {
          if (isClickFromSwipe()) return;
          moveNext();
        }}
      >
        {renderNavThumb(afterNext(currentSlide, presentation?.totalSlides, previewSources))}
        NEXT
      </NavButton>
    </Controls>
  );

  // --- Touch layout: slide views ------------------------------------------
  // Whether WE put the page in browser full screen, so that leaving the stage
  // can undo exactly that and never a full screen the user entered themselves.
  const stageOwnsFullscreen = useRef(false);

  const openStage = () => {
    captureUi("stage_view_opened", { source: "button" });
    setStageRequested(true);
    stageOwnsFullscreen.current = true;
    // Hides the browser's own toolbars where the browser allows it (Android,
    // iPad). An iPhone doesn't for a page, and the stage still covers the
    // viewport there, so a refusal is not an error worth surfacing.
    const root = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => void;
    };
    try {
      if (root.requestFullscreen) root.requestFullscreen().catch(() => {});
      else root.webkitRequestFullscreen?.();
    } catch {
      // Not supported here.
    }
  };

  const closeStage = useCallback(() => {
    setStageRequested(false);
    stageOwnsFullscreen.current = false;
    // A phone held sideways would reopen the stage straight away without this.
    // Only while it IS sideways: closing an expanded stage upright must not
    // stop the next rotation from opening it.
    if (isPhoneLandscape) setStageDismissed(true);
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => void;
    };
    if (doc.fullscreenElement) doc.exitFullscreen().catch(() => {});
    else if (doc.webkitFullscreenElement) doc.webkitExitFullscreen?.();
  }, [isPhoneLandscape]);

  // Leaving full screen the browser's way (the system back gesture, Esc on an
  // iPad keyboard) leaves the stage too. Otherwise the exit would take two
  // steps, the first of which appears to do nothing.
  useEffect(() => {
    if (!stageRequested) return;
    const onChange = () => {
      const doc = document as Document & {
        webkitFullscreenElement?: Element | null;
      };
      if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
        setStageRequested(false);
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [stageRequested]);

  // The stage can also close without its exit button: the slide image goes
  // away (previews turned off, a plan lapsing). Full screen with no stage is
  // a screen with no way out on the page, so leave it too.
  useEffect(() => {
    if (stageOpen || !stageOwnsFullscreen.current) return;
    stageOwnsFullscreen.current = false;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => void;
    };
    if (doc.fullscreenElement) doc.exitFullscreen().catch(() => {});
    else if (doc.webkitFullscreenElement) doc.webkitExitFullscreen?.();
  }, [stageOpen]);

  useEffect(() => {
    if (!stageOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeStage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stageOpen, closeStage]);

  // The rotation opens it without a press, so it's counted here rather than in
  // a handler. Only the transition counts, not every render it stays open.
  const stageWasOpen = useRef(false);
  useEffect(() => {
    if (stageOpen && !stageWasOpen.current && !stageRequested) {
      captureUi("stage_view_opened", { source: "rotate" });
    }
    stageWasOpen.current = stageOpen;
    // captureUi is rebuilt every render; see the note on the session effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageOpen, stageRequested]);

  // The current slide, as large as the space between the header and PREV/NEXT
  // allows.
  const renderBigSlide = () => {
    const wsOffline = readyState !== ReadyState.OPEN;
    const imageBroken = bigSlideSrc === failedPreviewSrc;
    const showFallback = wsOffline || imageBroken;
    return (
      <BigSlideArea aria-label="Current slide">
        <BigSlideCard $empty={showFallback}>
          {showFallback ? (
            <PreviewFallback role="status" aria-live="polite">
              <strong>
                {wsOffline ? "Reconnecting…" : "Preview unavailable"}
              </strong>
              <span>
                {wsOffline
                  ? "We'll refresh the slide as soon as you're back online."
                  : "The slide image couldn't be loaded — we'll retry on the next slide change."}
              </span>
            </PreviewFallback>
          ) : (
            <>
              <img
                src={bigSlideSrc}
                alt={`Slide ${bigSlideIndex}`}
                draggable={false}
                referrerPolicy="no-referrer"
                onError={() => {
                  setFailedPreviewSrc(bigSlideSrc);
                  reportImageFailure(bigSlideIndex, bigSlideSrc);
                }}
                onLoad={() => {
                  if (failedPreviewSrc) setFailedPreviewSrc(null);
                }}
              />
              <ExpandButton
                type="button"
                onClick={openStage}
                aria-label="Show slide full screen"
                title="Full screen"
              >
                <ExpandIcon />
              </ExpandButton>
            </>
          )}
          {/* The host's own reminder that the room is looking at a join code,
              as on the desktop preview. */}
          {isHostMode && !showFallback && qaRoom?.showingCode && (
            <QaOnDeck
              role="img"
              aria-label={`Join code ${qaRoom.code} is showing on your deck`}
            >
              <QaOnDeckHint>Ask a question</QaOnDeckHint>
              <QRCodeSVG value={qaAudienceUrl(qaRoom.code)} size={40} />
              <QaOnDeckUrl>{`${qaAudienceBase}/${qaRoom.code}`}</QaOnDeckUrl>
            </QaOnDeck>
          )}
        </BigSlideCard>
      </BigSlideArea>
    );
  };

  // Next slide for the presenter view. Its own failure handling for the reason
  // renderNavThumb gives: sharing failedPreviewSrc with the current slide would
  // have the two panels clearing each other's failures in a loop.
  const renderNextSlide = () => {
    // What the next tap shows: on a slide with click animations left, its own
    // next step (slideSteps.ts).
    const next = afterNext(bigSlideIndex, presentation?.totalSlides, previewSources);
    const atEnd = !next;
    const nextIndex = next?.slide ?? bigSlideIndex + 1;
    const src = next?.image;
    const usable = !atEnd && !!src && !failedThumbs.has(src);
    return (
      <PresenterNext>
        <PresenterNextLabel>
          {atEnd ? "End of deck" : `Next · slide ${nextIndex}`}
        </PresenterNextLabel>
        <PresenterNextCard $empty={!usable}>
          {usable && (
            <img
              src={src}
              alt={`Slide ${nextIndex}`}
              draggable={false}
              referrerPolicy="no-referrer"
              onError={() => {
                setFailedThumbs((prev) => new Set(prev).add(src));
                reportImageFailure(nextIndex, src);
              }}
            />
          )}
        </PresenterNextCard>
      </PresenterNext>
    );
  };

  // The slide view, or the presenter view when the device is a tablet on its
  // side. Replaces the whole presenting column, PREV/NEXT included, since the
  // presenter view moves the controls under the slide.
  const renderSlideView = () =>
    presenterView ? (
      <PresenterGrid>
        <PresenterMain>
          {renderBigSlide()}
          {renderControls(false, true, true)}
        </PresenterMain>
        <PresenterSide aria-label="Up next">
          {renderNextSlide()}
          {deckHasNotes && renderNotesPanel(false)}
        </PresenterSide>
      </PresenterGrid>
    ) : (
      <SlideViewColumn>
        {renderBigSlide()}
        {renderControls(false, true, true)}
      </SlideViewColumn>
    );

  // Stage view. Rendered alongside the normal layout rather than instead of it,
  // so closing it puts the speaker back exactly where they were, notes scroll
  // position included.
  const stage = stageOpen ? (
    <Stage role="dialog" aria-modal="true" aria-label="Slide view">
      <StageHeader>
        <StageHeading>
          {presentation?.title && <Title>{presentation.title}</Title>}
          <MetaRow>
            {slideCountLabel && <SlideCount>{slideCountLabel}</SlideCount>}
            <ConnectionStatus status={status} />
            {controlBlocked && <span>· View only</span>}
          </MetaRow>
        </StageHeading>
        {timerUsable && timerReadout?.running && (
          <StageTimer>
            <PillTime $tone={timerReadout.tone}>
              {formatRemaining(timerReadout.remainingSeconds)}
            </PillTime>
          </StageTimer>
        )}
        <StageExit
          type="button"
          onClick={closeStage}
          aria-label="Exit full screen"
          title="Exit full screen"
        >
          <CloseIcon />
        </StageExit>
      </StageHeader>
      <StageBody {...stageSwipeHandlers}>
        <StageSlide
          src={bigSlideSrc}
          alt={`Slide ${bigSlideIndex}`}
          draggable={false}
          referrerPolicy="no-referrer"
        />
        <StageTapZone
          type="button"
          $side="prev"
          aria-label="Previous slide"
          disabled={prevDisabled}
          onClick={() => {
            if (isClickFromSwipe()) return;
            movePrev();
          }}
        />
        <StageTapZone
          type="button"
          $side="next"
          aria-label="Next slide"
          disabled={nextDisabled}
          onClick={() => {
            if (isClickFromSwipe()) return;
            moveNext();
          }}
        />
      </StageBody>
    </Stage>
  ) : null;

  // Copresenter presenting column — notes (stacked or side-by-side), preview,
  // controls, all in one centre column.
  const presentingBody = (
    <BodyArea>
      {mobileShowsSlide ? (
        renderSlideView()
      ) : (
        <>
          {showNotesPanel && renderNotesPanel(sideBySide)}
          {showPreview && !isMobileLayout && renderPreview(previewPrimary, sideBySide)}
          {/* $fill on mobile too when the thumbnails are up: notes has capped
              itself at half, and without this the nav sits at its natural ~120px
              and leaves the rest of the column blank. */}
          {renderControls(controlsPrimary, previewActuallyVisible || navThumbsVisible)}
        </>
      )}
    </BodyArea>
  );

  const keyboardHint = (
    <KeyboardHint aria-hidden="true">
      Tip: use the <kbd>←</kbd> <kbd>→</kbd> arrow keys to switch slides
    </KeyboardHint>
  );


  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // The invite token, not the magicId — so "New link" can revoke this without
  // touching the session. Falls back to the magicId when the backend couldn't
  // issue a token: a link that can't be revoked still beats no link at all, and
  // the join path accepts both.
  const joinUrl = `${origin}/presentation/${inviteToken ?? magicId}`;
  // Host controller's own URL (the QR to open these controls on a phone) is
  // keyed to the DECK, not the rotating magic link, so it stays valid across
  // sessions. deckId is always set in host mode.
  //
  // Carries a pairing in its fragment once one has been issued, so scanning it
  // opens the controller on the phone with no sign-in (COP-17).
  const hostUrl = pairedHostUrl(origin, deckId ?? "", phonePairing?.token ?? null);

  // Host preview window. Unlike the copresenter's, this is ALWAYS on screen
  // (desktop) so the host sees the layout even before starting: it shows the
  // live slide once running, an enable-previews call to action when previews
  // aren't on, or a "waiting to start" placeholder otherwise.
  const renderHostPreview = (primary: boolean) => {
    // Before the slideshow starts currentSlide is 0 — show the FIRST slide's
    // thumbnail (once the deck has been rendered) so the host previews the
    // opening slide while waiting, mirroring the first-slide-note fallback.
    const previewIndex = currentSlide > 0 ? currentSlide : 1;
    const previewSrc = shownImage(previewIndex, previewSources) || currentSlideImage;
    const online = status === PresentationStatus.ONLINE;
    const imageBroken = !!previewSrc && previewSrc === failedPreviewSrc;
    // No plan check: previews are on every plan since COP-33.
    const hasImage = !!previewSrc && !imageBroken;
    return (
      <PreviewArea
        $primary={primary}
        $alongNotes={false}
        aria-label="Current slide preview"
      >
        <SlideCard $empty={!hasImage}>
          {hasImage ? (
            <img
              src={previewSrc}
              alt={`Slide ${previewIndex}`}
              draggable={false}
              referrerPolicy="no-referrer"
              onError={() => {
                setFailedPreviewSrc(previewSrc);
                reportImageFailure(previewIndex, previewSrc!);
              }}
              onLoad={() => {
                if (failedPreviewSrc) setFailedPreviewSrc(null);
              }}
            />
          ) : previewLoading && previewOn !== true ? (
            // The extension is drawing the deck (COP-19): previews are on their
            // way with no grant, so there's nothing to enable.
            <PreviewFallback role="status" aria-live="polite">
              <Spinner aria-hidden="true" />
              <strong>Loading slide previews…</strong>
              <span>Your slides will appear here in a few seconds.</span>
            </PreviewFallback>
          ) : (
            <PreviewFallback role="status" aria-live="polite">
              <strong>
                {online
                  ? "Loading slide…"
                  : previewOn === true
                  ? "Live previews on"
                  : previewOn === false
                  ? "Turn on live previews"
                  : "Waiting to start"}
              </strong>
              <span>
                {previewOn === true
                  ? online
                    ? "Your current slide will appear here in a moment."
                    : "Loading the first slide…"
                  : previewOn === false
                  ? "See your current slide here and let copresenters follow along."
                  : "Your slide preview appears here. Turn on live previews so copresenters can follow along."}
              </span>
              {/* Offer the enable action until previews are confirmed on
                  (previewOn === true, set by a PREVIEW_READY from the backend
                  once the deck is readable — which happens even before the
                  slideshow starts). Unknown (null, pre-start) or explicitly off
                  (false) both show it; true hides it. */}
              {previewOn !== true && (
                <HostActionButton
                  type="button"
                  style={{ alignSelf: "center", marginTop: 4 }}
                  onClick={() => enableLivePreviews("preview_panel")}
                >
                  Enable live previews
                </HostActionButton>
              )}
            </PreviewFallback>
          )}

          {/* Only over a real slide. Drawn on the fallback it would be a join
              code floating on "Turn on live previews", which says the room can
              see something the host cannot — and on the free branch it would
              decorate the paywall with the feature it is refusing. */}
          {hasImage && qaRoom?.showingCode && (
            <QaOnDeck
              role="img"
              aria-label={`Join code ${qaRoom.code} is showing on your deck`}
            >
              {/* The same three lines as the extension's card, in the same
                  order — a facsimile that drops one understates what the room
                  is being shown, which is the one thing it exists to report. */}
              <QaOnDeckHint>Ask a question</QaOnDeckHint>
              <QRCodeSVG value={qaAudienceUrl(qaRoom.code)} size={40} />
              <QaOnDeckUrl>{`${qaAudienceBase}/${qaRoom.code}`}</QaOnDeckUrl>
            </QaOnDeck>
          )}
        </SlideCard>
      </PreviewArea>
    );
  };

  // Host layout: notes live in their own right rail on desktop — shown by
  // default, toggled by the divider tab (not gated on the deck having notes, so
  // the panel is a consistent part of the layout, with an empty state pre-start).
  // On mobile there's no rail, so notes stack in the centre when the deck has
  // them. The preview window shows on desktop and honours the preview toggle
  // (hide it to give the controls the whole column).
  const hostNotesInCenter = showNotesPanel && isMobileLayout;
  const hostNotesInRail = !isMobileLayout && !notesCollapsed;
  // Every host gets the panel, free or not — the free one gets the offer in
  // place of the picture (see renderHostPreview).
  //
  // It used to be hidden outright, on the reasoning that a permanent upgrade
  // prompt mid-screen is nagging. What that actually bought was a Pro feature
  // nobody could see: the preview panel IS the pitch, and an empty column says
  // nothing. It stays honest because the header's preview toggle governs it and
  // the choice persists — a host who doesn't want the ask closes it once.
  const hostPreviewVisible = !isMobileLayout && previewVisible;
  const hostPreviewPrimary = !hostNotesInCenter;
  const hostControlsPrimary = !hostNotesInCenter && !hostPreviewVisible;

  const hostCenterBody = (
    <BodyArea>
      {mobileShowsSlide ? (
        renderSlideView()
      ) : (
        <>
          {hostNotesInCenter && renderNotesPanel(false)}
          {hostPreviewVisible && renderHostPreview(hostPreviewPrimary)}
          {renderControls(hostControlsPrimary, hostPreviewVisible || navThumbsVisible)}
        </>
      )}
    </BodyArea>
  );

  // Notes filling the right sidebar (a scrolling document, not a bordered card).
  const hostNotesRail = hostNotesInRail ? (
    <NotesRail aria-label="Speaker notes">
      <NotesRailHead>
        <HostLabel>Speaker notes</HostLabel>
        <NotesRailFonts>
          <FontButton
            type="button"
            onClick={decreaseFont}
            disabled={fontScale <= MIN_FONT_SCALE + 1e-6}
            aria-label="Decrease notes text size"
          >
            A−
          </FontButton>
          <FontButton
            type="button"
            onClick={increaseFont}
            disabled={fontScale >= MAX_FONT_SCALE - 1e-6}
            aria-label="Increase notes text size"
          >
            A+
          </FontButton>
        </NotesRailFonts>
      </NotesRailHead>
      {currentSlideHasNote ? (
        <NotesRailViewport>
          <NotesRailBody
            ref={notesCardRef}
            onScroll={recomputeNotesOverflow}
            $scale={fontScale}
          >
            {currentNote}
          </NotesRailBody>
          {notesHasMore && (
            <NotesRailFade aria-hidden="true">
              <MoreHint>More ↓</MoreHint>
            </NotesRailFade>
          )}
        </NotesRailViewport>
      ) : (
        <NotesRailEmpty>
          {status === PresentationStatus.ONLINE
            ? "No notes for this slide."
            : "Speaker notes appear here once you start the slideshow."}
        </NotesRailEmpty>
      )}
    </NotesRail>
  ) : null;

  // Left rail: the section switcher, the active section, and a footer that
  // outlives both.
  //
  // The QR and the account/upgrade block sit BELOW the sections rather than
  // inside one, because neither belongs to copresenting or to Q&A — pairing the
  // deck with a phone and the standing upgrade ask are true from wherever the
  // host happens to be looking, and burying the free plan's one standing ask
  // behind a tab would quietly retire it.
  // Where the account lives, which differs per surface — and must resolve to
  // exactly ONE of them, or the same name and address appear twice in the
  // document for a screen reader to read out.
  //
  //   host, desktop → the icon strip's avatar and plan pill
  //   host, phone   → the menu sheet (see HeadMenuButton)
  //   copresenter   → the panel itself, which is all they have
  // A paired phone (COP-17) drops the sections that need an account or make no
  // sense on it: Q&A runs on the account service, and a phone cannot pair a
  // phone. On a tablet the desktop rail would otherwise still offer both.
  const railSections = pairedDevice
    ? RAIL_SECTIONS.filter((s) => s.id === "room")
    : RAIL_SECTIONS;
  const mobileMenuSections = pairedDevice
    ? MOBILE_MENU_SECTIONS.filter((s) => s.id === "room")
    : MOBILE_MENU_SECTIONS;
  const stripCarriesAccount = isHostMode && !isMobileLayout;
  const menuCarriesAccount = isHostMode && isMobileLayout;

  // The pass sits at the foot of the Copresent tab only: the other tabs have
  // nothing to do with copresenters.
  const showPassFooter = freeCopresentPass != null && railSection === "room";

  const rosterContent = (
    <>
      {/* Roster + invite scroll here; the QR + account footer below is pinned by
          this region's flex:1 (desktop). display:contents on mobile leaves the
          drawer flowing exactly as before. */}
      <RosterScroll
        $hidden={isHostMode && railSection !== "room"}
        hidden={isHostMode && railSection !== "room"}
      >
      {/* No heading on the ROSTER. The tab that opens this section is labelled
          "Copresent" and is still on screen above it, so a title here repeats a
          word the host pressed a second ago — and spends the top of the panel
          doing it. Same reason the Q&A panel dropped its own.

          The empty state below is the exception, and for Q&A's reason: a list
          of people needs no label, but a pitch for a feature the host may never
          have used has to say what it is for. */}
      {coPresenters.length === 0 ? (
        /* Nobody here yet, so the section is not a roster — it is one
            proposition with one thing to do about it. Centred and pitched, in
            the same shape as Q&A's empty state, rather than a line of grey
            apology above a form the host still has to assemble. */
        <InviteEmpty>
          {/* Named here, unlike the roster below: this is an offer, and an
              offer has to say what it is for. */}
          <InviteEmptyTitle>Copresenters</InviteEmptyTitle>

          <InviteEmptyBody>
            <CopresentIllustration />
            <InviteEmptyText>
              {/* ONE pitch, whatever the plan — the Q&A panel's rule, and for
                  its reason. Branching this text sold before it explained: a
                  free host met the feature as a price rather than as a thing,
                  and the first sentence they ever read about copresenting was
                  that they could not have it.

                  So everybody gets the same sentence and the same button, and
                  the plan decides only what happens AFTER the tap. The gate is
                  not weakened by this — the server refuses the join either way
                  — it just stops being the first thing the panel says.

                  "the deck", not "your deck": a copresenter sees this panel
                  too, and it is not theirs. */}
              Share the link and others can follow the deck — and drive it —
              from their own screen.
            </InviteEmptyText>
            {/* One button, one label. A free host who taps it gets the purchase
                dialog instead of a link — which is the whole paywall, and why
                no version of this hands them a URL they cannot use. */}
            <InvitePrimary
              type="button"
              onClick={() => {
                if (copresentLocked) return openProPaywall("invite_empty");
                return canShareInvite
                  ? void shareInviteLink(joinUrl)
                  : copyLink("join", joinUrl);
              }}
              /* The visible label is the same for everyone; the accessible one
                 says what the tap will actually do. Sighted hosts learn that
                 from context — a share sheet slides up, or a "Copied" flash —
                 and a screen-reader user gets neither, so "Invite copresenters"
                 alone would leave them guessing whether anything happened. */
              aria-label={
                copresentLocked
                  ? "Invite copresenters — upgrade to Copresent Pro"
                  : canShareInvite
                    ? "Share invite link"
                    : "Copy invite link"
              }
            >
              {copiedLink === "join" ? "Copied" : "Invite copresenters"}
            </InvitePrimary>
          </InviteEmptyBody>
        </InviteEmpty>
      ) : (
        <>
          <RosterList>
            {coPresenters.map((p) => (
              <RosterItem key={p.presenterId}>
                <StatusDot $online={p.status === "ONLINE"} />
                <RosterName $paused={Boolean(p.paused)}>
                  {p.name || "Guest"}
                </RosterName>
                {p.paused && <PausedTag>Paused</PausedTag>}
                {/* Host-only, and offered on OFFLINE rows too — the roster keeps
                    a disconnected presenter for ~20 minutes and the backend
                    preserves their paused flag across a reconnect, so pausing
                    someone whose phone just dropped still holds when they return.

                    Icon-only, so aria-label carries the whole meaning for a
                    screen reader and title gives everyone else a tooltip. It
                    names the person too: "Pause" alone is ambiguous on a list
                    where every row has the same button.

                    On free it still renders and still clicks — into the paywall
                    rather than the action — but says so first. An unlabelled
                    button that opens a purchase dialog reads as a trick; naming
                    Pro up front makes the click a choice. */}
                {isHostMode && (
                  <RosterAction
                    type="button"
                    onClick={() => togglePresenterPaused(p)}
                    aria-label={pauseActionLabel(p)}
                    title={pauseActionLabel(p)}
                  >
                    {p.paused ? <ResumeIcon /> : <PauseIcon />}
                  </RosterAction>
                )}
                {/* Host-only, on OFFLINE rows too: a dropped phone sits on the
                    roster for ~20 minutes, and clearing that stale name (and,
                    on free, the seat it holds) is half the point. */}
                {isHostMode && (
                  <RosterAction
                    type="button"
                    onClick={() => removeCopresenter(p)}
                    aria-label={`Remove ${p.name || "Guest"} from the presentation`}
                    title={`Remove ${p.name || "Guest"} from the presentation`}
                  >
                    <RemoveIcon />
                  </RosterAction>
                )}
              </RosterItem>
            ))}
          </RosterList>

        {/* Free plan only — a Pro host gets no counter and no pitch, because the
            cap doesn't exist for them. Counts from the rendered roster rather
            than the backend's seatsInUse so the number can never disagree with
            the list right above it.

            Two shapes, because zero seats is not a small number of seats. A
            meter reading "0 of 0 free seats used", with a row of pips that can
            never fill, describes a quota that has run out — when what is true is
            that the free plan has no copresenters in it at all. So at zero the
            meter becomes a plain statement of what Pro adds, and the pips (whose
            only job is to show headroom) disappear rather than render an empty
            row. Above zero the meter is unchanged, which is what keeps the
            configured kill switch honest: put a seat back and the counter
            returns. */}
        {seatLimit != null && (
          <SeatMeter>
            <SeatMeterRow>
              <RailHint>
                {seatLimit === 0
                  ? "Copresenters are part of Pro"
                  : `${onlineCoPresenterCount} of ${seatLimit} free seat${
                      seatLimit === 1 ? "" : "s"
                    } used`}
              </RailHint>
              {seatLimit > 0 && (
                <SeatPips aria-hidden="true">
                  {Array.from({ length: seatLimit }, (_, i) => (
                    <SeatPip key={i} $taken={i < onlineCoPresenterCount} />
                  ))}
                </SeatPips>
              )}
            </SeatMeterRow>
            {/* The seat meter's ONLY upgrade prompt is a real turned-away person.
                Being full with no one blocked isn't a problem to pitch against —
                the standing button at the foot of the rail already carries the
                general ask. So this appears solely when someone actually hit the
                wall, and names them. */}
            {blockedJoiner && (
              <BlockedNotice role="status">
                <BlockedNoticeDismiss
                  type="button"
                  aria-label="Dismiss"
                  title="Dismiss"
                  onClick={() => {
                    captureUi("join_blocked_dismissed");
                    setBlockedJoiner(null);
                  }}
                >
                  <CloseIcon />
                </BlockedNoticeDismiss>
                {/* Two short sentences: who, then why. This fires mid-presentation
                    while the host is talking, so it has to survive a glance — and
                    the button underneath already says what the fix is.

                    "for this session" is load-bearing: without it the sentence
                    reads as an account-level allowance the host has spent for
                    good, rather than the capacity of the room they're in.

                    No number, and no singular/plural branch — the free cap has
                    already moved once (2 → 1), and prose that names a count is
                    prose that goes stale. The seat meter above carries the actual
                    figure. */}
                <BlockedNoticeText>
                  <BlockedNoticeName>{blockedJoiner}</BlockedNoticeName> couldn't
                  join. You've reached the free limit for this session.
                </BlockedNoticeText>
                <UpgradeButton
                  type="button"
                  onClick={() => openProPaywall("roster_blocked")}
                >
                  Upgrade to Pro
                </UpgradeButton>
              </BlockedNotice>
            )}
          </SeatMeter>
        )}

        {/* The link, once someone is already here: a field with Copy beside it,
            and nothing else.

            No heading and no explanation — the list above it has just shown what
            the link does, in the form of people who used it. And no "New link":
            rotating is a rare, destructive-looking control that sat at equal
            weight beside the one everybody presses, on the section a host opens
            most. Nothing else in the app rotates an invite now, so the capability
            is gone with the button — the backend endpoint remains. */}
        {/* The link itself, for a host whose plan can actually use it.

            A free host gets the upgrade ask in its place rather than a disabled
            field. Rendering the URL greyed out would still put a working invite
            on screen — it is a bearer link, and read-only is not secret; anyone
            could select it, or read it off a shared screen, and join. The plan
            is enforced on the server either way, so this is not the gate; it is
            about not dangling the thing we just said they cannot have. */}
        {copresentLocked ? (
          /* With the pass known, the foot of the panel says this, next to
             its upgrade button. */
          freeCopresent ? null : (
          <RosterSection>
            {/* Text, no button. This branch only renders once copresenters are
                already on the roster — free sessions just ran out — and the
                rail's standing upgrade button is a few pixels below it. A
                second one here would be the same ask twice in one drawer. The
                empty state above carries a button because it replaces one. */}
            <RailHint>
              You've used your free copresented sessions. Upgrade to bring
              anyone else into this session.
            </RailHint>
          </RosterSection>
          )
        ) : (
        <RosterSection>
          <LinkRow>
            <LinkInput
              readOnly
              aria-label="Invite link"
              value={joinUrl}
              onFocus={(e) => e.currentTarget.select()}
            />
            <LinkIconButton
              type="button"
              $primaryOnMobile
              onClick={() =>
                canShareInvite
                  ? void shareInviteLink(joinUrl)
                  : copyLink("join", joinUrl)
              }
              aria-label={
                copiedLink === "join"
                  ? "Copied"
                  : canShareInvite
                    ? "Share invite link"
                    : "Copy invite link"
              }
              title={copiedLink === "join" ? "Copied" : "Copy link"}
            >
              {copiedLink === "join" ? (
                <CheckIcon />
              ) : canShareInvite ? (
                <ShareIcon />
              ) : (
                <CopyLinkIcon />
              )}
              {/* Label only where the field is hidden — beside the field on
                  desktop it would say what the field already shows. */}
              <MobileOnlyLabel>
                {copiedLink === "join"
                  ? "Copied"
                  : canShareInvite
                    ? "Share invite link"
                    : "Copy invite link"}
              </MobileOnlyLabel>
            </LinkIconButton>
          </LinkRow>
        </RosterSection>
        )}

        {/* Mobile only: the preview window (which carries the enable button on
            desktop) is hidden on phones, so surface the nudge here in the drawer.
            On every plan since COP-33. */}
        {previewOn === false && !previewLoading && (
          <MobileRosterSection>
            <HostLabel>Live previews</HostLabel>
            <RailHint>
              Copresenters can't see your current slide yet. Turn on live previews
              so it shows in their controller.
            </RailHint>
            <HostActionButton
              type="button"
              onClick={() => enableLivePreviews("roster_mobile")}
            >
              Enable live previews
            </HostActionButton>
          </MobileRosterSection>
        )}
        </>
      )}
      </RosterScroll>

      {/* Audience Q&A — its own section, not more of the Room scroll.
          A different audience entirely: copresenters drive the deck, the room
          asks questions and never touches it, and stacking the two invited
          handing out the wrong link. Its own component too, because it owns a
          streaming connection, its own error states and its own moderation
          actions that nothing here needs to know about.

          Rendered even while Room is showing — see QaPane. */}
      {/* Not on a paired phone: Q&A runs on the account service, and a phone
          paired to one deck has no account to open a room with (COP-17). */}
      {isHostMode && !pairedDevice && (
        <QaPane $hidden={railSection !== "qa"} hidden={railSection !== "qa"}>
          <QaPanel
            magicId={magicId}
            // Q&A is on every plan since COP-33; a signed-in host is enough.
            isPro={true}
            onUpgrade={openProPaywall}
            onRoomChange={setQaRoom}
            capture={captureUi}
          />
        </QaPane>
      )}

      {/* Pair the deck with a phone — its own section now.
          Desktop only: on a phone you are already holding the controller, so
          there is nothing to pair with. The disclosure toggle went with the
          move; a section that exists to show one QR has no business hiding it,
          and the space it was competing for is no longer shared. */}
      {/* Not rendered at all on a phone — the pane is desktop-only, and building
          a 132px QR into a sheet that can never show it is work for nothing. */}
      {!isMobileLayout && (
      <PhonePane
        $hidden={!isHostMode || railSection !== "phone"}
        hidden={!isHostMode || railSection !== "phone"}
      >
        <HostLabel>Control from your phone</HostLabel>
        <RailHint>
          Scan this to drive the deck from your phone — no sign-in needed. It
          works for this deck only, for 24 hours, so keep it off a shared
          screen.
        </RailHint>
        <QRWrap>
          <QRCodeSVG value={hostUrl} size={132} />
        </QRWrap>
      </PhonePane>
      )}

      {/* The free plan's one standing ask, sat directly above the account it
          would apply to. Everything else that used to pitch Pro — the preview
          panel's CTA, its mobile twin, the always-on seat-meter button — is
          gone; the only other prompt left appears at the moment the cap
          actually bites. */}
      {/* The standing offer used to have an anonymous twin above it: a host
          in on the QR's key with no account was shown two doors to Google
          rather than a purchase, because signing in was what started their
          seven days. Both doors are now the wall in front of this whole page,
          so what is left here is the one ask a signed-in free host can act on. */}
      {/* On the Copresent tab only: Q&A and the phone are on every plan, so
          an upgrade ask there sells nothing the host is looking at. It leads
          with the free copresent pass, and the button names what Pro adds:
          the pass is the limit, so the ask sits directly under it. */}
      {isFreeHost && !pairedDevice && railSection === "room" && (
        <StandingUpgrade>
          {showPassFooter && freeCopresentPass}
          {/* No trial is offered since COP-33, so the label is the same for a
              host whose old trial lapsed: "Trial ended" would name a plan that
              no longer exists. */}
          <UpgradeButton
            type="button"
            onClick={() => openProPaywall(showPassFooter ? "copresent_pass" : "rail_standing")}
            style={showPassFooter ? { marginTop: 12 } : undefined}
          >
            {showPassFooter ? "Upgrade for unlimited copresenting" : "Upgrade to Pro"}
          </UpgradeButton>
        </StandingUpgrade>
      )}

      {/* Only where there is no icon strip to carry the account — the mobile
          drawer, and a copresenter. On the host's desktop rail the avatar and
          its plan pill say it in the strip instead, which is what freed the
          foot of every section.

          Chosen in JS rather than hidden in CSS: rendering both and hiding one
          leaves two copies of the same name in the document, which is a
          duplicate for a screen reader to read out. */}
      {!stripCarriesAccount && !menuCarriesAccount && (
        <HostAccountBar
          profile={hostProfile}
          isPro={isProNow}
          onUpgrade={() => openUpgrade("account_badge")}
          onSignOut={signOutHost}
          usageLine={usageLine}
          allowance={menuAllowance}
        />
      )}
    </>
  );

  // The rail as it is actually mounted: switcher, then the sections and the
  // footer that outlives them.
  //
  // The QR and the account/upgrade block sit BELOW the sections rather than
  // inside one, because neither belongs to copresenting or to Q&A — pairing the
  // deck with a phone and the standing upgrade ask are true from wherever the
  // host is looking, and burying the free plan's one standing ask behind a tab
  // would quietly retire it.
  //
  // A copresenter sees no switcher at all: they have exactly one section, and a
  // one-item menu is furniture.
  const railContent = (
    <>
      {isHostMode && !isMobileLayout && (
        <SectionNav aria-label="Sections">
          {/* Home: the host's dashboard at the root of the clicker. A link,
              not a section — it takes the host out of the controller (the
              dashboard offers the way back while the deck is live). */}
          {!pairedDevice && (
            <SectionTab
              as="a"
              href="/"
              $active={false}
              $connected={false}
              onClick={goHome}
            >
              <HomeIcon />
              Home
            </SectionTab>
          )}
          {railSections.map(({ id, label, Icon }) => (
            <SectionTab
              key={id}
              type="button"
              $active={railSection === id}
              $connected={!rosterCollapsed}
              // Not role="tab": these switch a whole rail panel rather than
              // pages of one widget, and a tablist owes keyboard arrow-key
              // navigation between the tabs, which this does not implement.
              aria-current={railSection === id ? "true" : undefined}
              onClick={() => setRailSection(id)}
            >
              <Icon />
              {label}
              {id === "qa" && qaWaiting > 0 && (
                <TabBadge aria-label={`${qaWaiting} waiting`}>
                  {qaWaiting > 9 ? "9+" : qaWaiting}
                </TabBadge>
              )}
            </SectionTab>
          ))}

          <SectionNavSpacer />
          {!pairedDevice && (
            <HostAccountBar
              compact
              profile={hostProfile}
              isPro={isProNow}
              onUpgrade={() => openUpgrade("account_badge")}
              onSignOut={signOutHost}
              usageLine={usageLine}
              allowance={menuAllowance}
            />
          )}
        </SectionNav>
      )}
      <RailBody $collapsed={isHostMode && rosterCollapsed}>{rosterContent}</RailBody>
    </>
  );

  // The Pro offer. A dialog rather than a page: every entry point fires while
  // the host is mid-presentation, and navigating the controller away would drop
  // their room.
  //
  // This is the PURCHASE half of the paywall, and it assumes a signed-in host —
  // it needs a `sub` to attribute the payment to. Every caller only renders once
  // signed in. The no-sub branch
  // below survives as a backstop, not as a state anyone should reach.
  const upgradeDialog = upgradeOpen && (
    <UpgradeOverlay role="presentation" onClick={closeUpgrade}>
      {/* The same plans card /upgrade shows, so the overlay and the page the
          emails link to can't quote different prices. Checkout opens Stripe
          in a NEW tab straight from here — this tab is the host's live room.
          The card leaves the deck's path for /upgrade/success to bounce back
          to, and the re-JOIN closes this overlay once Pro lands. */}
      <ProPlans
        variant="dialog"
        source={`controller_${upgradeSource}`}
        onClose={closeUpgrade}
        signInReturnPath={() => window.location.pathname}
      />
    </UpgradeOverlay>
  );

  // The "Sign up free" interstitial stood here: a dialog that explained the
  // 7-day trial before sending an anonymous host to Google. Its job has moved
  // into the wall, which every host now passes through — so an interstitial in
  // front of a wall would be two dialogs asking for the same tap.

  // Payment received — the buyer just bounced back here from Stripe.
  //
  // Unlike the /upgrade/success fallback, this dialog knows the live plan: the
  // 2.5s re-JOIN reports it, so the copy can say Pro is ON once it actually is
  // rather than always hedging with "activating". It also self-corrects — a
  // host who lands before the webhook has run reads "switching on", and the
  // line becomes "active" underneath them when it does.
  const paidDialog = paidOpen && (
    <ModalOverlay role="presentation" onClick={() => setPaidOpen(false)}>
      <PaidCard
        role="dialog"
        aria-modal="true"
        aria-labelledby="paid-dialog-title"
        aria-describedby="paid-dialog-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <PaidBadge>
          <BigCheckIcon />
        </PaidBadge>
        <PaidTitle id="paid-dialog-title">Payment received</PaidTitle>
        {/* One line, and it changes itself: the pending wording is replaced the
            moment the re-JOIN reports Pro, so the reassurance the old copy
            spent three sentences on ("takes a few seconds", "lifts on its own",
            "no reload needed") is simply watched happening instead. */}
        <PaidText id="paid-dialog-desc">
          {isProNow === true ? (
            <>
              Pro is active on{" "}
              <PaidEmail>{hostProfileEmail || "your account"}</PaidEmail>
            </>
          ) : (
            // No trailing ellipsis: butted against the address it reads as a
            // truncation mark, i.e. "your email got cut off", not "in progress".
            <>
              Activating Pro on{" "}
              <PaidEmail>{hostProfileEmail || "your account"}</PaidEmail>
            </>
          )}
        </PaidText>
        <PaidButton type="button" onClick={() => setPaidOpen(false)}>
          Back to presenting
        </PaidButton>
      </PaidCard>
    </ModalOverlay>
  );

  // A paired phone (COP-17) reached for something that belongs to the account.
  // It is a clicker for one deck, not signed in, so the honest answer is where
  // to go — not a Google sign-in that would turn it into a full account.
  const accountOnlyDialog = accountOnlyOpen && (
    <ModalOverlay role="presentation" onClick={() => setAccountOnlyOpen(false)}>
      <PaidCard
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-only-title"
        aria-describedby="account-only-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <PaidTitle id="account-only-title">Do this on your computer</PaidTitle>
        <PaidText id="account-only-desc">
          This phone is paired to this deck only, so it isn&rsquo;t signed in.
          Open Copresent on your computer to manage your plan and previews.
        </PaidText>
        <PaidButton type="button" onClick={() => setAccountOnlyOpen(false)}>
          Back to presenting
        </PaidButton>
      </PaidCard>
    </ModalOverlay>
  );

  // Host only: someone was turned away by the free cap. The pitch writes itself
  // — a named person is at the door right now — so this interrupts, where the
  // roster's seat meter only whispers.

  const nameModal = showNameModal && (
    <ModalOverlay role="presentation">
      <ModalCard
        role="dialog"
        aria-modal="true"
        aria-labelledby="name-modal-title"
        aria-describedby="name-modal-desc"
      >
        <form
          style={{ margin: 0 }}
          onSubmit={(e) => {
            e.preventDefault();
            submitName();
          }}
        >
          <ModalTitle id="name-modal-title">What's your name?</ModalTitle>
          <ModalText id="name-modal-desc">
            So the host can see who's joined the presentation.
          </ModalText>
          <ModalInput
            ref={nameInputRef}
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="e.g. Alex"
            maxLength={60}
            autoComplete="name"
            enterKeyHint="done"
            aria-label="Your name"
          />
          <ModalButton type="submit" disabled={!nameInput.trim()}>
            Join presentation
          </ModalButton>
        </form>
      </ModalCard>
    </ModalOverlay>
  );

  // ── The sign-in wall ─────────────────────────────────────────────────────
  //
  // Replaces two dead ends that can no longer be reached. One was a keyed deck
  // opened without its `#k=` fragment; the other was a deck already open on its
  // allowance of anonymous host devices. Both were consequences of a controller
  // that opened without an account, and both are gone with it.
  //
  // The controller behind this is a SKELETON, not the real thing under a blur.
  //
  // That distinction is the whole security of the screen. Blurring the live
  // controller would mean an unauthenticated visitor holding a deck id gets the
  // roster, the deck title, the slide count and the invite link rendered into
  // their DOM, one devtools property away from readable — and it would seat
  // them on the session besides. So nothing is resolved and no socket is opened
  // until there is an account (see the guards on the resolve effect and the
  // socket url). The blur is atmosphere; the gate is that there is nothing
  // behind it yet.
  //
  // The copy leads with WHY rather than with the ask. Whoever lands here has
  // usually just pressed Copresent in Google Slides and expected controls, so
  // "sign in" with no reason attached reads as a toll booth. The reason is real
  // and worth stating plainly: this page drives a live presentation, and
  // without an account the only thing standing between a stranger and someone
  // else's slides is who happens to have the link.
  if (isHostMode && !hostSignedIn) {
    return (
      <Page $dark={theme === "dark"} $host>
        {/* Inert in every sense: no interactivity, and removed from the
            accessibility tree entirely. A screen reader must not narrate a
            controller that is not there — and a keyboard user must not be able
            to tab into it behind the dialog. */}
        <AuthWallSkeleton aria-hidden="true" data-testid="auth-wall-skeleton">
          <AuthWallShot src={hostControllerShot} alt="" draggable={false} />
        </AuthWallSkeleton>

        <AuthWallOverlay role="presentation">
          <AuthCard
            role="dialog"
            aria-modal="true"
            aria-labelledby="host-auth-title"
            aria-describedby="host-auth-desc"
          >
            <SignInLogo src={copresentLogo} alt="" aria-hidden="true" />
            <SignInHeader>
              <SignInTitle id="host-auth-title">
                {pairingExpired ? "This Clicker has expired" : "Sign in to continue"}
              </SignInTitle>
            </SignInHeader>
            {/* One sentence, stating the rule rather than warning about it.
                An earlier draft ("anyone with the link could drive your
                slides") was accurate but read as an alarm, and the feature
                list and free-week line under the button made the rest read as
                a pitch — which is a thing a visitor declines. */}
            <SignInBody id="host-auth-desc">
              {/* A phone that was paired from the QR, not signed in, and the
                  pairing has run out. Say that — rescanning is the fix it
                  knows, and "sign in to continue" reads as if it had. */}
              {pairingExpired
                ? "Scan the QR code on your computer again to keep presenting from this phone."
                : "Host controls are tied to your Google account, so only you can drive this presentation."}
            </SignInBody>
            {isWebAuthConfigured() && (
              <GoogleButton
                type="button"
                onClick={() => {
                  // `intent` distinguishes the two things this one button does,
                  // which the backend cannot: the same OAuth round trip creates
                  // an account or resumes one, and the browser only knows which
                  // it expected. `hasPriorSession` is the closest thing to a
                  // signal, and pairing it with host_auth_completed's
                  // is_new_user is what makes the wall's conversion readable
                  // for first-timers and returners separately.
                  captureUi("host_auth_started", {
                    source: "wall",
                    intent: hasPriorSession ? "sign_in" : "sign_up",
                  });
                  signInAsHost();
                }}
              >
                <GoogleG />
                {hasPriorSession ? "Sign in with Google" : "Continue with Google"}
              </GoogleButton>
            )}
            <SignInLegal>
              By continuing you agree to our{" "}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>{" "}
              and{" "}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              .
            </SignInLegal>
          </AuthCard>
        </AuthWallOverlay>
        {themeToggle}
      </Page>
    );
  }

  // ── Host, out of free presentations for the month ─────────────────────────
  // Ahead of every other host state: the resolve withholds the session behind
  // a 402, so without this the host would sit on "Opening host controls…"
  // forever. Polling continues underneath, so an upgrade in the new tab — or
  // the allowance month rolling over — swaps this for the controls with no reload.
  if (hostLimitReached) {
    const resetDate = formatResetDate(presentationAllowance?.resetsAt);
    const limit = presentationAllowance?.limit || 5;
    const presentations = `${limit} free presentation${limit === 1 ? "" : "s"}`;
    return (
      <Page $dark={theme === "dark"} $host>
        <HostLoading role="status" aria-live="polite">
          <HostIdleTitle>You've used your {presentations} this month</HostIdleTitle>
          <HostIdleBody>
            Free includes {presentations} a month. Each deck counts once a day,
            however many times you present it.
            {resetDate ? ` More become available on ${resetDate}.` : ""} Pro has
            no limit.
          </HostIdleBody>
          <LimitActions>
            <PaidButton
              type="button"
              onClick={() => openProPaywall("presentation_limit")}
            >
              See Pro plans
            </PaidButton>
            {!pairedDevice && (
              <LimitLink href="/history" target="_blank" rel="noopener">
                See your history
              </LimitLink>
            )}
          </LimitActions>
          {themeToggle}
        </HostLoading>
        {upgradeDialog}
        {accountOnlyDialog}
      </Page>
    );
  }

  // ── Host shell: three-zone verified-host layout ──────────────────────────
  // ── Resolving a returning host: spinner instead of a sign-in flash ───────
  if (hostResolving) {
    return (
      <Page $dark={theme === "dark"} $host>
        <HostLoading role="status" aria-live="polite">
          <Spinner aria-hidden="true" />
          Opening host controls…
        </HostLoading>
      </Page>
    );
  }

  // ── Host, deck not running yet: "not running" screen ─────────────────────
  // Signed in or not — the gate came down, so both land here.
  //
  // This is now the ONLY answer for a deck with no session: the backend used to
  // mint one on demand, and stopped, because it couldn't tell the deck's real
  // host from anyone else holding the (semi-public) deck id. So this screen
  // covers a genuinely common case rather than an edge one, and it has to be a
  // waiting state rather than an error. It keeps polling in the background (the
  // resolve effect), so it flips straight to the controller the moment the host
  // opens the deck and the extension registers. Never the copresenter "link
  // expired" dead-end.
  if (hostDeckIdle) {
    return (
      <Page $dark={theme === "dark"} $host>
        <HostLoading role="status" aria-live="polite">
          {/* Says what actually clears this state, which is NOT presenting.
              The pre-start REGISTER the backend is waiting for fires when the
              host opens the Copresent popup on the /edit page — see
              `editRegistered` in the extension's content script. Telling them to
              start the slideshow sent hosts who had already done the right thing
              looking for a step that was never required, and gave them no way to
              tell a stuck controller from a working one. */}
          <HostIdleTitle>Waiting for this deck</HostIdleTitle>
          <HostIdleBody>
            Open it in Google Slides and click Copresent — these controls connect
            automatically. You don't need to start the slideshow.
          </HostIdleBody>
          {themeToggle}
        </HostLoading>
      </Page>
    );
  }

  // ── Host shell: three-zone verified-host layout ──────────────────────────
  if (hostShellActive) {
    return (
      <Page $dark={theme === "dark"} $host>
        <HostBody>
          {/* One tree or the other, never both. Rendering the rail and the
              sheet together would put two copies of the roster, the invite and
              the Q&A panel in the document — two "Q&A" buttons for a screen
              reader, and two live question streams. */}
          {!isMobileLayout && (
            <>
          <RosterRail aria-label="Copresenters" $collapsed={rosterCollapsed}>
            {railContent}
          </RosterRail>
          <CollapseTab
            type="button"
            $collapsed={rosterCollapsed}
            onClick={toggleRosterCollapsed}
            aria-label={rosterCollapsed ? "Show details panel" : "Hide details panel"}
            aria-expanded={!rosterCollapsed}
            title={rosterCollapsed ? "Show details panel" : "Hide details panel"}
          >
            <CollapseChevron $collapsed={rosterCollapsed}>
              <ChevronLeftIcon />
            </CollapseChevron>
          </CollapseTab>
            </>
          )}
          <HostMain>
            {/* Desktop only. On a phone this lives in the hamburger menu — a
                lone unlabelled glyph in the corner of a slide preview was the
                easiest control in the app to press by accident. */}
            {!isMobileLayout && <HeadCornerTheme>{themeToggle}</HeadCornerTheme>}
            <HeadCornerRight>
              {/* Desktop only for a host: on a phone the bottom bar is the way
                  in, and a second door in the far corner would be a door most
                  thumbs cannot reach anyway. */}
              <DesktopHamburger
                type="button"
                onClick={() => openMenu("host_shell")}
                aria-label="Open menu"
                title="Menu"
              >
                <MenuIcon />
              </DesktopHamburger>
            </HeadCornerRight>
            <HostCenterHead>
              {isMobileLayout ? (
                <HeadTitleRow>
                  <span aria-hidden="true" />
                  <Title>{presentation?.title ?? ""}</Title>
                  <HeadMenuButton
                    type="button"
                    onClick={() => {
                      if (rosterOpen) {
                        setRosterOpen(false);
                        return;
                      }
                      setSheetView("menu");
                      openMenu("header_menu");
                    }}
                    aria-label={
                      qaWaiting > 0
                        ? `Menu, ${qaWaiting} question${qaWaiting === 1 ? "" : "s"} waiting`
                        : "Menu"
                    }
                    aria-expanded={rosterOpen}
                    title="Menu"
                  >
                    <MenuIcon />
                    {qaWaiting > 0 && <MenuDot aria-hidden="true" />}
                  </HeadMenuButton>
                </HeadTitleRow>
              ) : (
                presentation?.title && <Title>{presentation.title}</Title>
              )}
              <MetaRow>
                {slideCountLabel && <SlideCount>{slideCountLabel}</SlideCount>}
                <ConnectionStatus status={status} />
              </MetaRow>
              {headerToolbar}
              {timerRow}
            </HostCenterHead>
            {hostCenterBody}
            {keyboardHint}
          </HostMain>
          {hostNotesRail}
          <NotesCollapseTab
            type="button"
            $collapsed={notesCollapsed}
            onClick={toggleNotesCollapsed}
            aria-label={notesCollapsed ? "Show speaker notes" : "Hide speaker notes"}
            aria-expanded={!notesCollapsed}
            title={notesCollapsed ? "Show speaker notes" : "Hide speaker notes"}
          >
            <CollapseChevron $collapsed={!notesCollapsed}>
              <ChevronLeftIcon />
            </CollapseChevron>
          </NotesCollapseTab>
        </HostBody>

        {/* The menu sheet, opened from the title row's menu button.

            Hidden rather than unmounted when closed: the Q&A panel inside it
            holds the question stream that feeds the menu button's dot, and a
            sheet that unmounted on close would leave the dot frozen at whatever
            it said when the speaker last looked — which is precisely the moment
            it stops being worth anything. */}
        {isMobileLayout && (
          <>
            {rosterOpen && <SheetScrim onClick={() => setRosterOpen(false)} />}
            <Sheet
              $open={rosterOpen}
              hidden={!rosterOpen}
              aria-label={
                sheetView === "menu"
                  ? "Menu"
                  : RAIL_SECTIONS.find((s) => s.id === railSection)?.label ?? "Menu"
              }
            >
              {/* One sheet, two views. The section panels stay MOUNTED behind
                  the menu rather than being swapped out: the Q&A panel inside
                  them holds the question stream that feeds the tab bar's badge,
                  and unmounting it while the host reads their plan would freeze
                  the badge at whatever it said when they opened the menu. */}
              <SectionPane
                $hidden={sheetView === "menu"}
                hidden={sheetView === "menu"}
              >
                <MenuBack type="button" onClick={() => setSheetView("menu")}>
                  <ChevronLeftIcon />
                  Back
                </MenuBack>
                {rosterContent}
              </SectionPane>

              {sheetView === "menu" && (
                <MobileMenuSection>
                  <MenuGrid>
                    {mobileMenuSections.map(({ id, label, Icon }) =>
                      menuTile({
                        id,
                        label,
                        Icon,
                        color: id === "qa" ? MENU_COLORS.qa : MENU_COLORS.room,
                        caption:
                          id === "qa"
                            ? qaRoom?.open
                              ? qaWaiting > 0
                                ? `${qaWaiting} waiting`
                                : "Open"
                              : "Not open"
                            : onlineCoPresenterCount > 0
                            ? `${onlineCoPresenterCount} in the room`
                            : "Invite",
                        captionTone:
                          id === "qa" && qaWaiting > 0 ? "accent" : undefined,
                        badge: id === "qa" ? qaWaiting : undefined,
                        onClick: () => {
                          setRailSection(id);
                          setSheetView("section");
                          captureUi("menu_section_opened", { section: id });
                        },
                      })
                    )}
                    {timerTile}
                    {previewTile}
                    {themeTile}
                    {!pairedDevice &&
                      menuTile({
                        id: "home",
                        label: "Home",
                        Icon: HomeIcon,
                        color: MENU_COLORS.home,
                        caption: "Your decks",
                        onClick: () => goHome(),
                      })}
                    {chatEnabled &&
                      menuTile({
                        id: "chat",
                        label: "Support",
                        Icon: HelpIcon,
                        color: MENU_COLORS.chat,
                        caption: "Chat with us",
                        onClick: () => openLiveChat("host_shell"),
                      })}
                  </MenuGrid>
                  {/* The account, with the plan, at the
                      FOOT of the menu — where an account sits in every other
                      app, and where its own dropdown (which opens upward) has
                      somewhere to go. */}
                  {menuCarriesAccount && !pairedDevice && (
                    <MenuAccountCard>
                      <HostAccountBar
                        profile={hostProfile}
                        isPro={isProNow}
                        onUpgrade={() => openUpgrade("account_badge")}
                        onSignOut={signOutHost}
                        usageLine={usageLine}
                        allowance={menuAllowance}
                      />
                    </MenuAccountCard>
                  )}
                </MobileMenuSection>
              )}

              <SheetHandle
                type="button"
                onClick={() => setRosterOpen(false)}
                aria-label="Close"
                title="Close"
              />
            </Sheet>

          </>
        )}

        {stage}
        {nameModal}
        {upgradeDialog}
        {paidDialog}
        {accountOnlyDialog}
      </Page>
    );
  }

  return (
    <Page $dark={theme === "dark"} $host={isHostMode}>
      <ThemeToggleSlot>{themeToggle}</ThemeToggleSlot>
      {copresenterHasMenu && (
        <MobileMenuSlot>
          <Hamburger
            type="button"
            onClick={() => openMenu("copresenter")}
            aria-label="Open menu"
            title="Menu"
          >
            <MenuIcon />
          </Hamburger>
        </MobileMenuSlot>
      )}

      <Chrome>
        {presentation?.title && <Title>{presentation.title}</Title>}
        <MetaRow>
          {slideCountLabel && <SlideCount>{slideCountLabel}</SlideCount>}
          <ConnectionStatus status={status} />
        </MetaRow>
        {headerToolbar}
        {timerRow}
      </Chrome>

      {/* Two different situations wear the same RoomFull flag, and telling a
          joiner the wrong one wastes their time. "All the seats are taken"
          invites them to wait for one to free up — true when the room has seats,
          and actively misleading at the shipped zero, where no seat can ever
          free up and waiting is futile. At zero the only thing that admits them
          is the host buying Pro, so that is what it says. */}
      {roomFull && (
        <HostPanel
          aria-label={
            seatLimitLabel === 0 ? "Copresenting needs Pro" : "Room is full"
          }
          $center
        >
          <SignInCard>
            <SignInHeader>
              <Eyebrow>
                {seatLimitLabel === 0 ? "Copresenting needs Pro" : "Room is full"}
              </Eyebrow>
              <SignInTitle>
                {seatLimitLabel === 0
                  ? "This presentation isn't open to copresenters"
                  : "This presentation has all its seats taken"}
              </SignInTitle>
            </SignInHeader>
            <SignInBody>
              {seatLimitLabel === 0 ? (
                <>
                  This host has used the free plan's copresented sessions, so
                  copresenting now needs Copresent Pro. The host has been told
                  you're waiting — if they upgrade, you'll be let in
                  automatically.
                </>
              ) : (
                <>
                  {seatLimitLabel === 1
                    ? "Copresent's free plan allows one copresenter at a time, and someone's already in the room."
                    : `Copresent's free plan allows up to ${seatLimitLabel} copresenters at a time, and they're all in the room right now.`}{" "}
                  Ask the host to upgrade to Pro for unlimited copresenters —
                  then reload this page to join.
                </>
              )}
            </SignInBody>
            {/* The turned-away copresenter's only move. Worth counting: a high
                retry rate on a 1-seat plan is someone waiting for a seat to
                free up, which is the seat cap biting a real room rather than
                an abstract limit. At zero seats it measures something else —
                people who did not believe the message — so the seat_limit on
                the event is what keeps the two readings apart. */}
            <HostActionButton
              type="button"
              onClick={() => {
                captureUi("room_full_retry", { seat_limit: seatLimitLabel });
                window.location.reload();
              }}
            >
              Try again
            </HostActionButton>
          </SignInCard>
        </HostPanel>
      )}

      {removed && (
        <HostPanel aria-label="Removed from the presentation" $center>
          <SignInCard>
            <SignInHeader>
              <Eyebrow>Removed</Eyebrow>
              <SignInTitle>The host removed you from this presentation</SignInTitle>
            </SignInHeader>
            <SignInBody>You're no longer controlling the slides.</SignInBody>
          </SignInCard>
        </HostPanel>
      )}

      {!roomFull && !removed && (
        <>
          {/* Two different reasons, two different sentences. "The host has
              locked control" when nobody can move, "the host paused you" when
              it's personal — telling someone the room is locked when they were
              singled out (or the reverse) sends them to the wrong question. */}
          {selfPaused ? (
            <ViewOnlyBanner role="status">
              ⏸ The host paused your slide control — you're viewing only.
            </ViewOnlyBanner>
          ) : (
            controlBlocked && (
              <ViewOnlyBanner role="status">
                🔒 The host has locked slide control — you're viewing only.
              </ViewOnlyBanner>
            )
          )}
          {presentingBody}
          {keyboardHint}
        </>
      )}

      <Footer />

      {copresenterHasMenu && rosterOpen && (
        <DrawerOverlay onClick={() => setRosterOpen(false)}>
          <DrawerPanel aria-label="Menu" onClick={(e) => e.stopPropagation()}>
            <DrawerHeader>
              <HostLabel>Menu</HostLabel>
              <IconButton
                type="button"
                onClick={() => setRosterOpen(false)}
                aria-label="Close"
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </DrawerHeader>
            <MenuGrid>
              {timerTile}
              {chatEnabled &&
                menuTile({
                  id: "chat",
                  label: "Support",
                  Icon: HelpIcon,
                  color: MENU_COLORS.chat,
                  caption: "Chat with us",
                  onClick: () => openLiveChat("copresenter"),
                })}
            </MenuGrid>
          </DrawerPanel>
        </DrawerOverlay>
      )}

      {!roomFull && !removed && stage}
      {nameModal}
      {/* Also here, not just in the host shell above: the bounce back from
          Stripe lands before the WebSocket has confirmed us as host, so this
          is the branch on screen at the moment the confirmation is due. */}
      {paidDialog}
    </Page>
  );
};

export default Controller;
